import "./App.css";
import "./styles/theme.css";
import "./styles/layout.css";
import "./styles/components.css";
import { useEffect, useRef, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import {
  clearLichessPkceQuery,
  finishLichessPkceLogin,
  startLichessPkceLogin,
} from "./lichessPkce";
import Bracket from "./components/Bracket";
import RootLayout from "./layouts/RootLayout";
import StatCard from "./components/StatCard";
import StatusBadge from "./components/StatusBadge";
import { buildHashRoute, parseRouteFromHash } from "./pages/pageRoutes";
import DashboardPage from "./pages/DashboardPage";
import HomePage from "./pages/HomePage";
import LeagueEditPage from "./pages/LeagueEditPage";
import LeaguePage from "./pages/LeaguePage";
import LeagueRulesPage from "./pages/LeagueRulesPage";
import MatchPage from "./pages/MatchPage";
import ProfilePage from "./pages/ProfilePage";
import TeamPage from "./pages/TeamPage";
import {
  getPageMapItems,
  getPageShell,
  getPrimaryNavItems,
  getSecondaryNavItem,
} from "./pages/siteStructure";
import { getAppRouteComponent, resolveAppRouteKey } from "./routes/appRoutes";
const ACCOUNT_NOTES = [
  "Use the handles you actually compete with. It keeps pairings, invites, and results anchored to the right player.",
  "Lichess launch access is tied to the linked handle, not the browser alone. Reconnect if the wrong account shows up.",
  "Chess.com links still matter even before full automation. They decide roster eligibility and board identity.",
  "If you captain across both sites, keep both profiles linked so your team desk stays flexible.",
];
const FORUM_COLUMNS = [
  ["Round Room", ["Round announcements before the lineups lock", "Captain check-ins and substitute requests", "Board scheduling threads by match", "League notices worth pinning for the week"]],
  ["Good Threading", ["Lead with the match id and team names.", "Keep one scheduling trail per board or match.", "Link disputes back to the board record.", "Post a short confirmation once a result lands."]],
];
const DISPUTE_COLUMNS = [
  ["What To Bring", ["Match id and board number", "Exact challenge or game link", "Time control and platform used", "A short factual summary with no extra heat"]],
  ["How Review Works", ["Confirm the board record first", "Check platform evidence and timestamps", "Record the ruling in board notes", "Update the result only after the ruling is final"]],
];
const CONTACT_COLUMNS = [
  ["Account Desk", "Login issues, linked handles, reconnects, and the cases where a session goes sideways."],
  ["League Desk", "Questions about rounds, scheduling, matchmaking rules, or getting a team set up the right way."],
  ["Arbiter Desk", "Board disputes, no-shows, forfeits, and official corrections once a ruling is ready."],
];
const MATCHMAKING_OPTIONS = [
  {
    value: "allow_all",
    label: "Allow all",
    shortLabel: "Allow all",
    description: "Enter the full pool and let the boards split across every supported platform on the roster.",
  },
  {
    value: "lichess",
    label: "Lichess only",
    shortLabel: "Lichess",
    description: "Keep every pairing on Lichess and only draw opponents from the same side of the pool.",
  },
  {
    value: "chess.com",
    label: "Chess.com only",
    shortLabel: "Chess.com",
    description: "Keep every pairing on Chess.com and stay in that platform's ladder.",
  },
];
const MATCH_TIME_CONTROL_OPTIONS = ["1+0", "2+1", "3+0", "3+2", "5+0", "10+0", "15+10", "25+10", "30+0"];
const MATCH_DURATION_OPTIONS = [
  { value: 1, label: "1 day" },
  { value: 2, label: "2 days" },
  { value: 3, label: "3 days" },
];
const LEAGUE_MODE_OPTIONS = [
  { value: "open", label: "Open Mode" },
  { value: "scholastic", label: "Scholastic Mode" },
  { value: "creator", label: "Creator Mode" },
];
const LEAGUE_FORMAT_OPTIONS = [
  { value: "single_elimination", label: "Single Elimination" },
  { value: "round_robin", label: "Round Robin" },
];
const LEAGUE_ENTRY_TYPE_OPTIONS = [
  { value: "free", label: "Free Entry" },
  { value: "prize", label: "Prize Entry" },
];
const GRADE_BAND_OPTIONS = [
  "K-5",
  "6-8",
  "9-12",
  "K-8",
  "College",
];
const MATCH_TIME_CONTROL_GROUPS = [
  { label: "Bullet", options: ["1+0", "2+1"] },
  { label: "Blitz", options: ["3+0", "3+2", "5+0"] },
  { label: "Rapid", options: ["10+0", "15+10"] },
  { label: "Classical", options: ["25+10", "30+0"] },
];
const TEAM_BADGE_OPTIONS = [
  { value: "\u2654", label: "King" },
  { value: "\u2655", label: "Queen" },
  { value: "\u2656", label: "Rook" },
  { value: "\u2657", label: "Bishop" },
  { value: "\u2658", label: "Knight" },
  { value: "\u2659", label: "Pawn" },
];
const COUNTRY_OPTIONS = [
  "Afghanistan",
  "Albania",
  "Algeria",
  "Andorra",
  "Angola",
  "Antigua and Barbuda",
  "Argentina",
  "Armenia",
  "Australia",
  "Austria",
  "Azerbaijan",
  "Bahamas",
  "Bahrain",
  "Bangladesh",
  "Barbados",
  "Belarus",
  "Belgium",
  "Belize",
  "Benin",
  "Bhutan",
  "Bolivia",
  "Bosnia and Herzegovina",
  "Botswana",
  "Brazil",
  "Brunei",
  "Bulgaria",
  "Burkina Faso",
  "Burundi",
  "Cabo Verde",
  "Cambodia",
  "Cameroon",
  "Canada",
  "Central African Republic",
  "Chad",
  "Chile",
  "China",
  "Colombia",
  "Comoros",
  "Congo",
  "Costa Rica",
  "Croatia",
  "Cuba",
  "Cyprus",
  "Czech Republic",
  "Democratic Republic of the Congo",
  "Denmark",
  "Djibouti",
  "Dominica",
  "Dominican Republic",
  "Ecuador",
  "Egypt",
  "El Salvador",
  "Equatorial Guinea",
  "Eritrea",
  "Estonia",
  "Eswatini",
  "Ethiopia",
  "Fiji",
  "Finland",
  "France",
  "Gabon",
  "Gambia",
  "Georgia",
  "Germany",
  "Ghana",
  "Greece",
  "Grenada",
  "Guatemala",
  "Guinea",
  "Guinea-Bissau",
  "Guyana",
  "Haiti",
  "Honduras",
  "Hungary",
  "Iceland",
  "India",
  "Indonesia",
  "Iran",
  "Iraq",
  "Ireland",
  "Israel",
  "Italy",
  "Jamaica",
  "Japan",
  "Jordan",
  "Kazakhstan",
  "Kenya",
  "Kiribati",
  "Kuwait",
  "Kyrgyzstan",
  "Laos",
  "Latvia",
  "Lebanon",
  "Lesotho",
  "Liberia",
  "Libya",
  "Liechtenstein",
  "Lithuania",
  "Luxembourg",
  "Madagascar",
  "Malawi",
  "Malaysia",
  "Maldives",
  "Mali",
  "Malta",
  "Marshall Islands",
  "Mauritania",
  "Mauritius",
  "Mexico",
  "Micronesia",
  "Moldova",
  "Monaco",
  "Mongolia",
  "Montenegro",
  "Morocco",
  "Mozambique",
  "Myanmar",
  "Namibia",
  "Nauru",
  "Nepal",
  "Netherlands",
  "New Zealand",
  "Nicaragua",
  "Niger",
  "Nigeria",
  "North Korea",
  "North Macedonia",
  "Norway",
  "Oman",
  "Pakistan",
  "Palau",
  "Palestine",
  "Panama",
  "Papua New Guinea",
  "Paraguay",
  "Peru",
  "Philippines",
  "Poland",
  "Portugal",
  "Qatar",
  "Romania",
  "Russia",
  "Rwanda",
  "Saint Kitts and Nevis",
  "Saint Lucia",
  "Saint Vincent and the Grenadines",
  "Samoa",
  "San Marino",
  "Sao Tome and Principe",
  "Saudi Arabia",
  "Senegal",
  "Serbia",
  "Seychelles",
  "Sierra Leone",
  "Singapore",
  "Slovakia",
  "Slovenia",
  "Solomon Islands",
  "Somalia",
  "South Africa",
  "South Korea",
  "South Sudan",
  "Spain",
  "Sri Lanka",
  "Sudan",
  "Suriname",
  "Sweden",
  "Switzerland",
  "Syria",
  "Taiwan",
  "Tajikistan",
  "Tanzania",
  "Thailand",
  "Timor-Leste",
  "Togo",
  "Tonga",
  "Trinidad and Tobago",
  "Tunisia",
  "Turkey",
  "Turkmenistan",
  "Tuvalu",
  "Uganda",
  "Ukraine",
  "United Arab Emirates",
  "United Kingdom",
  "United States",
  "Uruguay",
  "Uzbekistan",
  "Vanuatu",
  "Vatican City",
  "Venezuela",
  "Vietnam",
  "Yemen",
  "Zambia",
  "Zimbabwe",
];

function getInitialRoute() {
  return parseRouteFromHash(window.location.hash, { signedIn: false });
}

function formatMatchmakingPreference(value) {
  if (value === "allow_all") return "Allow all";
  if (value === "chess.com") return "Chess.com only";
  if (value === "lichess") return "Lichess only";
  return "Allow all";
}

function getPlatformsForSelectedAccounts(accounts, selectedAccountIds) {
  const selectedIds = new Set((selectedAccountIds || []).map(String));
  return accounts
    .filter((account) => selectedIds.has(String(account.id)))
    .map((account) => account.platform);
}

function getOwnerPlatformAccountIdsForPreference(accounts, preference) {
  const orderedAccounts = Array.isArray(accounts) ? accounts : [];
  if (orderedAccounts.length === 0) return [];

  if (preference === "allow_all") {
    const seenPlatforms = new Set();
    return orderedAccounts
      .filter((account) => {
        if (!account?.platform || seenPlatforms.has(account.platform)) return false;
        seenPlatforms.add(account.platform);
        return true;
      })
      .map((account) => Number(account.id))
      .filter(Boolean);
  }

  const matchingAccount = orderedAccounts.find((account) => account?.platform === preference);
  return matchingAccount ? [Number(matchingAccount.id)] : [];
}

function getProjectedLeagueBand(teamRating) {
  const rating = Number(teamRating);
  if (!Number.isFinite(rating) || rating <= 0) {
    return { name: "Placement pending", range: "Needs a rated roster" };
  }
  if (rating <= 1200) return { name: "Novice", range: "1000-1200" };
  if (rating <= 1400) return { name: "Intermediate", range: "1201-1400" };
  if (rating <= 1700) return { name: "Advanced Intermediate", range: "1401-1700" };
  if (rating <= 2000) return { name: "Advanced", range: "1701-2000" };
  if (rating <= 2200) return { name: "Expert", range: "2001-2200" };
  if (rating <= 2400) return { name: "Champions", range: "2201-2400" };
  if (rating <= 2600) return { name: "Masters", range: "2401-2600" };
  return { name: "Legends", range: "2601+" };
}

function formatCompetitionMode(value) {
  if (value === "scholastic") return "Scholastic";
  if (value === "creator") return "Creator";
  return "Open";
}

function formatLeagueFormat(value) {
  if (value === "single_elimination") return "Single Elimination";
  if (value === "round_robin") return "Round Robin";
  return "Single Elimination";
}

function formatLeagueEntryType(value) {
  return value === "prize" ? "Prize League" : "Free League";
}

function formatLeagueStatus(value) {
  if (!value) return "Draft";
  return value
    .split("_")
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

function formatLeagueDateRange(league) {
  if (league?.start_date && league?.end_date) {
    return `${new Date(league.start_date).toLocaleDateString()} - ${new Date(league.end_date).toLocaleDateString()}`;
  }
  if (league?.registration_open_at && league?.registration_close_at) {
    return `Registration ${new Date(league.registration_open_at).toLocaleDateString()} - ${new Date(league.registration_close_at).toLocaleDateString()}`;
  }
  return "Dates pending";
}

function isLeagueOpenOrActive(league) {
  return league?.status === "registration_open" || league?.status === "active";
}

function compareLeagueDeskPriority(left, right) {
  const priority = (league) => {
    if (league?.status === "active") return 0;
    if (league?.status === "registration_open") return 1;
    if (league?.status === "registration_closed") return 2;
    if (league?.status === "draft") return 3;
    if (league?.status === "completed") return 4;
    return 5;
  };

  const priorityDelta = priority(left) - priority(right);
  if (priorityDelta !== 0) return priorityDelta;

  return String(left?.name || "").localeCompare(String(right?.name || ""));
}

async function readResponseData(response) {
  const raw = await response.text();
  let data = {};
  if (raw) {
    try {
      data = JSON.parse(raw);
    } catch {
      data = { error: raw.slice(0, 300) };
    }
  }
  return data;
}

async function postJson(url, body) {
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await readResponseData(response);
  return { response, data };
}

function getTeamBadgeMark(team) {
  const rawBadge = String(team?.badge || "").trim();
  if (rawBadge) return rawBadge.slice(0, 4);
  return "\u2658";
}

function normalizeCountry(country) {
  return String(country || "").trim().toLowerCase();
}

function formatPresenceLabel(isOnline) {
  return isOnline ? "online" : "offline";
}

function getMatchDurationLabel(value) {
  const match = MATCH_DURATION_OPTIONS.find((option) => Number(option.value) === Number(value));
  return match?.label || "Select Match Duration";
}

function getMatchTimeControlLabel(value) {
  return MATCH_TIME_CONTROL_OPTIONS.includes(value) ? value : "Select Time Control";
}

function App() {
  const supportedPlatforms = ["lichess", "chess.com"];
  const [signupForm, setSignupForm] = useState({ username: "", email: "", password: "", country: "", lichessUsername: "", chessComUsername: "" });
  const [loginForm, setLoginForm] = useState({ identifier: "", password: "" });
  const [profileForm, setProfileForm] = useState({ country: "" });
  const [accountLinkDrafts, setAccountLinkDrafts] = useState({ lichess: "", "chess.com": "" });
  const [teamForm, setTeamForm] = useState({
    name: "",
    owner_platform_account_ids: [],
    matchmaking_preference: "allow_all",
    bio: "",
    country: "",
    badge: "",
    league_registered: false,
    competition_mode: "open",
    grade_band: "",
  });
  const [matchForm, setMatchForm] = useState({ team_a_id: "", team_b_id: "", platform: "", time_control: "", match_duration_days: "", rated: false });
  const [currentUser, setCurrentUser] = useState(null);
  const [teams, setTeams] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [leagueTeamParticipation, setLeagueTeamParticipation] = useState([]);
  const [playerDirectory, setPlayerDirectory] = useState([]);
  const [matches, setMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [boardDrafts, setBoardDrafts] = useState({});
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [editTeamForm, setEditTeamForm] = useState({
    name: "",
    owner_platform_account_ids: [],
    matchmaking_preference: "allow_all",
    bio: "",
    country: "",
    badge: "",
    league_registered: false,
    competition_mode: "open",
    grade_band: "",
  });
  const [joiningTeamId, setJoiningTeamId] = useState(null);
  const [teamSearch, setTeamSearch] = useState("");
  const [rosterConfigs, setRosterConfigs] = useState([]);
  const [ratingHistory, setRatingHistory] = useState([]);
  const [boardAssignmentDraft, setBoardAssignmentDraft] = useState({});
  const [rosterConfigName, setRosterConfigName] = useState("");
  const [editingAccountPlatform, setEditingAccountPlatform] = useState(null);
  const [joinPlatformSelections, setJoinPlatformSelections] = useState({});
  const [inviteDrafts, setInviteDrafts] = useState({});
  const [invitePlatformSelections, setInvitePlatformSelections] = useState({});
  const [incomingInvites, setIncomingInvites] = useState([]);
  const [invitePreview, setInvitePreview] = useState(null);
  const [acceptingInviteId, setAcceptingInviteId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [teamLoading, setTeamLoading] = useState(false);
  const [matchLoading, setMatchLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [teamMessage, setTeamMessage] = useState("");
  const [teamError, setTeamError] = useState("");
  const [matchMessage, setMatchMessage] = useState("");
  const [matchError, setMatchError] = useState("");
  const initialRoute = getInitialRoute();
  const [currentPage, setCurrentPage] = useState(initialRoute.page);
  const [currentSubpage, setCurrentSubpage] = useState(initialRoute.subpage);
  const lastRouteHashRef = useRef(window.location.hash || initialRoute.hash);
  const [activeInviteToken, setActiveInviteToken] = useState(() => new URLSearchParams(window.location.search).get("invite") || "");
  const [memberTeams, setMemberTeams] = useState([]);
  const [activeTeamMembers, setActiveTeamMembers] = useState([]);
  const [teamExitPrompt, setTeamExitPrompt] = useState(null);
  const [selectedTeamProfile, setSelectedTeamProfile] = useState(null);
  const [showAllDiscoverTeams, setShowAllDiscoverTeams] = useState(false);
  const [recommendedTeamIndex, setRecommendedTeamIndex] = useState(0);
  const [activeMatchSettingPanel, setActiveMatchSettingPanel] = useState(null);
  const [showMatchComposer, setShowMatchComposer] = useState(false);
  const [matchComposerMode, setMatchComposerMode] = useState("search");
  const [matchOpponentQuery, setMatchOpponentQuery] = useState("");
  const [homeAuthMode, setHomeAuthMode] = useState("signup");
  const [leagueLoading, setLeagueLoading] = useState(false);
  const [leagueMessage, setLeagueMessage] = useState("");
  const [leagueError, setLeagueError] = useState("");
  const [editingLeagueId, setEditingLeagueId] = useState(null);
  const [leagueSeedDrafts, setLeagueSeedDrafts] = useState({});
  const [leagueForm, setLeagueForm] = useState({
    name: "",
    description: "",
    mode: "open",
    format: "single_elimination",
    entry_type: "free",
    status: "registration_open",
    max_teams: 32,
    season_label: "",
    grade_band: "",
    rules_text: "",
    tiebreak_rules_text: "",
    default_match_platform: "allow_all",
    default_time_control: "10+0",
    default_match_duration_days: 3,
    default_rated: false,
    requires_verification: false,
    allow_cross_mode_registration: false,
    is_public: true,
    registration_open_at: "",
    registration_close_at: "",
  });

  const linkedAccounts = currentUser?.linked_accounts || [];
  const linkedLichessUsername =
    linkedAccounts.find((account) => account.platform === "lichess")?.platform_username || "";
  const linkedChessComUsername =
    linkedAccounts.find((account) => account.platform === "chess.com")?.platform_username || "";
  const lichessAccount = linkedAccounts.find((account) => account.platform === "lichess") || null;
  const availablePlatformsToLink = supportedPlatforms.filter((platform) => !linkedAccounts.some((account) => account.platform === platform));
  const completedMatches = matches.filter((match) => match.status === "completed").length;
  const liveMatches = matches.filter((match) => match.status === "in_progress").length;
  const scheduledMatches = matches.filter((match) => match.status === "scheduled").length;
  const connectedLaunchCount = linkedAccounts.filter((account) => account.oauth_ready).length;
  const normalizedUserCountry = normalizeCountry(currentUser?.country);
  const activeMemberTeamIdForRefresh = memberTeams[0]?.id || null;
  const selectedTeamProfileIdForRefresh = selectedTeamProfile?.team?.id || null;
  const selectedLeagueIdForRefresh = selectedLeague?.id || null;
  const ownerManagedTeams = teams.filter((team) => team.owner_id === currentUser?.id);
  const selectedTeamA =
    ownerManagedTeams.find((team) => String(team.id) === String(matchForm.team_a_id)) ||
    ownerManagedTeams[0] ||
    null;
  const getSupportedPlatformIds = (team) => {
    const supported = (team?.supported_platforms || []).map((platformLink) => platformLink.platform);
    return supported.length > 0 ? supported : [team?.platform].filter(Boolean);
  };
  const getMatchPoolPlatforms = (team) => {
    const supported = getSupportedPlatformIds(team);
    const preference = String(team?.matchmaking_preference || "allow_all").trim().toLowerCase();
    if (preference === "allow_all") return supported;
    return supported.includes(preference) ? [preference] : supported;
  };
  const normalizedMatchOpponentQuery = String(matchOpponentQuery || "").trim().toLowerCase();
  const teamPresenceCounts = playerDirectory.reduce((counts, player) => {
    if (!player?.team_id || !player?.is_online) return counts;
    counts[player.team_id] = (counts[player.team_id] || 0) + 1;
    return counts;
  }, {});
  const availableOpponentTeams = selectedTeamA
    ? teams.filter((team) => {
        if (!team || team.id === selectedTeamA.id) return false;
        if (team.owner_id === currentUser?.id) return false;
        const sharedPlatforms = getMatchPoolPlatforms(selectedTeamA).filter((platformId) =>
          getMatchPoolPlatforms(team).includes(platformId)
        );
        return sharedPlatforms.length > 0;
      })
    : [];
  const searchedOpponentTeams = normalizedMatchOpponentQuery
    ? availableOpponentTeams.filter((team) =>
        [
          team.name,
          team.country,
          formatMatchmakingPreference(team.matchmaking_preference),
          ...(team.supported_platforms || []).map((platformLink) => platformLink.platform),
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedMatchOpponentQuery))
      )
    : availableOpponentTeams.slice(0, 6);
  const rankedLiveMatchCandidates = availableOpponentTeams
    .filter((team) => (teamPresenceCounts[team.id] || 0) > 0)
    .map((team) => {
      const teamRating = Number(team.competitive_rating || 1500);
      const ownerTeamRating = Number(selectedTeamA?.competitive_rating || 1500);
      const ratingGap = Math.abs(teamRating - ownerTeamRating);
      const sharedPlatforms = getMatchPoolPlatforms(selectedTeamA).filter((platformId) =>
        getMatchPoolPlatforms(team).includes(platformId)
      );
      const onlineCount = teamPresenceCounts[team.id] || 0;
      const score =
        ratingGap -
        onlineCount * 30 -
        sharedPlatforms.length * 10 -
        (normalizeCountry(team.country) && normalizeCountry(team.country) === normalizeCountry(selectedTeamA?.country)
          ? 8
          : 0);

      return { team, ratingGap, onlineCount, sharedPlatforms, score };
    })
    .sort((left, right) => left.score - right.score);
  const suggestedLiveOpponent = rankedLiveMatchCandidates[0]?.team || null;
  const activeChallengeTargetId =
    matchComposerMode === "live" ? String(suggestedLiveOpponent?.id || "") : String(matchForm.team_b_id || "");
  const selectedTeamB = teams.find((team) => String(team.id) === activeChallengeTargetId) || null;
  const sharedMatchPlatforms =
    selectedTeamA && selectedTeamB
      ? getMatchPoolPlatforms(selectedTeamA).filter((platformId) => getMatchPoolPlatforms(selectedTeamB).includes(platformId))
      : [];
  const allowMixedMatch =
    Boolean(selectedTeamA && selectedTeamB) &&
    selectedTeamA?.matchmaking_preference === "allow_all" &&
    selectedTeamB?.matchmaking_preference === "allow_all" &&
    sharedMatchPlatforms.length > 1;
  const matchPlatformOptions = allowMixedMatch ? ["allow_all", ...sharedMatchPlatforms] : sharedMatchPlatforms;
  const pendingMatchPlatform =
    matchPlatformOptions.includes(matchForm.platform)
      ? matchForm.platform
      : "";
  const chessComRatedDraft =
    (pendingMatchPlatform === "chess.com" || (pendingMatchPlatform === "allow_all" && sharedMatchPlatforms.includes("chess.com"))) &&
    matchForm.rated;
  const currentUserOwnsSelectedTeamA = Boolean(currentUser && selectedTeamA?.owner_id === currentUser.id);
  const currentUserOwnsSelectedTeamB = Boolean(currentUser && selectedTeamB?.owner_id === currentUser.id);
  const canCurrentUserRequestMatch = currentUserOwnsSelectedTeamA || currentUserOwnsSelectedTeamB;

  const pickErrorMessage = (data, fallback) => data?.details || data?.error || fallback;
  const syncCurrentUser = (user) => {
    localStorage.setItem("gca_user", JSON.stringify(user));
    setCurrentUser(user);
  };

  const handleLogout = () => {
    localStorage.removeItem("gca_user");
    setCurrentUser(null);
    setSelectedMatch(null);
    setBoardDrafts({});
    setAccountLinkDrafts({ lichess: "", "chess.com": "" });
    setTeamForm({ name: "", owner_platform_account_ids: [], matchmaking_preference: "allow_all", bio: "", country: "", badge: "", league_registered: false, competition_mode: "open", grade_band: "" });
    setEditTeamForm({ name: "", owner_platform_account_ids: [], matchmaking_preference: "allow_all", bio: "", country: "", badge: "", league_registered: false, competition_mode: "open", grade_band: "" });
    setJoinPlatformSelections({});
    setInviteDrafts({});
    setInvitePlatformSelections({});
    setIncomingInvites([]);
    setInvitePreview(null);
    setTeamSearch("");
    setMemberTeams([]);
    setActiveTeamMembers([]);
    setTeamExitPrompt(null);
    setSelectedTeamProfile(null);
    setMatchForm({ team_a_id: "", team_b_id: "", platform: "", time_control: "", match_duration_days: "", rated: false });
    setMatchComposerMode("search");
    setMatchOpponentQuery("");
    setActiveMatchSettingPanel(null);
    setShowMatchComposer(false);
    setMessage("");
    setError("");
    setTeamMessage("");
    setTeamError("");
    setMatchMessage("");
    setMatchError("");
  };

  useEffect(() => {
    const savedUser = localStorage.getItem("gca_user");
    if (!savedUser) return;
    try {
      setCurrentUser(JSON.parse(savedUser));
    } catch (err) {
      console.error("Failed to parse saved user:", err);
    }
  }, []);

  useEffect(() => {
    const syncPageFromHash = () => {
      const nextRoute = parseRouteFromHash(window.location.hash, { signedIn: Boolean(currentUser) });
      lastRouteHashRef.current = nextRoute.hash;
      setCurrentPage((previousPage) => (previousPage === nextRoute.page ? previousPage : nextRoute.page));
      setCurrentSubpage((previousSubpage) => (previousSubpage === nextRoute.subpage ? previousSubpage : nextRoute.subpage));
      if (window.location.hash !== nextRoute.hash) {
        window.history.replaceState(null, "", nextRoute.hash);
      }
    };
    const handleHashChange = () => syncPageFromHash();
    window.addEventListener("hashchange", handleHashChange);
    syncPageFromHash();
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [currentUser]);

  useEffect(() => {
    if (currentPage !== "play") return;
    if (currentSubpage === "create" && !showMatchComposer) {
      setShowMatchComposer(true);
    }
    if (currentSubpage === "matches" && showMatchComposer) {
      setShowMatchComposer(false);
    }
  }, [currentPage, currentSubpage, showMatchComposer]);

  useEffect(() => {
    setProfileForm({ country: currentUser?.country || "" });
  }, [currentUser?.country]);

  useEffect(() => {
    setAccountLinkDrafts({
      lichess: linkedLichessUsername,
      "chess.com": linkedChessComUsername,
    });
  }, [linkedChessComUsername, linkedLichessUsername]);

  useEffect(() => {
    if (!currentUser?.id) return undefined;

    let cancelled = false;

    const sendHeartbeat = async () => {
      try {
        const response = await fetch("/api/users", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "heartbeat",
            user_id: currentUser.id,
          }),
        });
        const data = await response.json().catch(() => ({}));

        if (!cancelled && response.ok && data?.id === currentUser.id) {
          localStorage.setItem("gca_user", JSON.stringify(data));
          setCurrentUser(data);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to refresh presence:", err);
        }
      }
    };

    sendHeartbeat();
    const intervalId = window.setInterval(sendHeartbeat, 60000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [currentUser?.id]);

  useEffect(() => {
    let cancelled = false;
    const completePkceLogin = async () => {
      try {
        const result = await finishLichessPkceLogin();
        if (!result || cancelled) return;
        clearLichessPkceQuery();
        const { response, data } = await postJson("/api/users", {
          action: "savePlatformToken",
          user_id: result.userId,
          platform_account_id: Number(result.platformAccountId),
          oauth_access_token: result.accessToken,
          oauth_scope: result.scope,
        });
        if (!response.ok) return setError(pickErrorMessage(data, "Failed to save Lichess connection."));
        if (data?.id === result.userId) {
          syncCurrentUser(data);
        }
        const nextRoute = parseRouteFromHash(buildHashRoute("account", null, { signedIn: true }), {
          signedIn: true,
        });
        if (window.location.hash !== nextRoute.hash) {
          window.location.hash = nextRoute.hash;
        }
        setCurrentPage(nextRoute.page);
        setCurrentSubpage(nextRoute.subpage);
        setMessage("Lichess connected successfully.");
      } catch (err) {
        if (!cancelled) {
          clearLichessPkceQuery();
          setError(err.message || "Failed to complete Lichess login.");
        }
      }
    };
    completePkceLogin();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  useEffect(() => {
    if (!["home", "teams", "play", "leagues"].includes(currentPage)) return;
    const loadPageData = async () => {
      const teamsResponse = await fetch("/api/teams");
      const teamsData = await readResponseData(teamsResponse);
      teamsResponse.ok ? setTeams(teamsData) : setTeamError(pickErrorMessage(teamsData, "Failed to load teams"));

      const matchesResponse = await fetch("/api/matches");
      const matchesData = await readResponseData(matchesResponse);
      matchesResponse.ok ? setMatches(matchesData) : setMatchError(pickErrorMessage(matchesData, "Failed to load matches"));

      if (currentPage === "leagues") {
        const leaguesResponse = await fetch("/api/leagues");
        const leaguesData = await readResponseData(leaguesResponse);
        leaguesResponse.ok ? setLeagues(leaguesData) : setLeagueError(pickErrorMessage(leaguesData, "Failed to load leagues"));
      }
    };
    loadPageData();
  }, [currentPage]);

  useEffect(() => {
    if (currentPage !== "teams") return;

    const loadPlayerDirectory = async () => {
      const response = await fetch("/api/teams?directory=players");
      const data = await readResponseData(response);
      if (!response.ok) {
        setTeamError(pickErrorMessage(data, "Failed to load player directory"));
        return;
      }
      setPlayerDirectory(data);
    };

    loadPlayerDirectory();
  }, [currentPage]);

  useEffect(() => {
    if (currentPage !== "teams" || !currentUser) return;

    const loadInvites = async () => {
      try {
        const incomingResponse = await fetch(`/api/teams?invites_for_user_id=${currentUser.id}`);
        const incomingData = await readResponseData(incomingResponse);

        incomingResponse.ok
          ? setIncomingInvites(incomingData)
          : setTeamError(pickErrorMessage(incomingData, "Failed to load incoming invites"));
      } catch (err) {
        setTeamError(err.message || "Failed to load invite data.");
      }
    };

    loadInvites();
  }, [currentPage, currentUser]);

  useEffect(() => {
    if (!currentUser?.id) return undefined;

    let cancelled = false;

    const refreshSignedInDesk = async () => {
      try {
        const requestEntries = [
          ["invites", fetch(`/api/teams?invites_for_user_id=${currentUser.id}`)],
          ["memberships", fetch(`/api/teams?member_of_user_id=${currentUser.id}`)],
        ];

        if (["home", "teams", "play", "leagues"].includes(currentPage)) {
          requestEntries.push(["teams", fetch("/api/teams")]);
          requestEntries.push(["matches", fetch("/api/matches")]);
        }

        if (currentPage === "account") {
          requestEntries.push(["ratingHistory", fetch(`/api/users/rating-history?user_id=${currentUser.id}`)]);
        }

        if (currentPage === "teams") {
          requestEntries.push(["players", fetch("/api/teams?directory=players")]);
          if (activeMemberTeamIdForRefresh) {
            requestEntries.push(["teamMembers", fetch(`/api/teams?team_id=${activeMemberTeamIdForRefresh}`)]);
            requestEntries.push(["rosterConfigs", fetch(`/api/teams/roster-configs?team_id=${activeMemberTeamIdForRefresh}`)]);
          }
          if (selectedTeamProfileIdForRefresh) {
            requestEntries.push(["teamProfile", fetch(`/api/teams?team_profile_id=${selectedTeamProfileIdForRefresh}`)]);
          }
          if (activeInviteToken) {
            requestEntries.push(["invitePreview", fetch(`/api/teams?invite_token=${encodeURIComponent(activeInviteToken)}`)]);
          }
        }

        if (currentPage === "play" && selectedMatch?.id) {
          requestEntries.push(["selectedMatch", fetch(`/api/matches?match_id=${selectedMatch.id}`)]);
        }

        if (currentPage === "leagues") {
          requestEntries.push(["leagues", fetch("/api/leagues")]);
          if (selectedLeagueIdForRefresh) {
            requestEntries.push(["leagueDetail", fetch(`/api/leagues?league_id=${selectedLeagueIdForRefresh}`)]);
          }
          if (activeMemberTeamIdForRefresh) {
            requestEntries.push(["teamLeagues", fetch(`/api/leagues?team_id=${activeMemberTeamIdForRefresh}`)]);
          }
        }

        const responses = await Promise.all(requestEntries.map(([, request]) => request));
        const payloads = await Promise.all(responses.map(async (response) => ({
          ok: response.ok,
          data: await readResponseData(response),
        })));

        if (cancelled) return;

        requestEntries.forEach(([key], index) => {
          const { ok, data } = payloads[index];
          if (!ok) return;

          if (key === "invites") setIncomingInvites(data);
          if (key === "memberships") setMemberTeams(data);
          if (key === "teams") setTeams(data);
          if (key === "matches") setMatches(data);
          if (key === "players") setPlayerDirectory(data);
          if (key === "teamMembers") setActiveTeamMembers(data);
          if (key === "rosterConfigs") setRosterConfigs(Array.isArray(data) ? data : []);
          if (key === "ratingHistory") setRatingHistory(Array.isArray(data) ? data : []);
          if (key === "teamProfile") setSelectedTeamProfile(data);
          if (key === "invitePreview") setInvitePreview(data);
          if (key === "selectedMatch") setSelectedMatch(data);
          if (key === "leagues") setLeagues(data);
          if (key === "leagueDetail") {
            setSelectedLeague(data);
            hydrateLeagueSeedDrafts(data);
          }
          if (key === "teamLeagues") setLeagueTeamParticipation(data);
        });
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to refresh signed-in desk:", err);
        }
      }
    };

    refreshSignedInDesk();
    const intervalId = window.setInterval(refreshSignedInDesk, 12000);

    const handleWindowFocus = () => {
      refreshSignedInDesk();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshSignedInDesk();
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    currentUser?.id,
    currentPage,
    activeMemberTeamIdForRefresh,
    selectedMatch?.id,
    selectedTeamProfileIdForRefresh,
    activeInviteToken,
    selectedLeagueIdForRefresh,
  ]);

  useEffect(() => {
    if (currentPage !== "leagues") return;

    const loadLeagueDesk = async () => {
      setLeagueLoading(true);
      try {
        const leaguesResponse = await fetch("/api/leagues");
        const leagueRows = await leaguesResponse.json();

        if (!leaguesResponse.ok) {
          setLeagueError(pickErrorMessage(leagueRows, "Failed to load leagues"));
          setLeagueLoading(false);
          return;
        }

        setLeagues(leagueRows);

        if (currentUser?.id && activeMemberTeamIdForRefresh) {
          const teamParticipationResponse = await fetch(`/api/leagues?team_id=${activeMemberTeamIdForRefresh}`);
          const teamParticipationData = await teamParticipationResponse.json();
          if (teamParticipationResponse.ok) {
            setLeagueTeamParticipation(teamParticipationData);
          } else {
            setLeagueError(pickErrorMessage(teamParticipationData, "Failed to load league participation"));
          }
        } else {
          setLeagueTeamParticipation([]);
        }

        const nextLeagueId = selectedLeagueIdForRefresh || leagueRows[0]?.id || null;
        if (nextLeagueId) {
          const leagueDetailResponse = await fetch(`/api/leagues?league_id=${nextLeagueId}`);
          const leagueDetailData = await leagueDetailResponse.json();
          if (leagueDetailResponse.ok) {
            setSelectedLeague(leagueDetailData);
            hydrateLeagueSeedDrafts(leagueDetailData);
          } else {
            setLeagueError(pickErrorMessage(leagueDetailData, "Failed to load league details"));
          }
        } else {
          setSelectedLeague(null);
        }
      } catch (err) {
        setLeagueError(err.message || "Failed to load the league desk");
      }
      setLeagueLoading(false);
    };

    loadLeagueDesk();
  }, [currentPage, currentUser?.id, activeMemberTeamIdForRefresh, selectedLeagueIdForRefresh]);

  useEffect(() => {
    if (currentPage !== "teams") return;
    if (!currentUser) {
      setMemberTeams([]);
      return;
    }
    const loadMemberTeams = async () => {
      const response = await fetch(`/api/teams?member_of_user_id=${currentUser.id}`);
      const data = await response.json();
      if (!response.ok) {
        setTeamError(pickErrorMessage(data, "Failed to load your teams"));
        return;
      }
      setMemberTeams(data);
    };
    loadMemberTeams();
  }, [currentPage, currentUser]);

  useEffect(() => {
    if (currentPage !== "teams") return;
    const activeTeamId = memberTeams[0]?.id;
    if (!activeTeamId) {
      setActiveTeamMembers([]);
      return;
    }

    const loadActiveTeamMembers = async () => {
      const response = await fetch(`/api/teams?team_id=${activeTeamId}`);
      const data = await response.json();
      if (!response.ok) {
        setTeamError(pickErrorMessage(data, "Failed to load current team members"));
        return;
      }
      setActiveTeamMembers(data);
    };

    loadActiveTeamMembers();
  }, [currentPage, memberTeams]);

  useEffect(() => {
    if (currentPage !== "teams" || !activeInviteToken) return;

    const loadInvitePreview = async () => {
      try {
        const response = await fetch(`/api/teams?invite_token=${encodeURIComponent(activeInviteToken)}`);
        const data = await response.json();

        if (!response.ok) {
          setInvitePreview(null);
          setTeamError(pickErrorMessage(data, "Failed to load invite"));
          return;
        }

        setInvitePreview(data);
      } catch (err) {
        setTeamError(err.message || "Failed to load invite");
      }
    };

    loadInvitePreview();
  }, [activeInviteToken, currentPage]);

  useEffect(() => {
    setShowAllDiscoverTeams(false);
  }, [teamSearch, currentPage, currentSubpage, currentUser, selectedTeamProfileIdForRefresh]);

  const setFormValue = (setter) => (e) => {
    const { name, value, type, checked } = e.target;
    setter((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };
  const handleSignupChange = setFormValue(setSignupForm);
  const handleLoginChange = setFormValue(setLoginForm);
  const handleProfileChange = setFormValue(setProfileForm);
  const handleTeamChange = setFormValue(setTeamForm);
  const handleEditTeamChange = setFormValue(setEditTeamForm);
  const handleMatchChange = setFormValue(setMatchForm);
  const handleAccountLinkDraftChange = (platform, value) => {
    setAccountLinkDrafts((prev) => ({
      ...prev,
      [platform]: value,
    }));
  };

  const renderTeamNameButton = (team, className = "") => (
    <button
      type="button"
      className={`textActionButton teamNameLink ${className}`.trim()}
      onClick={(event) => {
        event.stopPropagation();
        handleOpenTeamProfile(team);
      }}
    >
      {team.name}
    </button>
  );

  const renderMatchSettingSelector = ({ id, label, valueLabel, active, onToggle, children, disabled }) => (
    <div className={`matchSettingCard ${active ? "isOpen" : ""} ${disabled ? "isDisabled" : ""}`}>
      <button type="button" className="matchSettingTrigger" onClick={onToggle} disabled={disabled}>
        <div>
          <span className="matchSettingLabel">{label}</span>
          <strong>{valueLabel}</strong>
        </div>
        <span className="matchSettingChevron">{active ? "Close" : "Select"}</span>
      </button>
      {active && (
        <div className="matchSettingPanel" id={id}>
          {children}
        </div>
      )}
    </div>
  );

  const renderBadgePicker = (selectedBadge, onSelect) => (
    <div className="badgePicker">
      {TEAM_BADGE_OPTIONS.map((badgeOption) => (
        <button
          key={badgeOption.value}
          type="button"
          className={`badgeOption ${selectedBadge === badgeOption.value ? "isSelected" : ""}`}
          onClick={() => onSelect(badgeOption.value)}
          title={badgeOption.label}
          aria-label={`Use ${badgeOption.label} badge`}
        >
          <span className="badgeOptionMark">{badgeOption.value}</span>
          <span className="badgeOptionLabel">{badgeOption.label}</span>
        </button>
      ))}
    </div>
  );

  const formatAccountRating = (account) => {
    if (!account?.rating_value) return "rating unavailable";
    return `${account.rating_label || "rating"} ${account.rating_value}`;
  };

  const renderAccountChoiceGroup = (accounts, selectedId, onSelect) => (
    <div className="accountChoiceGroup">
      {accounts.map((account) => {
        const isSelected = String(selectedId) === String(account.id);
        return (
          <button
            key={`account-choice-${account.id}`}
            type="button"
            className={`accountChoiceCard ${isSelected ? "isSelected" : ""}`}
            onClick={() => onSelect(String(account.id))}
          >
            <span className="accountChoicePlatform">{account.platform}</span>
            <strong>{account.platform_username}</strong>
            <small>{formatAccountRating(account)}</small>
          </button>
        );
      })}
    </div>
  );

  const handleOpenTeamProfile = async (team) => {
    setTeamError("");
    navigateTeamView("team-page");
    await fetchTeamProfile(team.id);
  };

  const handleTeamCardKeyDown = (event, team) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleOpenTeamProfile(team);
    }
  };

  const renderDiscoverTeamCard = (team, { featured = false } = {}) => {
    const matchingAccounts = linkedAccounts.filter((account) =>
      (team.supported_platforms || []).some((platformLink) => platformLink.platform === account.platform) ||
      account.platform === team.platform
    );

    const selectedJoinAccountId = joinPlatformSelections[team.id] || String(matchingAccounts[0]?.id || "");
    const isCurrentMembership = memberTeams.some((memberTeam) => memberTeam.id === team.id);
    const leagueBand = getProjectedLeagueBand(team.team_rating);

    return (
      <div
        key={team.id}
        className={`teamListCard directoryCard isTeamPageTrigger ${isCurrentMembership ? "isCurrentTeam" : ""} ${featured ? "featuredDirectoryCard" : ""}`}
        role="button"
        tabIndex={0}
        onClick={() => handleOpenTeamProfile(team)}
        onKeyDown={(event) => handleTeamCardKeyDown(event, team)}
      >
        <div className="teamCardHeader">
          <div>
            <h3>{renderTeamNameButton(team)}</h3>
            <p className="teamMetaLine">
              {team.team_rating ? `avg ${team.team_rating}` : "avg pending"} | site {team.competitive_rating || 1500} | {team.member_count} players
            </p>
          </div>
          {isCurrentMembership ? <span className="ownerBadge">Current Team</span> : featured ? <span className="statusPill is-in-progress">Recommended</span> : null}
        </div>
        <div className="teamIdentityRow">
          <span className="teamBadgeMark">{getTeamBadgeMark(team)}</span>
          {team.country && <span className="teamIdentityChip">{team.country}</span>}
          <span className="teamIdentityChip">{leagueBand.name}</span>
          <span className={`teamIdentityChip ${team.league_registered ? "isLeagueRegistered" : ""}`}>
            {team.league_registered ? "League registered" : "League optional"}
          </span>
        </div>
        {team.bio && <p className="teamBioText">{team.bio}</p>}
        <p className="compactNote">Matchmaking: {formatMatchmakingPreference(team.matchmaking_preference)}</p>
        <div className="teamPlatformList">
          {(team.supported_platforms || []).map((platformLink) => (
            <span key={`${team.id}-${platformLink.platform}`} className="teamPlatformChip">
              {platformLink.platform} - {platformLink.platform_username}
            </span>
          ))}
        </div>
        {currentUser && isCurrentMembership ? (
          <>
            <p className="mutedText">You are already on this roster.</p>
          </>
        ) : currentUser && !canManageMembership ? (
          <>
            <p className="mutedText">Leave your current team before joining another one.</p>
          </>
        ) : currentUser && matchingAccounts.length > 0 ? (
          <>
            <div className="fieldBlock compactFieldBlock">
              <div className="fieldBlockHeader">
                <strong>Pick your seat</strong>
                <span>Choose the linked account you want to represent on this roster.</span>
              </div>
              {renderAccountChoiceGroup(
                matchingAccounts,
                selectedJoinAccountId,
                (nextId) => setJoinPlatformSelections((prev) => ({ ...prev, [team.id]: nextId }))
              )}
            </div>
            <div className="teamCardActions">
              <button className="secondaryButton joinTeamButton" type="button" disabled={joiningTeamId === team.id || teamLoading} onClick={(event) => { event.stopPropagation(); handleJoinTeam(team); }}>
                {joiningTeamId === team.id ? "Joining..." : "Join Team"}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="mutedText">
              {currentUser
                ? "Link one of this team's supported platforms before joining."
                : "Sign in to join this team."}
            </p>
          </>
        )}
      </div>
    );
  };

  const renderMatchmakingPreferenceControl = (value, selectedAccountIds, onChange, variant = "full") => {
    const supportedPlatforms = getPlatformsForSelectedAccounts(linkedAccounts, selectedAccountIds);

    return (
      <div className={`preferenceControl preferenceControl-${variant}`}>
        {MATCHMAKING_OPTIONS.map((option) => {
          const isSupported = option.value === "allow_all" || supportedPlatforms.includes(option.value);
          const isSelected = value === option.value;

          return (
            <button
              key={option.value}
              type="button"
              className={`preferenceChip ${isSelected ? "isSelected" : ""} ${!isSupported ? "isDisabled" : ""}`}
              onClick={() => isSupported && onChange(option.value)}
              disabled={!isSupported}
            >
              <span>{variant === "compact" ? option.shortLabel : option.label}</span>
              {variant !== "compact" && <small>{option.description}</small>}
            </button>
          );
        })}
      </div>
    );
  };

  const buildSignupAccounts = () => {
    const accounts = [];
    if (signupForm.lichessUsername.trim()) accounts.push({ platform: "lichess", platform_username: signupForm.lichessUsername.trim() });
    if (signupForm.chessComUsername.trim()) accounts.push({ platform: "chess.com", platform_username: signupForm.chessComUsername.trim() });
    return accounts;
  };

  const hydrateBoardDrafts = (match) => {
    const nextDrafts = {};
    for (const board of match?.boards || []) nextDrafts[board.id] = { game_link: board.game_link || "", result: board.result || "", notes: board.notes || "" };
    setBoardDrafts(nextDrafts);
  };

  const fetchTeams = async () => {
    const response = await fetch("/api/teams");
    const data = await readResponseData(response);
    if (!response.ok) return setTeamError(pickErrorMessage(data, "Failed to load teams"));
    setTeams(data);
  };

  const fetchPlayerDirectory = async () => {
    const response = await fetch("/api/teams?directory=players");
    const data = await readResponseData(response);
    if (!response.ok) return setTeamError(pickErrorMessage(data, "Failed to load player directory"));
    setPlayerDirectory(data);
  };

  const fetchMemberTeams = async (userId) => {
    if (!userId) {
      setMemberTeams([]);
      return;
    }
    const response = await fetch(`/api/teams?member_of_user_id=${userId}`);
    const data = await readResponseData(response);
    if (!response.ok) return setTeamError(pickErrorMessage(data, "Failed to load your teams"));
    setMemberTeams(data);
  };

  const fetchTeamMembers = async (teamId) => {
    if (!teamId) {
      setActiveTeamMembers([]);
      return [];
    }
    const response = await fetch(`/api/teams?team_id=${teamId}`);
    const data = await readResponseData(response);
    if (!response.ok) {
      setTeamError(pickErrorMessage(data, "Failed to load team members"));
      return [];
    }
    setActiveTeamMembers(data);
    return data;
  };

  const fetchTeamProfile = async (teamId) => {
    if (!teamId) {
      setSelectedTeamProfile(null);
      return null;
    }
    const response = await fetch(`/api/teams?team_profile_id=${teamId}`);
    const data = await readResponseData(response);
    if (!response.ok) {
      setTeamError(pickErrorMessage(data, "Failed to load team profile"));
      return null;
    }
    setSelectedTeamProfile(data);
    return data;
  };

  const fetchIncomingInvites = async (userId) => {
    if (!userId) return;
    const response = await fetch(`/api/teams?invites_for_user_id=${userId}`);
    const data = await readResponseData(response);
    if (!response.ok) return setTeamError(pickErrorMessage(data, "Failed to load incoming invites"));
    setIncomingInvites(data);
  };

  const fetchMatches = async () => {
    const response = await fetch("/api/matches");
    const data = await readResponseData(response);
    if (!response.ok) return setMatchError(pickErrorMessage(data, "Failed to load matches"));
    setMatches(data);
  };

  const fetchMatchDetail = async (matchId) => {
    setMatchLoading(true);
    const response = await fetch(`/api/matches?match_id=${matchId}`);
    const data = await readResponseData(response);
    if (!response.ok) {
      setMatchLoading(false);
      return setMatchError(pickErrorMessage(data, "Failed to load match details"));
    }
    setSelectedMatch(data);
    hydrateBoardDrafts(data);
    setMatchLoading(false);
  };

  const hydrateLeagueSeedDrafts = (league) => {
    const nextDrafts = {};
    for (const registration of league?.registrations || []) {
      if (registration.registration_status !== "approved") continue;
      nextDrafts[registration.id] = registration.seed_number ? String(registration.seed_number) : "";
    }
    setLeagueSeedDrafts(nextDrafts);
  };

  const fetchLeagues = async () => {
    const response = await fetch("/api/leagues");
    const data = await readResponseData(response);
    if (!response.ok) {
      setLeagueError(pickErrorMessage(data, "Failed to load leagues"));
      return [];
    }
    setLeagues(data);
    return data;
  };

  const fetchLeagueDetail = async (leagueId) => {
    if (!leagueId) {
      setSelectedLeague(null);
      return null;
    }

    const response = await fetch(`/api/leagues?league_id=${leagueId}`);
    const data = await readResponseData(response);
    if (!response.ok) {
      setLeagueError(pickErrorMessage(data, "Failed to load league details"));
      return null;
    }
    setSelectedLeague(data);
    hydrateLeagueSeedDrafts(data);
    return data;
  };

  const fetchTeamLeagueParticipation = async (teamId) => {
    if (!teamId) {
      setLeagueTeamParticipation([]);
      return [];
    }

    const response = await fetch(`/api/leagues?team_id=${teamId}`);
    const data = await readResponseData(response);
    if (!response.ok) {
      setLeagueError(pickErrorMessage(data, "Failed to load league participation"));
      return [];
    }
    setLeagueTeamParticipation(data);
    return data;
  };

  const handleLeagueFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setLeagueForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const navigateLeagueSubpage = (subpage = "overview") => {
    handleNavigate("leagues", subpage);
  };

  const openLeague = async (league, preferredSubpage = null) => {
    setLeagueError("");
    setLeagueMessage("");
    await fetchLeagueDetail(league.id);
    navigateLeagueSubpage(preferredSubpage || (currentPage === "leagues" && currentSubpage === "rules" ? "rules" : "overview"));
  };

  const resetLeagueForm = () => {
    setEditingLeagueId(null);
    setLeagueForm({
      name: "",
      description: "",
      mode: "open",
      format: "single_elimination",
      entry_type: "free",
      status: "registration_open",
      max_teams: 32,
      season_label: "",
      grade_band: "",
      rules_text: "",
      tiebreak_rules_text: "",
      default_match_platform: "allow_all",
      default_time_control: "10+0",
      default_match_duration_days: 3,
      default_rated: false,
      requires_verification: false,
      allow_cross_mode_registration: false,
      is_public: true,
      registration_open_at: "",
      registration_close_at: "",
    });
  };

  const startEditingLeague = (league) => {
    setEditingLeagueId(league.id);
    setLeagueForm({
      name: league.name || "",
      description: league.description || "",
      mode: league.mode || "open",
      format: league.format || "single_elimination",
      entry_type: league.entry_type || "free",
      status: league.status || "registration_open",
      max_teams: league.max_teams || 32,
      season_label: league.season_label || "",
      grade_band: league.grade_band || "",
      rules_text: league.rules_text || "",
      tiebreak_rules_text: league.tiebreak_rules_text || "",
      default_match_platform: league.default_match_platform || "allow_all",
      default_time_control: league.default_time_control || "10+0",
      default_match_duration_days: league.default_match_duration_days || 3,
      default_rated: Boolean(league.default_rated),
      requires_verification: Boolean(league.requires_verification),
      allow_cross_mode_registration: Boolean(league.allow_cross_mode_registration),
      is_public: league.is_public !== false,
      registration_open_at: league.registration_open_at ? String(league.registration_open_at).slice(0, 16) : "",
      registration_close_at: league.registration_close_at ? String(league.registration_close_at).slice(0, 16) : "",
    });
    navigateLeagueSubpage("edit");
  };

  const buildLeaguePayload = () => ({
    name: leagueForm.name,
    description: leagueForm.description,
    mode: leagueForm.mode,
    format: leagueForm.format,
    entry_type: leagueForm.entry_type,
    status: leagueForm.status,
    max_teams: Number(leagueForm.max_teams) || 32,
    season_label: leagueForm.season_label,
    grade_band: leagueForm.mode === "scholastic" ? leagueForm.grade_band : "",
    rules_text: leagueForm.rules_text,
    tiebreak_rules_text: leagueForm.tiebreak_rules_text,
    default_match_platform: leagueForm.default_match_platform,
    default_time_control: leagueForm.default_time_control,
    default_match_duration_days: Number(leagueForm.default_match_duration_days) || 3,
    default_rated: Boolean(leagueForm.default_rated),
    requires_verification: Boolean(leagueForm.requires_verification),
    allow_cross_mode_registration: Boolean(leagueForm.allow_cross_mode_registration),
    is_public: Boolean(leagueForm.is_public),
    registration_open_at: leagueForm.registration_open_at || null,
    registration_close_at: leagueForm.registration_close_at || null,
  });

  const handleCreateLeague = async (e) => {
    e.preventDefault();
    if (!currentUser) return;

    setLeagueMessage("");
    setLeagueError("");
    setLeagueLoading(true);

    const { response, data } = await postJson("/api/leagues", {
      action: "create_league",
      created_by_user_id: currentUser.id,
      ...buildLeaguePayload(),
    });

    if (!response.ok) {
      setLeagueError(pickErrorMessage(data, "Failed to create league"));
      setLeagueLoading(false);
      return;
    }

    setSelectedLeague(data);
    hydrateLeagueSeedDrafts(data);
    setLeagueMessage(`League "${data.name}" created.`);
    resetLeagueForm();
    navigateLeagueSubpage("overview");
    await fetchLeagues();
    setLeagueLoading(false);
  };

  const handleUpdateLeague = async (e) => {
    e.preventDefault();
    if (!currentUser || !editingLeagueId) return;

    setLeagueMessage("");
    setLeagueError("");
    setLeagueLoading(true);

    const { response, data } = await postJson("/api/leagues", {
      action: "update_league",
      league_id: editingLeagueId,
      user_id: currentUser.id,
      ...buildLeaguePayload(),
    });

    if (!response.ok) {
      setLeagueError(pickErrorMessage(data, "Failed to update league"));
      setLeagueLoading(false);
      return;
    }

    setSelectedLeague(data);
    hydrateLeagueSeedDrafts(data);
    setLeagueMessage(`League "${data.name}" updated.`);
    resetLeagueForm();
    navigateLeagueSubpage("overview");
    await fetchLeagues();
    setLeagueLoading(false);
  };

  const handleRegisterOwnedTeamForLeague = async (teamId) => {
    if (!currentUser || !selectedLeague?.id || !teamId) return;

    setLeagueMessage("");
    setLeagueError("");
    setLeagueLoading(true);

    const { response, data } = await postJson("/api/leagues", {
      action: "register_team",
      league_id: selectedLeague.id,
      team_id: teamId,
      user_id: currentUser.id,
    });

    if (!response.ok) {
      setLeagueError(pickErrorMessage(data, "Failed to register team"));
      setLeagueLoading(false);
      return;
    }

    setSelectedLeague(data);
    hydrateLeagueSeedDrafts(data);
    setLeagueMessage("Team registered and added to the league.");
    await Promise.all([
      fetchLeagues(),
      activeMemberTeamIdForRefresh ? fetchTeamLeagueParticipation(activeMemberTeamIdForRefresh) : Promise.resolve([]),
    ]);
    setLeagueLoading(false);
  };

  const handleSaveLeagueSeeds = async () => {
    if (!currentUser || !selectedLeague?.id) return;

    const seedAssignments = (selectedLeague.registrations || [])
      .filter((registration) => registration.registration_status === "approved")
      .map((registration) => ({
        league_team_id: registration.id,
        team_id: registration.team_id,
        seed_number: Number(leagueSeedDrafts[registration.id] || 0),
      }))
      .filter((entry) => Number.isInteger(entry.seed_number) && entry.seed_number > 0);

    if (seedAssignments.length === 0) {
      setLeagueError("Add at least one seed assignment before saving.");
      return;
    }

    setLeagueMessage("");
    setLeagueError("");
    setLeagueLoading(true);

    const { response, data } = await postJson("/api/leagues", {
      action: "assign_seeds",
      league_id: selectedLeague.id,
      user_id: currentUser.id,
      seeds: seedAssignments,
    });

    if (!response.ok) {
      setLeagueError(pickErrorMessage(data, "Failed to save seeds"));
      setLeagueLoading(false);
      return;
    }

    setSelectedLeague(data);
    hydrateLeagueSeedDrafts(data);
    setLeagueMessage("Seeds saved.");
    await fetchLeagues();
    setLeagueLoading(false);
  };

  const handleGenerateLeagueBracket = async () => {
    if (!currentUser || !selectedLeague?.id) return;

    setLeagueMessage("");
    setLeagueError("");
    setLeagueLoading(true);

    const { response, data } = await postJson("/api/leagues", {
      action: "generate_bracket",
      league_id: selectedLeague.id,
      user_id: currentUser.id,
    });

    if (!response.ok) {
      setLeagueError(pickErrorMessage(data, "Failed to generate bracket"));
      setLeagueLoading(false);
      return;
    }

    setSelectedLeague(data);
    hydrateLeagueSeedDrafts(data);
    setLeagueMessage("Bracket generated.");
    await fetchLeagues();
    setLeagueLoading(false);
  };

  const handleSetLeagueRoundStatus = async (roundId, status) => {
    if (!currentUser || !selectedLeague?.id || !roundId) return;

    setLeagueMessage("");
    setLeagueError("");
    setLeagueLoading(true);

    const { response, data } = await postJson("/api/leagues", {
      action: "set_round_status",
      league_id: selectedLeague.id,
      round_id: roundId,
      status,
      user_id: currentUser.id,
    });

    if (!response.ok) {
      setLeagueError(pickErrorMessage(data, "Failed to update round status"));
      setLeagueLoading(false);
      return;
    }

    setSelectedLeague(data);
    hydrateLeagueSeedDrafts(data);
    setLeagueMessage(`Round moved to ${formatLeagueStatus(status).toLowerCase()}.`);
    await fetchLeagues();
    setLeagueLoading(false);
  };

  const handleResolveLeagueMatch = async (matchId, winnerTeamId) => {
    if (!currentUser || !selectedLeague?.id || !matchId || !winnerTeamId) return;

    setLeagueMessage("");
    setLeagueError("");
    setLeagueLoading(true);

    const { response, data } = await postJson("/api/leagues", {
      action: "resolve_match",
      league_id: selectedLeague.id,
      match_id: matchId,
      winner_team_id: winnerTeamId,
      user_id: currentUser.id,
    });

    if (!response.ok) {
      setLeagueError(pickErrorMessage(data, "Failed to resolve match"));
      setLeagueLoading(false);
      return;
    }

    setSelectedLeague(data);
    hydrateLeagueSeedDrafts(data);
    setLeagueMessage("League match resolved and bracket updated.");
    await Promise.all([fetchLeagues(), fetchMatches()]);
    setLeagueLoading(false);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    setLoading(true);
    const accounts = buildSignupAccounts();
    if (!accounts.length) {
      setError("Add at least one chess platform username to create your account.");
      setLoading(false);
      return;
    }
    const { response, data } = await postJson("/api/users", {
      username: signupForm.username,
      email: signupForm.email,
      password: signupForm.password,
      country: signupForm.country,
      accounts,
    });
    if (!response.ok) {
      setError(pickErrorMessage(data, "Sign up failed"));
      setLoading(false);
      return;
    }
    syncCurrentUser(data);
    setMessage("Account created successfully.");
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    setLoading(true);
    try {
      const { response, data } = await postJson("/api/users", { action: "login", ...loginForm });
      if (!response.ok) {
        setError(pickErrorMessage(data, "Log in failed"));
        setLoading(false);
        return;
      }
    syncCurrentUser(data);
    setSelectedMatch(null);
    setBoardDrafts({});
    handleNavigate("account");
    setTeamForm({
      name: "",
      owner_platform_account_ids: data.linked_accounts?.map((account) => String(account.id)).slice(0, 1) || [],
      matchmaking_preference: "allow_all",
      bio: "",
      country: "",
      badge: "",
      league_registered: false,
      competition_mode: "open",
      grade_band: "",
    });
      setMessage(`Signed in as ${data.username}.`);
    } catch (err) {
      setError(err.message || "Log in failed");
    }
    setLoading(false);
  };

  const handleLinkAccount = async (e, platform) => {
    e.preventDefault();
    setMessage("");
    setError("");
    setLoading(true);
    const linkedPlatform = platform;
    const platformUsername = String(accountLinkDrafts[linkedPlatform] || "").trim();
    if (!platformUsername) {
      setError(`Enter a ${linkedPlatform} username first.`);
      setLoading(false);
      return;
    }
    const { response, data } = await postJson("/api/users", {
      action: "linkAccount",
      user_id: currentUser.id,
      platform: linkedPlatform,
      platform_username: platformUsername,
    });
    if (!response.ok) {
      setError(pickErrorMessage(data, "Failed to link account"));
      setLoading(false);
      return;
    }
    syncCurrentUser(data);
    setAccountLinkDrafts((prev) => ({
      ...prev,
      [linkedPlatform]: "",
    }));
    setMessage(`Connected ${linkedPlatform} account successfully.`);
    setLoading(false);
  };

  const handleConnectLichess = async () => {
    if (!currentUser || !lichessAccount) return;
    setMessage("");
    setError("");
    try {
      await startLichessPkceLogin({ userId: currentUser.id, platformAccountId: lichessAccount.id });
    } catch (err) {
      setError(err.message || "Failed to start Lichess connection.");
    }
  };

  const handleReconnectPlatform = async (platform) => {
    if (!currentUser) return;

    const platformUsername = String(accountLinkDrafts[platform] || "").trim();
    if (!platformUsername) {
      setError(`Enter a ${platform} username first.`);
      return;
    }

    setMessage("");
    setError("");
    setLoading(true);

    const { response, data } = await postJson("/api/users", {
      action: "updateAccountLink",
      user_id: currentUser.id,
      platform,
      platform_username: platformUsername,
    });

    if (!response.ok) {
      setError(pickErrorMessage(data, `Failed to reconnect ${platform}`));
      setLoading(false);
      return;
    }

    syncCurrentUser(data);
    setAccountLinkDrafts((prev) => ({
      ...prev,
      [platform]: data.linked_accounts?.find((account) => account.platform === platform)?.platform_username || "",
    }));
    setMessage(`${platform} account updated successfully.`);
    setLoading(false);
  };

  const handleSaveRosterConfig = async () => {
    if (!activeMemberTeam?.id || !rosterConfigName.trim()) return;
    const slots = Object.entries(boardAssignmentDraft).map(([board, memberId]) => ({
      board_number: Number(board),
      user_id: Number(memberId),
    }));
    try {
      const res = await fetch("/api/teams/roster-configs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ team_id: activeMemberTeam.id, name: rosterConfigName.trim(), slots }),
      });
      const data = await res.json();
      if (!res.ok) { setTeamError(data.error || "Failed to save config"); return; }
      setRosterConfigs((prev) => [data, ...prev]);
      setRosterConfigName("");
      setTeamMessage("Roster configuration saved.");
    } catch { setTeamError("Failed to save roster config"); }
  };

  const handleDeleteRosterConfig = async (configId) => {
    try {
      await fetch(`/api/teams/roster-configs/${configId}`, { method: "DELETE" });
      setRosterConfigs((prev) => prev.filter((c) => c.id !== configId));
    } catch { setTeamError("Failed to delete roster config"); }
  };

  const handleApplyRosterConfig = (config) => {
    const draft = {};
    (config.slots || []).forEach((slot) => {
      draft[slot.board_number] = slot.user_id;
    });
    setBoardAssignmentDraft(draft);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!currentUser) return;

    setMessage("");
    setError("");
    setLoading(true);

    const { response, data } = await postJson("/api/users", {
      action: "updateProfile",
      user_id: currentUser.id,
      country: profileForm.country,
    });

    if (!response.ok) {
      setError(pickErrorMessage(data, "Failed to update profile"));
      setLoading(false);
      return;
    }

    syncCurrentUser(data);
    setMessage("Profile updated.");
    setLoading(false);
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    setTeamMessage("");
    setTeamError("");
    setTeamLoading(true);

    if (!currentUser) {
      setTeamError("Sign in before creating a team.");
      setTeamLoading(false);
      return;
    }

    const selectedAccountIds = getOwnerPlatformAccountIdsForPreference(
      currentUser.linked_accounts,
      teamForm.matchmaking_preference
    );

    if (selectedAccountIds.length === 0) {
      setTeamError(
        teamForm.matchmaking_preference === "allow_all"
          ? "Link both Lichess and Chess.com on your account before setting this team to Allow all."
          : `Link a ${teamForm.matchmaking_preference} account before creating a team on that platform.`
      );
      setTeamLoading(false);
      return;
    }

    const { response, data } = await postJson("/api/teams", {
      action: "create",
      name: teamForm.name,
      owner_id: currentUser.id,
      owner_platform_account_id: selectedAccountIds[0],
      owner_platform_account_ids: selectedAccountIds,
      matchmaking_preference: teamForm.matchmaking_preference,
      bio: teamForm.bio,
      country: teamForm.country,
      badge: teamForm.badge,
      league_registered: teamForm.league_registered,
      competition_mode: teamForm.competition_mode,
      grade_band: teamForm.competition_mode === "scholastic" ? teamForm.grade_band : "",
    });
    if (!response.ok) {
      setTeamError(pickErrorMessage(data, "Failed to create team"));
      setTeamLoading(false);
      return;
    }
      setTeamMessage(`Team "${data.name}" created successfully.`);
      setTeamForm({
        name: "",
        owner_platform_account_ids: [],
        matchmaking_preference: "allow_all",
        bio: "",
        country: "",
        badge: "",
        league_registered: false,
        competition_mode: "open",
        grade_band: "",
      });
    await Promise.all([fetchTeams(), fetchMemberTeams(currentUser.id)]);
    navigateTeamView("my-team");
    setTeamLoading(false);
  };

  const handleJoinTeam = async (team) => {
    setTeamMessage("");
    setTeamError("");
    const matchingAccounts = linkedAccounts.filter((account) =>
      (team.supported_platforms || []).some(
        (platformLink) => platformLink.platform === account.platform
      ) || account.platform === team.platform
    );

    const selectedAccountId = Number(joinPlatformSelections[team.id] || matchingAccounts[0]?.id || 0);
    const matchingAccount = matchingAccounts.find((account) => account.id === selectedAccountId);

    if (!matchingAccount) return setTeamError(`Connect your ${team.platform} account before joining this team.`);
    setJoiningTeamId(team.id);
    const { response, data } = await postJson("/api/teams", {
      action: "join",
      team_id: team.id,
      user_id: currentUser.id,
      platform_account_id: matchingAccount.id,
    });
    if (!response.ok) {
      setTeamError(pickErrorMessage(data, "Failed to join team"));
      setJoiningTeamId(null);
      return;
    }
    setTeamMessage(`You joined "${team.name}" using ${matchingAccount.platform_username}.`);
    await Promise.all([fetchTeams(), fetchMemberTeams(currentUser.id)]);
    await fetchIncomingInvites(currentUser.id);
    navigateTeamView("my-team");
    setJoiningTeamId(null);
  };

  const startEditingTeam = (team) => {
    setEditingTeamId(team.id);
    setEditTeamForm({
      name: team.name,
      owner_platform_account_ids:
        team.supported_platforms?.map((platformLink) => String(platformLink.owner_platform_account_id)) ||
        [team.owner_platform_account_id?.toString()].filter(Boolean),
      matchmaking_preference: team.matchmaking_preference || "allow_all",
      bio: team.bio || "",
      country: team.country || "",
      badge: team.badge || "",
      league_registered: Boolean(team.league_registered),
      competition_mode: team.competition_mode || "open",
      grade_band: team.grade_band || "",
    });
    setTeamMessage("");
    setTeamError("");
  };

  const cancelEditingTeam = () => {
    setEditingTeamId(null);
    setEditTeamForm({
      name: "",
      owner_platform_account_ids: [],
      matchmaking_preference: "allow_all",
      bio: "",
      country: "",
      badge: "",
      league_registered: false,
      competition_mode: "open",
      grade_band: "",
    });
  };

  const handleUpdateTeam = async (teamId) => {
    if (!currentUser) return;
    setTeamMessage("");
    setTeamError("");
    setTeamLoading(true);
    const selectedAccountIds = getOwnerPlatformAccountIdsForPreference(
      currentUser.linked_accounts,
      editTeamForm.matchmaking_preference
    );

    if (selectedAccountIds.length === 0) {
      setTeamError(
        editTeamForm.matchmaking_preference === "allow_all"
          ? "Link both Lichess and Chess.com on your account before setting this team to Allow all."
          : `Link a ${editTeamForm.matchmaking_preference} account before saving this team.`
      );
      setTeamLoading(false);
      return;
    }

    const { response, data } = await postJson("/api/teams", {
      action: "update",
      team_id: teamId,
      owner_id: currentUser.id,
      name: editTeamForm.name,
      owner_platform_account_id: selectedAccountIds[0],
      owner_platform_account_ids: selectedAccountIds,
      matchmaking_preference: editTeamForm.matchmaking_preference,
      bio: editTeamForm.bio,
      country: editTeamForm.country,
      badge: editTeamForm.badge,
      league_registered: editTeamForm.league_registered,
      competition_mode: editTeamForm.competition_mode,
      grade_band: editTeamForm.competition_mode === "scholastic" ? editTeamForm.grade_band : "",
    });
    if (!response.ok) {
      setTeamError(pickErrorMessage(data, "Failed to update team"));
      setTeamLoading(false);
      return;
    }
    setTeamMessage(`Team updated to "${data.name}".`);
    cancelEditingTeam();
    await Promise.all([fetchTeams(), fetchMemberTeams(currentUser.id)]);
    setTeamLoading(false);
  };

  const renderTeamEditPanel = (teamId) => (
    <div className="teamEditPanel">
      <div className="editorIntro">
        <span className="editorBadge">Owner Controls</span>
        <p className="compactNote">
          Set the room identity and decide whether this team should answer Lichess only, Chess.com only, or the full mixed pool.
        </p>
      </div>
      <input type="text" name="name" placeholder="Team Name" value={editTeamForm.name} onChange={handleEditTeamChange} />
      <div className="teamIdentityGrid">
        <input type="text" name="country" list="country-options" placeholder="Country" value={editTeamForm.country} onChange={handleEditTeamChange} />
        <div className="badgePickerCard">
          <span className="badgePickerLabel">Team Badge</span>
          {renderBadgePicker(editTeamForm.badge, (badge) => setEditTeamForm((prev) => ({ ...prev, badge })))}
        </div>
      </div>
      <textarea
        name="bio"
        placeholder="Team bio"
        value={editTeamForm.bio}
        onChange={handleEditTeamChange}
        rows={4}
      />
      <div className="fieldBlock">
        <div className="fieldBlockHeader">
          <strong>Matchmaking pool</strong>
          <span>The site will use each member's linked platform account to build pairings and match links board by board.</span>
        </div>
        {renderMatchmakingPreferenceControl(
          editTeamForm.matchmaking_preference,
          linkedAccounts.map((account) => String(account.id)),
          (nextPreference) => setEditTeamForm((prev) => ({ ...prev, matchmaking_preference: nextPreference })),
          "compact"
        )}
      </div>
      <p className="teamEditHint">
        Lichess keeps every board on Lichess. Chess.com keeps every board on Chess.com. Allow all lets the desk check which platform each player has linked and assign boards from there.
      </p>
      <div className="leagueFormGrid teamModeGrid">
        <label>
          <span>Team Mode</span>
          <select
            name="competition_mode"
            value={editTeamForm.competition_mode}
            onChange={(event) =>
              setEditTeamForm((prev) => ({
                ...prev,
                competition_mode: event.target.value,
                grade_band: event.target.value === "scholastic" ? prev.grade_band : "",
              }))
            }
          >
            {LEAGUE_MODE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        {editTeamForm.competition_mode === "scholastic" && (
          <label>
            <span>Grade Band</span>
            <select name="grade_band" value={editTeamForm.grade_band} onChange={handleEditTeamChange}>
              <option value="">Select grade band</option>
              {GRADE_BAND_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </label>
        )}
      </div>
      <label className="checkboxRow">
        <input type="checkbox" name="league_registered" checked={editTeamForm.league_registered} onChange={handleEditTeamChange} />
        <span>Registered to compete in leagues</span>
      </label>
      <div className="teamCardActions">
        <button className="primaryButton inlineActionButton" type="button" disabled={teamLoading} onClick={() => handleUpdateTeam(teamId)}>{teamLoading ? "Saving..." : "Save"}</button>
        <button className="secondaryButton inlineActionButton" type="button" disabled={teamLoading} onClick={cancelEditingTeam}>Cancel</button>
      </div>
    </div>
  );

  const buildInviteShareUrl = (inviteToken) => {
    const url = new URL(window.location.href);
    url.searchParams.set("invite", inviteToken);
    url.hash = "teams";
    return url.toString();
  };

  const clearInviteTokenFromUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("invite");
    window.history.replaceState({}, "", url.toString());
    setActiveInviteToken("");
    setInvitePreview(null);
  };

  const handleInviteDraftChange = (teamId, value) => {
    setInviteDrafts((prev) => ({ ...prev, [teamId]: value }));
  };

  const handleCreateInvite = async (team) => {
    const inviteTarget = String(inviteDrafts[team.id] || "").trim();

    if (!currentUser) {
      setTeamError("Sign in before sending team invites.");
      return;
    }

    if (!inviteTarget) {
      setTeamError("Enter a username or email address to send an invite.");
      return;
    }

    setTeamMessage("");
    setTeamError("");
    setTeamLoading(true);

    const { response, data } = await postJson("/api/teams", {
      action: "create_invite",
      team_id: team.id,
      owner_id: currentUser.id,
      invite_target: inviteTarget,
    });

    if (!response.ok) {
      setTeamError(pickErrorMessage(data, "Failed to send team invite"));
      setTeamLoading(false);
      return;
    }

    const shareUrl = buildInviteShareUrl(data.invite_token);

    setInviteDrafts((prev) => ({ ...prev, [team.id]: "" }));
    setTeamMessage(
      data.recipient_username
        ? `Invite sent! ${data.recipient_username} has 24 hours to accept or decline it.`
        : `Invite sent! Share this link if they are not on the site yet: ${shareUrl}`
    );
    setTeamLoading(false);
  };

  const getInviteMatchingAccounts = (invite) => {
    const supportedPlatforms = (invite.supported_platforms || []).map((platformLink) => platformLink.platform);
    const normalizedPlatforms = supportedPlatforms.length > 0 ? supportedPlatforms : [invite.team_platform].filter(Boolean);
    return linkedAccounts.filter((account) => normalizedPlatforms.includes(account.platform));
  };

  const completeInviteAcceptance = async (invite, selectedAccountId, extraBody = {}) => {
    setTeamMessage("");
    setTeamError("");
    setAcceptingInviteId(invite.id);

    const { response, data } = await postJson("/api/teams", {
      action: "accept_invite",
      invite_token: invite.invite_token,
      user_id: currentUser.id,
      platform_account_id: selectedAccountId,
      ...extraBody,
    });

    if (!response.ok) {
      setTeamError(pickErrorMessage(data, "Failed to accept invite"));
      setAcceptingInviteId(null);
      return;
    }

    let nextMessage = `You joined ${invite.team_name}.`;
    if (data.current_team_action === "disbanded" && data.previous_team_name) {
      nextMessage = `${data.previous_team_name} was disbanded and you joined ${invite.team_name}.`;
    } else if (data.current_team_action === "transferred" && data.previous_team_name && data.new_owner_username) {
      nextMessage = `${data.previous_team_name} was passed to ${data.new_owner_username}, and you joined ${invite.team_name}.`;
    } else if (data.current_team_action === "left" && data.previous_team_name) {
      nextMessage = `You left ${data.previous_team_name} and joined ${invite.team_name}.`;
    }

    setTeamMessage(nextMessage);
    await Promise.all([
      fetchTeams(),
      fetchMemberTeams(currentUser.id),
      fetchIncomingInvites(currentUser.id),
    ]);

    if (invitePreview?.invite_token === invite.invite_token) {
      clearInviteTokenFromUrl();
    }

    setTeamExitPrompt(null);
    setAcceptingInviteId(null);
      navigateTeamView("my-team");
  };

  const handleAcceptInvite = async (invite) => {
    if (!currentUser) {
      setTeamError("Sign in before accepting a team invite.");
      return;
    }

    const matchingAccounts = getInviteMatchingAccounts(invite);
    const selectedAccountId = Number(invitePlatformSelections[invite.id] || matchingAccounts[0]?.id || 0);

    if (!selectedAccountId) {
      setTeamError("Connect a supported platform account before accepting this invite.");
      return;
    }

    if (activeMemberTeam && activeMemberTeam.id !== invite.team_id) {
      if (activeTeamIsOwnerControlled) {
        if (activeMemberCount <= 1) {
          const confirmed = window.confirm(
            `Warning, accepting this invite will disband your current team "${activeMemberTeam.name}". Continue?`
          );
          if (!confirmed) return;

          await completeInviteAcceptance(invite, selectedAccountId, {
            disband_current_team: true,
          });
          return;
        }

        const transferCandidates =
          activeTeamTransferCandidates.length > 0
            ? activeTeamTransferCandidates
            : (await fetchTeamMembers(activeMemberTeam.id)).filter((member) => member.user_id !== currentUser.id);

        if (transferCandidates.length === 0) {
          setTeamError("Load your current roster before passing team ownership.");
          return;
        }

        setTeamExitPrompt({
          mode: "invite",
          team: activeMemberTeam,
          invite,
          selectedAccountId,
          transferTeamOwnerId: String(transferCandidates[0]?.user_id || ""),
          members: transferCandidates,
        });
        return;
      }

      const confirmed = window.confirm(
        `Accepting this invite will remove you from ${activeMemberTeam.name} and join ${invite.team_name}. Continue?`
      );
      if (!confirmed) return;
    }

    await completeInviteAcceptance(invite, selectedAccountId);
  };

  const handleDeclineInvite = async (invite) => {
    if (!currentUser) {
      setTeamError("Sign in before declining a team invite.");
      return;
    }

    setTeamMessage("");
    setTeamError("");
    setAcceptingInviteId(invite.id);

    const { response, data } = await postJson("/api/teams", {
      action: "decline_invite",
      invite_token: invite.invite_token,
      user_id: currentUser.id,
    });

    if (!response.ok) {
      setTeamError(pickErrorMessage(data, "Failed to decline invite"));
      setAcceptingInviteId(null);
      return;
    }

    setTeamMessage(`Invite declined for ${invite.team_name}.`);
    await fetchIncomingInvites(currentUser.id);

    if (invitePreview?.invite_token === invite.invite_token) {
      clearInviteTokenFromUrl();
    }

    setAcceptingInviteId(null);
  };

  const completeLeaveTeam = async (team, extraBody = {}) => {
    if (!currentUser) return;
    setTeamMessage("");
    setTeamError("");
    setTeamLoading(true);

    const { response, data } = await postJson("/api/teams", {
      action: "leave",
      team_id: team.id,
      user_id: currentUser.id,
      ...extraBody,
    });

    if (!response.ok) {
      setTeamError(pickErrorMessage(data, "Failed to leave team"));
      setTeamLoading(false);
      return;
    }

    await Promise.all([
      fetchTeams(),
      fetchMemberTeams(currentUser.id),
      fetchIncomingInvites(currentUser.id),
    ]);
    await fetchTeamMembers(null);
    if (data.current_team_action === "disbanded") {
      setTeamMessage(`${team.name} was disbanded.`);
    } else if (data.current_team_action === "transferred" && data.new_owner_username) {
      setTeamMessage(`You passed ${team.name} to ${data.new_owner_username} and left the team.`);
    } else {
      setTeamMessage(`You left ${team.name}.`);
    }
    setTeamExitPrompt(null);
      navigateTeamView("discover");
    setTeamLoading(false);
  };

  const handleLeaveTeam = async (team) => {
    if (!currentUser) return;

    if (team.owner_id === currentUser.id) {
      if (activeMemberCount <= 1) {
        const confirmed = window.confirm(
          `Warning, leaving ${team.name} will disband the team. Continue?`
        );
        if (!confirmed) return;

        await completeLeaveTeam(team, { disband_current_team: true });
        return;
      }

      const transferCandidates =
        activeTeamTransferCandidates.length > 0
          ? activeTeamTransferCandidates
          : (await fetchTeamMembers(team.id)).filter((member) => member.user_id !== currentUser.id);

      if (transferCandidates.length === 0) {
        setTeamError("Load your current roster before passing team ownership.");
        return;
      }

      setTeamExitPrompt({
        mode: "leave",
        team,
        transferTeamOwnerId: String(transferCandidates[0]?.user_id || ""),
        members: transferCandidates,
      });
      return;
    }

    const confirmed = window.confirm(`Leave ${team.name}?`);
    if (!confirmed) return;
    await completeLeaveTeam(team);
  };

  const handleConfirmTeamExitPrompt = async () => {
    if (!teamExitPrompt) return;

    if (!teamExitPrompt.transferTeamOwnerId) {
      setTeamError("Choose another member to become the new owner first.");
      return;
    }

    if (teamExitPrompt.mode === "invite") {
      await completeInviteAcceptance(teamExitPrompt.invite, teamExitPrompt.selectedAccountId, {
        transfer_team_owner_id: Number(teamExitPrompt.transferTeamOwnerId),
      });
      return;
    }

    await completeLeaveTeam(teamExitPrompt.team, {
      transfer_team_owner_id: Number(teamExitPrompt.transferTeamOwnerId),
    });
  };

  const handleCreateMatch = async (e) => {
    e.preventDefault();
    setMatchMessage("");
    setMatchError("");
    setMatchLoading(true);
    if (!currentUser) {
      setMatchError("Sign in before creating a match.");
      setMatchLoading(false);
      return;
    }
    if (!selectedTeamA) {
      setMatchError("Choose which of your teams is sending the challenge.");
      setMatchLoading(false);
      return;
    }
    if (!selectedTeamB) {
      setMatchError(
        matchComposerMode === "live"
          ? "No live matchmaking opponent is available for this team yet."
          : "Search for a team to challenge before sending the match request."
      );
      setMatchLoading(false);
      return;
    }
    if (chessComRatedDraft) {
      setMatchError("Create Chess.com matches as casual for now. Rated Chess.com live games do not reliably preserve the assigned White and Black board seats.");
      setMatchLoading(false);
      return;
    }
    if (!pendingMatchPlatform) {
      setMatchError("Select a compatible platform before sending the match request.");
      setMatchLoading(false);
      return;
    }
    if (!matchForm.time_control) {
      setMatchError("Select a time control before sending the match request.");
      setMatchLoading(false);
      return;
    }
    if (!matchForm.match_duration_days) {
      setMatchError("Select a match duration before sending the match request.");
      setMatchLoading(false);
      return;
    }
    const { response, data } = await postJson("/api/matches", {
      team_a_id: Number(selectedTeamA.id),
      team_b_id: Number(selectedTeamB.id),
      platform: pendingMatchPlatform,
      time_control: matchForm.time_control,
      match_duration_days: Number(matchForm.match_duration_days),
      rated: matchForm.rated,
      requested_by_user_id: currentUser.id,
    });
    if (!response.ok) {
      setMatchError(pickErrorMessage(data, "Failed to create match"));
      setMatchLoading(false);
      return;
    }
    setSelectedMatch(data);
    hydrateBoardDrafts(data);
    setActiveMatchSettingPanel(null);
    setMatchOpponentQuery("");
    setMatchForm((prev) => ({ ...prev, team_b_id: "", platform: "" }));
    setMatchComposerMode("search");
    setMatchMessage(
      data.status === "pending_acceptance"
        ? `Match request sent: ${data.team_a_name} vs ${data.team_b_name}. Waiting on the other owner to accept before the ${data.match_duration_days}-day window begins.`
        : `Match created on ${data.platform}: ${data.team_a_name} vs ${data.team_b_name}.`
    );
    await fetchMatches();
    setMatchLoading(false);
  };

  const handleRespondToMatchRequest = async (accept) => {
    if (!currentUser || !selectedMatch) return;
    setMatchMessage("");
    setMatchError("");
    setMatchLoading(true);
    const { response, data } = await postJson("/api/matches", {
      action: "respond_to_match_request",
      match_id: selectedMatch.id,
      user_id: currentUser.id,
      accept,
    });
    if (!response.ok) {
      setMatchError(pickErrorMessage(data, "Failed to update match request"));
      setMatchLoading(false);
      return;
    }
    setSelectedMatch(data);
    hydrateBoardDrafts(data);
    await fetchMatches();
    setMatchMessage(
      accept
        ? data.status === "scheduled"
          ? `Match accepted. Both owners are in, and the boards are live until ${new Date(data.deadline_at).toLocaleString()}.`
          : "Your team has accepted the match request."
        : "Match request declined."
    );
    setMatchLoading(false);
  };

  const handleBoardDraftChange = (boardId, field, value) =>
    setBoardDrafts((prev) => ({ ...prev, [boardId]: { ...prev[boardId], [field]: value } }));

  const getBoardRoleForCurrentUser = (board) => {
    if (!currentUser) return null;
    if (board.white_user_id === currentUser.id) return "white";
    if (board.black_user_id === currentUser.id) return "black";
    return null;
  };

  const handleSetBoardReady = async (boardId, ready) => {
    if (!currentUser) return;
    setMatchMessage("");
    setMatchError("");
    setMatchLoading(true);
    const { response, data } = await postJson("/api/matches", {
      action: "set_board_ready",
      board_pairing_id: boardId,
      user_id: currentUser.id,
      ready,
    });
    if (!response.ok) {
      setMatchError(pickErrorMessage(data, "Failed to update ready status"));
      setMatchLoading(false);
      return;
    }
    setSelectedMatch(data);
    hydrateBoardDrafts(data);
    await fetchMatches();
    setMatchMessage(ready ? "You are marked ready for this board." : "You are no longer marked ready for this board.");
    setMatchLoading(false);
  };

  const handleSubmitBoard = async (boardId) => {
    setMatchLoading(true);
    const draft = boardDrafts[boardId] || {};
    const { response, data } = await postJson("/api/matches", {
      action: "update_board",
      board_pairing_id: boardId,
      game_link: draft.game_link || null,
      result: draft.result || null,
      notes: draft.notes || null,
    });
    if (!response.ok) {
      setMatchError(pickErrorMessage(data, "Failed to update board"));
      setMatchLoading(false);
      return;
    }
    setSelectedMatch(data);
    hydrateBoardDrafts(data);
    await fetchMatches();
    setMatchLoading(false);
  };

  const handleLaunchLichessChallenge = async (boardId) => {
    setMatchMessage("");
    setMatchError("");
    setMatchLoading(true);
    try {
      const { response, data } = await postJson("/api/matches", { action: "launch_lichess_challenge", board_pairing_id: boardId });
      if (!response.ok) {
        setMatchError(pickErrorMessage(data, "Failed to launch Lichess challenge"));
        setMatchLoading(false);
        return;
      }
      setSelectedMatch(data);
      hydrateBoardDrafts(data);
      await fetchMatches();
      setMatchMessage("Lichess challenge created for this board.");
    } catch (err) {
      setMatchError(err.message || "Failed to launch Lichess challenge");
    }
    setMatchLoading(false);
  };

  const handlePrepareChessComChallenge = async (boardId) => {
    setMatchMessage("");
    setMatchError("");
    setMatchLoading(true);
    try {
      const { response, data } = await postJson("/api/matches", {
        action: "prepare_chesscom_challenge",
        board_pairing_id: boardId,
      });
      if (!response.ok) {
        setMatchError(pickErrorMessage(data, "Failed to prepare Chess.com challenge"));
        setMatchLoading(false);
        return;
      }
      setSelectedMatch(data);
      hydrateBoardDrafts(data);
      await fetchMatches();
      setMatchMessage("Chess.com setup is ready for this board.");
    } catch (err) {
      setMatchError(err.message || "Failed to prepare Chess.com challenge");
    }
    setMatchLoading(false);
  };

  const handleSyncLichessResult = async (boardId) => {
    setMatchMessage("");
    setMatchError("");
    setMatchLoading(true);
    const { response, data } = await postJson("/api/matches", { action: "sync_lichess_result", board_pairing_id: boardId });
    if (!response.ok) {
      setMatchError(pickErrorMessage(data, "Failed to sync Lichess result"));
      setMatchLoading(false);
      return;
    }
    setSelectedMatch(data);
    hydrateBoardDrafts(data);
    await fetchMatches();
    setMatchMessage("Lichess game synced.");
    setMatchLoading(false);
  };

  const handleDeleteSelectedMatch = async () => {
    if (!selectedMatch) return;
    setMatchMessage("");
    setMatchError("");
    setMatchLoading(true);
    const { response, data } = await postJson("/api/matches", { action: "delete_match", match_id: selectedMatch.id, user_id: currentUser?.id });
    if (!response.ok) {
      setMatchError(pickErrorMessage(data, "Failed to delete match"));
      setMatchLoading(false);
      return;
    }
    setSelectedMatch(null);
    setBoardDrafts({});
    await fetchMatches();
    setMatchMessage("Match deleted.");
    setMatchLoading(false);
  };

  const handleLoadTeamsCenter = async () => {
    setTeamLoading(true);
    await fetchTeams();
    await fetchPlayerDirectory();
    if (currentUser) {
      await Promise.all([
        fetchMemberTeams(currentUser.id),
        fetchIncomingInvites(currentUser.id),
      ]);
    }
    setTeamLoading(false);
  };

  const handleLoadLeaguesCenter = async () => {
    setLeagueLoading(true);
    await fetchTeams();
    await fetchMatches();
    await fetchLeagues();
    if (activeMemberTeamIdForRefresh) {
      await fetchTeamLeagueParticipation(activeMemberTeamIdForRefresh);
    }
    if (selectedLeagueIdForRefresh) {
      await fetchLeagueDetail(selectedLeagueIdForRefresh);
    }
    setLeagueLoading(false);
  };

  function handleNavigate(pageId, subpage = null) {
    const nextRoute = parseRouteFromHash(buildHashRoute(pageId, subpage, { signedIn: Boolean(currentUser) }), {
      signedIn: Boolean(currentUser),
    });
    if (currentPage === nextRoute.page && currentSubpage === nextRoute.subpage) {
      return;
    }
    if (window.location.hash !== nextRoute.hash) {
      window.location.hash = nextRoute.hash;
    }
    setCurrentPage(nextRoute.page);
    setCurrentSubpage(nextRoute.subpage);
  }

  function navigateTeamView(viewId) {
    handleNavigate("teams", viewId);
  }

  function navigatePlayView(viewId) {
    setShowMatchComposer(viewId === "create");
    handleNavigate("play", viewId);
  }

  const buildChessComSetupText = (board) => {
    const setupItems = board.instructions?.setup_items || [];
    const lines = [
      `Board ${board.board_number} on Chess.com`,
      `${board.white_platform_username} should challenge ${board.black_platform_username}.`,
      ...setupItems,
      "After the game starts, paste the live game link back into the board record.",
    ];
    return lines.join("\n");
  };

  const handleCopyChessComSetup = async (board) => {
    setMatchMessage("");
    setMatchError("");
    try {
      await navigator.clipboard.writeText(buildChessComSetupText(board));
      setMatchMessage("Chess.com setup copied.");
    } catch (err) {
      setMatchError("Could not copy the Chess.com setup from this browser.");
    }
  };

  const renderMetricStrip = (items) => (
    <div className="dashboardStrip dashboardStripUnified">
      {items.map((item) => (
        <StatCard
          key={item.label}
          label={item.label}
          value={item.value}
          note={item.note}
          tone={item.tone || "default"}
        />
      ))}
    </div>
  );

  const renderInfoBlocks = (items) => (
    <div className="infoList">
      {items.map((item) => <div key={item}>{item}</div>)}
    </div>
  );

  const formatMatchMeta = (match) => `${match.platform} | ${match.time_control} | ${match.rated ? "rated" : "casual"}`;
  const formatMatchDeadline = (match) => {
    if (!match?.deadline_at) return "No deadline set";
    return `Closes ${new Date(match.deadline_at).toLocaleString()}`;
  };
  const formatBoardState = (board) => board.challenge_status ? board.challenge_status.replace(/_/g, " ") : board.result ? `result recorded: ${board.result}` : "waiting";
  const renderPresencePill = (isOnline) => (
    <span className={`presencePill ${isOnline ? "isOnline" : "isOffline"}`}>
      <span className="presenceDot" />
      {formatPresenceLabel(isOnline)}
    </span>
  );

  const accountMetrics = [
    { label: "Linked Handles", value: linkedAccounts.length, note: "Platform identities connected to this account" },
    { label: "Launch Ready", value: connectedLaunchCount, note: "Connected for Lichess challenge creation" },
    { label: "Available Links", value: availablePlatformsToLink.length, note: "Remaining platforms you can add" },
  ];
  const standingTeams = [...teams]
    .sort((a, b) => (b.competitive_rating || 1500) - (a.competitive_rating || 1500) || (b.team_rating || 0) - (a.team_rating || 0) || a.name.localeCompare(b.name))
    .slice(0, 10);
  const teamMetrics = [
    { label: "Teams", value: teams.length, note: `${teams.filter((team) => team.team_rating).length} with a live average Elo` },
    { label: "Top Team", value: standingTeams[0]?.name || "Open Field", note: `site ${standingTeams[0]?.competitive_rating || 1500}${standingTeams[0]?.team_rating ? ` | avg ${standingTeams[0].team_rating}` : ""}` },
    { label: "Inbox", value: incomingInvites.filter((invite) => invite.status === "pending").length, note: currentUser ? "Pending invites waiting on your answer" : "Sign in to view your invites" },
  ];
  const normalizedTeamSearch = teamSearch.trim().toLowerCase();
  const visibleTeams = teams.filter((team) => {
    if (!normalizedTeamSearch) return true;

    const platformText = (team.supported_platforms || [])
      .map((platformLink) => `${platformLink.platform} ${platformLink.platform_username}`)
      .join(" ")
      .toLowerCase();

    return (
      team.name?.toLowerCase().includes(normalizedTeamSearch) ||
      team.owner_platform_username?.toLowerCase().includes(normalizedTeamSearch) ||
      platformText.includes(normalizedTeamSearch)
    );
  });
  const visiblePlayers = playerDirectory.filter((player) => {
    if (!normalizedTeamSearch) return true;

    const platformText = (player.supported_platforms || [])
      .map((platformLink) => `${platformLink.platform} ${platformLink.platform_username}`)
      .join(" ")
      .toLowerCase();

    return (
      player.username?.toLowerCase().includes(normalizedTeamSearch) ||
      player.team_name?.toLowerCase().includes(normalizedTeamSearch) ||
      player.platform?.toLowerCase().includes(normalizedTeamSearch) ||
      player.platform_username?.toLowerCase().includes(normalizedTeamSearch) ||
      platformText.includes(normalizedTeamSearch)
    );
  });
  const getRecommendedTeamScore = (team) => {
    const teamPlatforms = getSupportedPlatformIds(team);
    const matchingAccountCount = linkedAccounts.filter((account) => teamPlatforms.includes(account.platform)).length;
    const countryMatch = normalizedUserCountry && normalizeCountry(team.country) === normalizedUserCountry ? 8 : 0;
    const leagueBonus = team.league_registered ? 1 : 0;
    const ownerBonus = team.owner_id === currentUser?.id ? -100 : 0;
    return countryMatch + (matchingAccountCount * 3) + leagueBonus + ownerBonus;
  };
  const rankedDiscoverTeams = [...visibleTeams].sort((a, b) => (
    getRecommendedTeamScore(b) - getRecommendedTeamScore(a)
    || (b.competitive_rating || 1500) - (a.competitive_rating || 1500)
    || (b.team_rating || 0) - (a.team_rating || 0)
    || a.name.localeCompare(b.name)
  ));
  const recommendedTeams = normalizedTeamSearch
    ? rankedDiscoverTeams
    : rankedDiscoverTeams.filter((team) => getRecommendedTeamScore(team) > 0).slice(0, 6);
  const recommendedTeamIds = new Set(recommendedTeams.map((team) => team.id));
  const discoverTeamsFallback = normalizedTeamSearch
    ? []
    : rankedDiscoverTeams.filter((team) => !recommendedTeamIds.has(team.id));
  const visibleFallbackTeams = showAllDiscoverTeams ? discoverTeamsFallback : discoverTeamsFallback.slice(0, 6);
  const myTeams = ownerManagedTeams;
  const previewInviteMatchingAccounts = invitePreview ? getInviteMatchingAccounts(invitePreview) : [];
  const activeMemberTeam = memberTeams[0] || null;
  const activeMemberCount = activeTeamMembers.length || activeMemberTeam?.member_count || 0;
  const activeTeamTransferCandidates = activeTeamMembers.filter((member) => member.user_id !== currentUser?.id);
  const canManageMembership = Boolean(currentUser && memberTeams.length === 0);
  const activeTeamIsOwnerControlled = Boolean(activeMemberTeam && activeMemberTeam.owner_id === currentUser?.id);
  const defaultTeamView = currentUser ? "my-team" : "overview";
  const requestedTeamView = currentPage === "teams" ? currentSubpage || defaultTeamView : defaultTeamView;
  const resolvedTeamView = (() => {
    const allowedViews = new Set(
      currentUser
        ? ["my-team", "discover", "players", "invites", "table", "team-page"]
        : ["overview", "discover", "players", "table"]
    );
    let nextView = requestedTeamView;
    if (nextView === "team-page" && !selectedTeamProfile?.team) {
      nextView = currentUser ? "my-team" : "discover";
    }
    if (!allowedViews.has(nextView)) {
      nextView = defaultTeamView;
    }
    return nextView;
  })();
  const pendingInviteCount = incomingInvites.filter((invite) => invite.status === "pending").length;
  const ownedTeamIds = new Set(myTeams.map((team) => team.id));
  const selectedLeagueAdmins = selectedLeague?.admins || [];
  const selectedLeagueRegistrations = selectedLeague?.registrations || [];
  const selectedLeagueRounds = selectedLeague?.rounds || [];
  const selectedLeagueMatches = selectedLeague?.matches || [];
  const leagueSubpage = currentPage === "leagues" && ["overview", "edit", "rules"].includes(currentSubpage)
    ? currentSubpage
    : "overview";
  const selectedLeagueAdminIds = new Set(selectedLeagueAdmins.map((admin) => admin.user_id));
  const canManageSelectedLeague = Boolean(currentUser && selectedLeagueAdminIds.has(currentUser.id));
  const registeredLeagueRegistrations = selectedLeagueRegistrations.filter((registration) => registration.registration_status !== "rejected");
  const approvedLeagueRegistrations = selectedLeagueRegistrations.filter((registration) => registration.registration_status === "approved");
  const availableOwnedTeamsForSelectedLeague = selectedLeague
    ? ownerManagedTeams.filter((team) => !registeredLeagueRegistrations.some((registration) => registration.team_id === team.id))
    : [];
  const selectedLeagueParticipationEntry = leagueTeamParticipation.find((entry) => Number(entry.league_id) === Number(selectedLeague?.id || 0)) || null;
  const ownerApprovalCount = matches.filter((match) =>
    match.status === "pending_acceptance" &&
    ((match.team_a_owner_id === currentUser?.id && !match.team_a_accepted) ||
      (match.team_b_owner_id === currentUser?.id && !match.team_b_accepted) ||
      ownedTeamIds.has(match.team_a_id) ||
      ownedTeamIds.has(match.team_b_id))
  ).length;
  const recommendedHeading = normalizedUserCountry
    ? `Recommended near ${currentUser.country}`
    : "Recommended teams";
  const activeRecommendedTeam =
    recommendedTeams.length > 0 ? recommendedTeams[recommendedTeamIndex % recommendedTeams.length] : null;
  useEffect(() => {
    if (recommendedTeams.length === 0) {
      setRecommendedTeamIndex(0);
      return;
    }

    if (recommendedTeamIndex >= recommendedTeams.length) {
      setRecommendedTeamIndex(0);
    }
  }, [recommendedTeamIndex, recommendedTeams.length]);

  useEffect(() => {
    if (currentPage !== "teams" || normalizedTeamSearch || recommendedTeams.length <= 1) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setRecommendedTeamIndex((current) => (current + 1) % recommendedTeams.length);
    }, 5500);

    return () => window.clearInterval(intervalId);
  }, [currentPage, normalizedTeamSearch, recommendedTeams.length]);

  const renderHomeField = (label, field, helper = "") => (
    <label className="homeAuthField">
      <span className="homeAuthLabel">{label}</span>
      {field}
      {helper ? <small className="homeAuthHelper">{helper}</small> : null}
    </label>
  );

  const renderHomeAuthCard = () => (
    <div className="surfaceCard homeFigmaPanel authCommandCard">
      <div className="homePanelHeader">
        <div className="homePanelIcon">{"\u26E8"}</div>
        <div>
          <h2>{homeAuthMode === "signup" ? "Create Account" : "Welcome Back"}</h2>
          <p className="compactNote">
            {homeAuthMode === "signup"
              ? "Join Global Chess Arena and launch your first team room."
              : "Sign in to your command center and pick up where match night left off."}
          </p>
        </div>
      </div>
      <div className="authModeToggle">
        <button
          type="button"
          className={`authModeToggleButton ${homeAuthMode === "signup" ? "isActive" : ""}`}
          onClick={() => setHomeAuthMode("signup")}
        >
          Create Account
        </button>
        <button
          type="button"
          className={`authModeToggleButton ${homeAuthMode === "login" ? "isActive" : ""}`}
          onClick={() => setHomeAuthMode("login")}
        >
          Log In
        </button>
      </div>
      {homeAuthMode === "signup" ? (
        <form className="signupForm homeAuthForm" onSubmit={handleSignup}>
          {renderHomeField(
            "Username",
            <input type="text" name="username" placeholder="Choose a username" value={signupForm.username} onChange={handleSignupChange} required />
          )}
          {renderHomeField(
            "Email",
            <input type="email" name="email" placeholder="Enter your email" value={signupForm.email} onChange={handleSignupChange} required />
          )}
          {renderHomeField(
            "Password",
            <input type="password" name="password" placeholder="Create a password" value={signupForm.password} onChange={handleSignupChange} required />
          )}
          {renderHomeField(
            "Country",
            <input type="text" name="country" list="country-options" placeholder="Start typing your country" value={signupForm.country} onChange={handleSignupChange} required />
          )}
          <div className="homeAuthDivider">
            <span>Platform Accounts</span>
            <small>Optional</small>
          </div>
          {renderHomeField(
            "Lichess Username",
            <input type="text" name="lichessUsername" placeholder="Link your Lichess handle" value={signupForm.lichessUsername} onChange={handleSignupChange} />,
            "Add this now or connect it later in Account Desk."
          )}
          {renderHomeField(
            "Chess.com Username",
            <input type="text" name="chessComUsername" placeholder="Link your Chess.com handle" value={signupForm.chessComUsername} onChange={handleSignupChange} />
          )}
          <button className="primaryButton signupButton" type="submit" disabled={loading}>
            {loading ? "Creating Account..." : "Create Account"}
          </button>
        </form>
      ) : (
        <form className="signupForm homeAuthForm" onSubmit={handleLogin}>
          {renderHomeField(
            "Username or Email",
            <input type="text" name="identifier" placeholder="Username or email" value={loginForm.identifier} onChange={handleLoginChange} required />
          )}
          {renderHomeField(
            "Password",
            <input type="password" name="password" placeholder="Enter your password" value={loginForm.password} onChange={handleLoginChange} required />
          )}
          <button className="primaryButton signupButton" type="submit" disabled={loading}>
            {loading ? "Signing In..." : "Log In"}
          </button>
        </form>
      )}
      <button
        type="button"
        className="homeAuthSwitch"
        onClick={() => setHomeAuthMode(homeAuthMode === "signup" ? "login" : "signup")}
      >
        {homeAuthMode === "signup" ? "Already have an account? Sign in" : "Need an account? Create one"}
      </button>
      {message && !currentUser && <p className="successMessage">{message}</p>}
      {error && !currentUser && <p className="errorMessage">{error}</p>}
    </div>
  );

  const renderHomeSection = () => {
    if (!currentUser) {
      return (
        <HomePage
          badge="Global Chess Arena"
          titleLead="Team Chess,"
          titleAccent="Built to Compete."
          description="Create a roster, enter leagues, schedule matches across Lichess and Chess.com, and track every result in one place."
          stats={[
            { label: "Active Players", value: `${playerDirectory.length || 0}+`, tone: "blue" },
            { label: "Teams", value: `${teams.length || 0}+`, tone: "gold" },
            { label: "Live Matches", value: liveMatches ? `${liveMatches}` : "24/7", tone: "green" },
          ]}
          primaryActionLabel="Start Competing"
          secondaryActionLabel="Browse Leagues"
          onPrimaryAction={() => {
            setHomeAuthMode("signup");
            handleNavigate("account");
          }}
          onSecondaryAction={() => handleNavigate("leagues")}
        />
      );
    }

    const myTeamIds = Array.from(
      new Set([...ownerManagedTeams, ...memberTeams].map((team) => Number(team.id)).filter(Boolean))
    );
    const relevantMatches = matches.filter((match) =>
      myTeamIds.includes(Number(match.team_a_id)) || myTeamIds.includes(Number(match.team_b_id))
    );
    const completedRelevantMatches = relevantMatches.filter((match) => match.status === "completed");
    const completedWins = completedRelevantMatches.filter((match) => myTeamIds.includes(Number(match.winner_team_id))).length;
    const winRateValue = completedRelevantMatches.length
      ? `${Math.round((completedWins / completedRelevantMatches.length) * 100)}%`
      : "0%";
    const rankedTeams = [...teams].sort(
      (a, b) =>
        Number(b.team_rating || b.competitive_rating || 0) - Number(a.team_rating || a.competitive_rating || 0) ||
        String(a.name || "").localeCompare(String(b.name || ""))
    );
    const activeTeamRank = activeMemberTeam
      ? rankedTeams.findIndex((team) => Number(team.id) === Number(activeMemberTeam.id)) + 1
      : 0;
    const highlightedLeagues = leagues
      .filter((league) => isLeagueOpenOrActive(league))
      .sort(compareLeagueDeskPriority)
      .slice(0, 3);
    const activeTeamMatches = activeMemberTeam
      ? matches.filter(
          (match) =>
            Number(match.team_a_id) === Number(activeMemberTeam.id) ||
            Number(match.team_b_id) === Number(activeMemberTeam.id)
        )
      : [];
    const activeTeamCompleted = activeTeamMatches.filter((match) => match.status === "completed");
    const activeTeamWins = activeTeamCompleted.filter((match) => Number(match.winner_team_id) === Number(activeMemberTeam?.id)).length;
    const activeTeamLosses = activeTeamCompleted.filter(
      (match) => match.winner_team_id && Number(match.winner_team_id) !== Number(activeMemberTeam?.id)
    ).length;
    const dashboardNotifications = [];

    if (incomingInvites.length > 0) {
      dashboardNotifications.push({
        label: "Invites",
        value: `${incomingInvites.length} team invite${incomingInvites.length === 1 ? "" : "s"} waiting`,
      });
    }

    if (ownerApprovalCount > 0) {
      dashboardNotifications.push({
        label: "Captain actions",
        value: `${ownerApprovalCount} approval${ownerApprovalCount === 1 ? "" : "s"} need attention`,
      });
    }

    if (!dashboardNotifications.length) {
      dashboardNotifications.push({
        label: "Desk status",
        value: "No urgent captain actions right now",
      });
    }

    return (
      <DashboardPage
        username={currentUser.username}
        description="Your matches, standings, and team activity at a glance."
        stats={[
          {
            label: "Win Rate",
            value: winRateValue,
            note: "Completed team matches",
            icon: "\u2197",
            trend: completedRelevantMatches.length ? `${completedWins} wins recorded` : "No results yet",
            trendPositive: completedWins > 0,
            tone: "blue",
          },
          {
            label: "Team Rank",
            value: activeTeamRank ? `#${activeTeamRank}` : "Unranked",
            note: activeMemberTeam ? "Current primary roster standing" : "Create or join a team",
            icon: "\u265B",
            trend: activeMemberTeam ? `${activeMemberTeam.name}` : "No active team selected",
            trendPositive: Boolean(activeMemberTeam),
            tone: "gold",
          },
          {
            label: "Active Leagues",
            value: highlightedLeagues.length,
            note: "Open or active competitions",
            icon: "\u2655",
            trend: highlightedLeagues.length ? "Competition rooms are live" : "No live leagues yet",
            trendPositive: highlightedLeagues.length > 0,
            tone: "blue",
            onClick: () => handleNavigate("leagues"),
          },
          {
            label: "Total Matches",
            value: relevantMatches.length,
            note: `${liveMatches} live now`,
            icon: "\u26A1",
            trend: relevantMatches.length ? `${completedRelevantMatches.length} completed` : "No match history yet",
            trendPositive: relevantMatches.length > 0,
            tone: "violet",
            onClick: () => handleNavigate("play"),
          },
        ]}
        activeLeagues={highlightedLeagues.map((league) => ({
          id: league.id,
          name: league.name,
          subtitle: `${formatCompetitionMode(league.mode)} League`,
          status: league.status,
          isLive: league.status === "active",
          teamsLabel: `${league.current_team_count || registeredLeagueRegistrations.filter((entry) => Number(entry.league_id) === Number(league.id)).length}/${league.max_teams || 0} teams competing`,
        }))}
        upcomingMatches={relevantMatches
          .filter((match) => ["scheduled", "in_progress", "pending_acceptance"].includes(match.status))
          .slice(0, 3)
          .map((match) => ({
            id: match.id,
            teamA: match.team_a_name,
            teamB: match.team_b_name,
            timeLabel: formatMatchMeta(match),
            status: match.status,
            statusLabel:
              match.status === "in_progress"
                ? "Live"
                : String(match.status || "")
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (letter) => letter.toUpperCase()),
          }))}
        teamSummary={activeMemberTeam ? {
          name: activeMemberTeam.name,
          rankLabel: activeTeamRank ? `Rank #${activeTeamRank}` : "Unranked",
          wins: activeTeamWins,
          losses: activeTeamLosses,
        } : null}
        notifications={dashboardNotifications}
        onOpenLeague={(league) => {
          if (league) {
            openLeague(league);
            return;
          }
          handleNavigate("leagues");
        }}
        onOpenMatch={(match) => {
          if (match?.id) {
            fetchMatchDetail(match.id);
          }
          handleNavigate("play");
        }}
        onOpenTeam={() => {
          if (activeMemberTeam) {
            handleOpenTeamProfile(activeMemberTeam);
            navigateTeamView("team-page");
            return;
          }
          navigateTeamView("my-team");
        }}
      />
    );
  };

  const renderPlatformCard = (platform) => {
    const account = linkedAccounts.find((a) => a.platform === platform);
    const isEditing = editingAccountPlatform === platform;
    const isLichess = platform === "lichess";
    const platformLabel = platform === "chess.com" ? "Chess.com" : "Lichess";
    const platformIcon = platform === "chess.com" ? "♟" : "♞";

    return (
      <div key={platform} className={`platformAccountCard ${account ? "isLinked" : "isUnlinked"}`}>
        <div className="platformAccountCardHead">
          <div className="platformAccountIcon">{platformIcon}</div>
          <div className="platformAccountMeta">
            <strong>{platformLabel}</strong>
            {account ? (
              <span className="platformAccountUsername">{account.platform_username}</span>
            ) : (
              <span className="platformAccountUnlinked">Not connected</span>
            )}
          </div>
          {account && !isEditing && (
            <div className="platformAccountActions">
              {account.rating_value && (
                <span className="platformRatingPill">{account.rating_label} {account.rating_value}</span>
              )}
              <button className="platformAccountEditBtn" type="button" onClick={() => {
                setEditingAccountPlatform(platform);
                setAccountLinkDrafts((prev) => ({ ...prev, [platform]: account.platform_username }));
              }}>Edit</button>
            </div>
          )}
          {!account && !isEditing && (
            <button className="platformAccountConnectBtn" type="button" onClick={() => setEditingAccountPlatform(platform)}>
              Connect
            </button>
          )}
        </div>
        {isEditing && (
          <form className="platformAccountEditForm" onSubmit={(e) => {
            e.preventDefault();
            if (isLichess && !account) { handleConnectLichess(); return; }
            if (account) { handleReconnectPlatform(platform); } else { handleLinkAccount(e, platform); }
            setEditingAccountPlatform(null);
          }}>
            {isLichess ? (
              <div className="platformAccountEditRow">
                <span className="mutedText">Lichess uses OAuth — click below to authenticate.</span>
                <div className="platformAccountEditActions">
                  <button className="primaryButton platformSaveBtn" type="button" onClick={() => { handleConnectLichess(); setEditingAccountPlatform(null); }} disabled={loading}>
                    {account ? "Reconnect via OAuth" : "Connect via OAuth"}
                  </button>
                  <button className="secondaryButton platformCancelBtn" type="button" onClick={() => setEditingAccountPlatform(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="platformAccountEditRow">
                <input
                  className="platformUsernameInput"
                  type="text"
                  placeholder={`${platformLabel} username`}
                  value={accountLinkDrafts[platform] || ""}
                  onChange={(e) => handleAccountLinkDraftChange(platform, e.target.value)}
                  autoFocus
                  required
                />
                <div className="platformAccountEditActions">
                  <button className="primaryButton platformSaveBtn" type="submit" disabled={loading}>
                    {loading ? "Saving..." : account ? "Update" : "Connect"}
                  </button>
                  <button className="secondaryButton platformCancelBtn" type="button" onClick={() => setEditingAccountPlatform(null)}>Cancel</button>
                </div>
              </div>
            )}
          </form>
        )}
        {account && isLichess && !isEditing && (
          <div className={`lichessOauthRow ${account.oauth_ready ? "isReady" : "isPending"}`}>
            <span>{account.oauth_ready ? `♟ Launch access active — ${account.platform_username}` : "⚠ OAuth not connected — game launches won't work"}</span>
            {!account.oauth_ready && (
              <button className="secondaryButton platformSaveBtn" type="button" onClick={handleConnectLichess} disabled={loading}>
                Connect OAuth
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderEloChart = () => {
    if (!ratingHistory.length) return null;
    const platforms = [...new Set(ratingHistory.map((p) => p.platform))];
    const COLORS = { lichess: "#5f8ff0", "chess.com": "#f0c658" };
    const chartData = ratingHistory.map((p) => ({
      date: new Date(p.recorded_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      [p.platform]: p.rating_value,
    }));

    return (
      <div className="surfaceCard eloChartCard">
        <div className="cardIntro">
          <h3>Rating History</h3>
          <p className="compactNote">Your Elo over time across connected platforms.</p>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
            <XAxis dataKey="date" tick={{ fill: "rgba(240,244,251,0.5)", fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: "rgba(240,244,251,0.5)", fontSize: 11 }} axisLine={false} tickLine={false} domain={["auto", "auto"]} />
            <Tooltip contentStyle={{ background: "rgba(10,17,28,0.96)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, color: "#f0f4fb", fontSize: 13 }} />
            {platforms.map((platform) => (
              <Line key={platform} type="monotone" dataKey={platform} stroke={COLORS[platform] || "#a0a7b8"} strokeWidth={2} dot={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderAccountSection = () => {
    const winDrawLoss = [
      { name: "Wins", value: activeMemberTeam?.rating_wins || 0, color: "#22c55e" },
      { name: "Draws", value: activeMemberTeam?.rating_draws || 0, color: "#f0c658" },
      { name: "Losses", value: activeMemberTeam?.rating_losses || 0, color: "#ff6b6b" },
    ];
    const totalGames = winDrawLoss.reduce((sum, s) => sum + s.value, 0);

    return (
      <ProfilePage
        title="Profile"
        description="Your competition identity, linked platforms, and performance at a glance."
        metricsNode={renderMetricStrip(accountMetrics)}
        message={message}
        error={error}
        profileSummary={currentUser ? {
          initial: currentUser.username?.charAt(0)?.toUpperCase() || "P",
          name: currentUser.username,
          email: currentUser.email,
          country: currentUser.country || profileForm.country || "Country pending",
          joinedLabel: currentUser.created_at ? `Joined ${new Date(currentUser.created_at).toLocaleDateString()}` : "",
          platforms: linkedAccounts.map((a) => ({
            label: a.platform === "chess.com" ? "Chess.com" : "Lichess",
            value: a.platform_username,
            note: a.rating_value ? `${a.rating_label} ${a.rating_value}` : "Rating pending",
          })),
        } : null}
        primaryNode={!currentUser ? renderHomeAuthCard() : (
          <div className="profilePrimaryStack">
            <div className="surfaceCard profileIdentityCard">
              <div className="profileIdentityRow">
                <div>
                  <h3>{currentUser.username}</h3>
                  <p className="mutedText">{currentUser.email}</p>
                </div>
                <button className="secondaryButton logoutButton" type="button" onClick={handleLogout}>Log Out</button>
              </div>
              <form className="profileCountryForm" onSubmit={handleSaveProfile}>
                <input
                  type="text"
                  name="country"
                  list="country-options"
                  placeholder="Country"
                  value={profileForm.country}
                  onChange={handleProfileChange}
                />
                <button className="secondaryButton" type="submit" disabled={loading}>
                  {loading ? "Saving..." : "Save"}
                </button>
              </form>
            </div>

            <div className="surfaceCard connectedPlatformsCard">
              <div className="cardIntro">
                <h3>Connected Platforms</h3>
                <p className="compactNote">Your usernames here decide which boards you can launch and which teams you can join.</p>
              </div>
              <div className="platformAccountList">
                {supportedPlatforms.map((platform) => renderPlatformCard(platform))}
              </div>
            </div>

            {renderEloChart()}

            {totalGames > 0 && (
              <div className="surfaceCard wdlChartCard">
                <div className="cardIntro">
                  <h3>Team Record</h3>
                  <p className="compactNote">{totalGames} rated team match{totalGames !== 1 ? "es" : ""}</p>
                </div>
                <div className="wdlChartRow">
                  <ResponsiveContainer width={120} height={120}>
                    <PieChart>
                      <Pie data={winDrawLoss} cx="50%" cy="50%" innerRadius={36} outerRadius={54} dataKey="value" paddingAngle={2}>
                        {winDrawLoss.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="wdlLegend">
                    {winDrawLoss.map((entry) => (
                      <div key={entry.name} className="wdlLegendItem">
                        <span className="wdlDot" style={{ background: entry.color }} />
                        <span>{entry.name}</span>
                        <strong>{entry.value}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        sideNode={(
          <div className="surfaceCard sideInfoCard">
            <h3>Good Setup Habits</h3>
            {renderInfoBlocks(ACCOUNT_NOTES)}
          </div>
        )}
      />
    );
  };

  const renderBoardCard = (board) => {
    const draft = boardDrafts[board.id] || {};
    const boardRole = getBoardRoleForCurrentUser(board);
    const currentUserReady = boardRole === "white" ? board.white_ready : boardRole === "black" ? board.black_ready : false;
    const boardPlatform = board.platform || selectedMatch.platform;
    const challengeLinkLabel = boardPlatform === "chess.com" ? "Open Chess.com" : "Open Game";
    const matchAwaitingOwners = selectedMatch?.status === "pending_acceptance";
    const matchFinished = ["completed", "declined", "expired"].includes(selectedMatch?.status);

    return (
      <div key={board.id} className="boardCard">
        <div className="boardHeader">
          <div>
            <strong>Board {board.board_number}</strong>
            <p className="boardSubline">{boardPlatform === "mixed" ? "mixed" : boardPlatform}</p>
          </div>
          <span className={`statusPill is-${(board.challenge_status || "waiting").replace(/_/g, "-")}`}>
            {formatBoardState(board)}
          </span>
        </div>
        <div className="boardPlayers">
          <div className="boardSeat">
            <span className="boardSeatLabel">White</span>
            <strong>{board.white_platform_username}</strong>
          </div>
          <div className="boardSeat">
            <span className="boardSeatLabel">Black</span>
            <strong>{board.black_platform_username}</strong>
          </div>
        </div>
        <p className="instructionText">{board.instructions.summary}</p>
        <div className="boardLinks">
          <a href={board.instructions.white_profile_url} target="_blank" rel="noreferrer">White Profile</a>
          <a href={board.instructions.black_profile_url} target="_blank" rel="noreferrer">Black Profile</a>
          {board.instructions.challenge_url && <a href={board.instructions.challenge_url} target="_blank" rel="noreferrer">{challengeLinkLabel}</a>}
        </div>
        {boardPlatform === "lichess" && (
          <div className="challengeState">
            <span>White launch: {board.white_oauth_ready ? "ready" : "not connected"}</span>
            <span>Black launch: {board.black_oauth_ready ? "ready" : "not connected"}</span>
            <span>White ready: {board.white_ready ? "ready" : "waiting"}</span>
            <span>Black ready: {board.black_ready ? "ready" : "waiting"}</span>
          </div>
        )}
        {boardPlatform === "chess.com" && Array.isArray(board.instructions.setup_items) && (
          <div className="challengeState">
            {board.instructions.setup_items.map((item) => (
              <span key={`${board.id}-${item}`}>{item}</span>
            ))}
          </div>
        )}
        {boardPlatform === "lichess" && boardRole && !board.instructions.challenge_url && (
          <div className="readyControls">
            <button
              className={`secondaryButton inlineActionButton ${currentUserReady ? "isReadyButton" : ""}`}
              type="button"
              onClick={() => handleSetBoardReady(board.id, !currentUserReady)}
              disabled={matchLoading || matchAwaitingOwners || matchFinished}
            >
              {currentUserReady ? "Cancel Ready" : `Mark ${boardRole === "white" ? "White" : "Black"} Ready`}
            </button>
          </div>
        )}
        <div className="boardActionRow">
          {boardPlatform === "lichess" && !board.instructions.challenge_url && (
            <button
              className="primaryButton boardActionButton"
              type="button"
              onClick={() => handleLaunchLichessChallenge(board.id)}
              disabled={matchLoading || matchAwaitingOwners || matchFinished || !board.white_oauth_ready || !board.black_oauth_ready || !board.white_ready || !board.black_ready}
            >
              {matchLoading ? "Launching..." : "Create Lichess Game"}
            </button>
          )}
          {boardPlatform === "chess.com" && (
            <>
              <button
                className="primaryButton boardActionButton"
                type="button"
                onClick={() => handlePrepareChessComChallenge(board.id)}
                disabled={matchLoading || matchAwaitingOwners || matchFinished || selectedMatch.rated}
              >
                {matchLoading ? "Preparing..." : "Open Chess.com Setup"}
              </button>
              <button
                className="secondaryButton boardActionButton"
                type="button"
                onClick={() => handleCopyChessComSetup(board)}
                disabled={matchAwaitingOwners || matchFinished || selectedMatch.rated}
              >
                Copy Setup
              </button>
            </>
          )}
          {boardPlatform === "lichess" && (board.game_link || board.challenge_url) && (
            <button className="secondaryButton boardActionButton" type="button" onClick={() => handleSyncLichessResult(board.id)} disabled={matchLoading || matchFinished}>
              {matchLoading ? "Syncing..." : "Sync From Lichess"}
            </button>
          )}
        </div>
        <div className="boardFormGrid">
          <input type="url" placeholder="Paste game link" value={draft.game_link || ""} onChange={(e) => handleBoardDraftChange(board.id, "game_link", e.target.value)} disabled={matchFinished} />
          <select value={draft.result || ""} onChange={(e) => handleBoardDraftChange(board.id, "result", e.target.value)} disabled={matchFinished}>
            <option value="">Result Pending</option>
            <option value="white">White Won</option>
            <option value="black">Black Won</option>
            <option value="draw">Draw</option>
          </select>
          <input type="text" placeholder="Notes or challenge details" value={draft.notes || ""} onChange={(e) => handleBoardDraftChange(board.id, "notes", e.target.value)} disabled={matchFinished} />
        </div>
        <button className="secondaryButton signupButton" type="button" onClick={() => handleSubmitBoard(board.id)} disabled={matchLoading || matchFinished}>
          {matchLoading ? "Saving..." : "Save Board Update"}
        </button>
      </div>
    );
  };

  const renderRosterMemberCard = (member) => (
    <div key={`roster-${member.user_id}`} className="rosterMemberCard">
      <div className="rosterMemberAvatar">{member.username?.charAt(0)?.toUpperCase()}</div>
      <div className="rosterMemberBody">
        <div className="rosterMemberNameRow">
          <strong>{member.username}</strong>
          {renderPresencePill(member.is_online)}
        </div>
        {member.platform_username ? (
          <span className={`rosterMemberHandle is-${(member.platform || "").replace(".", "-")}`}>
            {member.platform === "chess.com" ? "Chess.com" : member.platform} · {member.platform_username}
          </span>
        ) : (
          <span className="rosterMemberHandle isPending">No platform linked</span>
        )}
      </div>
      <div className="rosterMemberRating">
        {member.rating_value ? (
          <>
            <strong>{member.rating_value}</strong>
            <small>{member.rating_label || "elo"}</small>
          </>
        ) : (
          <small className="mutedText">Unrated</small>
        )}
      </div>
    </div>
  );

  const renderBoardAssignmentPanel = () => {
    const canEdit = activeTeamIsOwnerControlled;
    if (!canEdit || !activeMemberTeam) return null;
    const boardCount = activeTeamMembers.length;
    const boards = Array.from({ length: Math.max(boardCount, 1) }, (_, i) => i + 1);

    return (
      <div className="surfaceCard boardAssignmentCard">
        <div className="cardIntro">
          <h3>Board Assignment</h3>
          <p className="compactNote">Set the board order for your next match. Save up to 5 lineups to reuse.</p>
        </div>

        {rosterConfigs.length > 0 && (
          <div className="rosterConfigList">
            <span className="rosterConfigListLabel">Saved lineups</span>
            <div className="rosterConfigChips">
              {rosterConfigs.map((config) => (
                <div key={config.id} className="rosterConfigChip">
                  <button className="rosterConfigChipName" type="button" onClick={() => handleApplyRosterConfig(config)}>
                    {config.name}
                  </button>
                  <button className="rosterConfigChipDelete" type="button" onClick={() => handleDeleteRosterConfig(config.id)} title="Delete">✕</button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="boardAssignmentSlots">
          {boards.map((boardNum) => {
            const assignedId = boardAssignmentDraft[boardNum];
            return (
              <div key={boardNum} className="boardAssignmentSlot">
                <span className="boardSlotLabel">Board {boardNum}</span>
                <select
                  className="boardSlotSelect"
                  value={assignedId || ""}
                  onChange={(e) => setBoardAssignmentDraft((prev) => ({ ...prev, [boardNum]: e.target.value ? Number(e.target.value) : undefined }))}
                >
                  <option value="">— Unassigned —</option>
                  {activeTeamMembers.map((m) => (
                    <option key={m.user_id} value={m.user_id}>{m.username}{m.rating_value ? ` (${m.rating_value})` : ""}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>

        {rosterConfigs.length < 5 && (
          <div className="rosterConfigSaveRow">
            <input
              className="rosterConfigNameInput"
              type="text"
              placeholder="Lineup name (e.g. Standard, Aggressive)"
              value={rosterConfigName}
              onChange={(e) => setRosterConfigName(e.target.value)}
              maxLength={64}
            />
            <button
              className="secondaryButton"
              type="button"
              onClick={handleSaveRosterConfig}
              disabled={!rosterConfigName.trim()}
            >
              Save Lineup
            </button>
          </div>
        )}
        {teamMessage && <p className="successMessage">{teamMessage}</p>}
        {teamError && <p className="errorMessage">{teamError}</p>}
      </div>
    );
  };

  const renderSignedInTeamOverview = () => {
    const activeLeagueBand = getProjectedLeagueBand(activeMemberTeam?.team_rating);
    const teamSummaryStats = activeMemberTeam
      ? [
          { label: "Site Rating", value: activeMemberTeam.competitive_rating || 1500, note: "Live standing" },
          { label: "Avg Elo", value: activeMemberTeam.team_rating || "—", note: "Roster average" },
          { label: "Record", value: `${activeMemberTeam.rating_wins || 0}W ${activeMemberTeam.rating_draws || 0}D ${activeMemberTeam.rating_losses || 0}L`, note: "Rated matches" },
          { label: "Roster", value: activeMemberCount, note: "Active members" },
        ]
      : [];

    return (
      <div className="signedInTeamDeck">
        <div className="surfaceCard signedInTeamHeroCard">
          {pendingInviteCount > 0 && (
            <div className="liveInboxNotice">
              <div>
                <strong>{pendingInviteCount} team {pendingInviteCount === 1 ? "invite" : "invites"} waiting</strong>
                <p className="compactNote">Open the invite desk to accept or decline them.</p>
              </div>
              <button className="secondaryButton inlineActionButton" type="button" onClick={() => navigateTeamView("invites")}>
                Open Invites
              </button>
            </div>
          )}
          {activeMemberTeam ? (
            <>
              <div className="teamRoomHeroTop">
                <div className="teamRoomHeroIdentity">
                  <span className="teamProfileBadge teamRoomHeroBadge">{getTeamBadgeMark(activeMemberTeam)}</span>
                  <div className="teamRoomHeroText">
                    <h3>{activeMemberTeam.name}</h3>
                    <div className="teamRoomHeroMeta">
                      <span>{activeMemberTeam.country || "Country pending"}</span>
                      <span>{activeMemberCount} {activeMemberCount === 1 ? "member" : "members"}</span>
                      <span className="teamRatingBadge">♟ {activeMemberTeam.competitive_rating || 1500}</span>
                    </div>
                    <div className="teamPlatformList">
                      {(activeMemberTeam.supported_platforms || []).map((platformLink) => (
                        <span key={`hero-${activeMemberTeam.id}-${platformLink.platform}`} className={`teamPlatformChip is-${platformLink.platform.replace(".", "-")}`}>
                          {platformLink.platform}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="teamRoomHeroActions">
                  {activeTeamIsOwnerControlled ? (
                    <>
                      <button className="secondaryButton inlineActionButton" type="button" onClick={() => navigateTeamView("my-team")}>
                        Edit Team
                      </button>
                      <button className="primaryButton inlineActionButton" type="button" onClick={() => navigateTeamView("invites")}>
                        Invite Player
                      </button>
                    </>
                  ) : (
                    <span className="compactNote">View-only — contact your captain to make changes.</span>
                  )}
                </div>
              </div>
              {activeMemberTeam.bio && (
                <p className="teamBioText">{activeMemberTeam.bio}</p>
              )}
              <div className="teamRoomStatStrip">
                {teamSummaryStats.map((stat) => (
                  <div key={stat.label} className="teamRoomStat">
                    <span>{stat.label}</span>
                    <strong>{stat.value}</strong>
                    <small>{stat.note}</small>
                  </div>
                ))}
              </div>
              <div className="teamIdentityRow teamRoomBandRow">
                <span className="teamIdentityChip">{activeLeagueBand.name}</span>
                <span className="teamIdentityChip">{formatMatchmakingPreference(activeMemberTeam.matchmaking_preference)}</span>
                <span className={`teamIdentityChip ${activeMemberTeam.league_registered ? "isLeagueRegistered" : ""}`}>
                  {activeMemberTeam.league_registered ? "Registered for leagues" : "League optional"}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="cardIntro">
                <h3>Your Team Desk</h3>
                <p className="compactNote">
                  You are signed in but not on a roster yet. Create a team or accept an invite to enter the pool.
                </p>
              </div>
              <div className="teamCardActions">
                <button className="primaryButton inlineActionButton" type="button" onClick={() => navigateTeamView("discover")}>Discover Teams</button>
                <button className="secondaryButton inlineActionButton" type="button" onClick={() => navigateTeamView("my-team")}>Create Team</button>
              </div>
            </>
          )}
        </div>

        {activeMemberTeam && activeTeamMembers.length > 0 && (
          <div className="surfaceCard rosterCard">
            <div className="rosterCardHead">
              <div className="cardIntro">
                <h3>Team Roster</h3>
                <p className="compactNote">{activeTeamMembers.length} player{activeTeamMembers.length !== 1 ? "s" : ""} · sorted by rating</p>
              </div>
              {activeTeamIsOwnerControlled && (
                <button className="secondaryButton inlineActionButton" type="button" onClick={() => navigateTeamView("invites")}>
                  + Invite
                </button>
              )}
            </div>
            <div className="rosterMemberList">
              {activeTeamMembers.map(renderRosterMemberCard)}
            </div>
          </div>
        )}

        {renderBoardAssignmentPanel()}
      </div>
    );
  };

  const renderTeamsSection = () => {
    const signedInTeamView = ["my-team", "discover", "players", "table", "invites", "team-page"].includes(resolvedTeamView) ? resolvedTeamView : "my-team";
    const teamRoomPrimaryViews = currentUser
      ? [
          { id: "my-team", label: "My Team" },
          { id: "discover", label: "Discover Teams" },
          { id: "players", label: "Players" },
          { id: "table", label: "Leaderboard" },
        ]
      : [
          { id: "overview", label: "Overview" },
          { id: "discover", label: "Discover Teams" },
          { id: "players", label: "Players" },
          { id: "table", label: "Leaderboard" },
        ];
    const showVisitorTeamHub = !currentUser && (resolvedTeamView === "overview" || resolvedTeamView === "discover" || resolvedTeamView === "table");
    const showSignedInDiscoverHub = currentUser && signedInTeamView === "discover";
    const signedInTeamTitle = signedInTeamView === "players"
      ? "Players"
      : signedInTeamView === "table"
        ? "Leaderboard"
        : signedInTeamView === "discover"
          ? "Teams"
          : "My Team";
    const signedInTeamDescription = signedInTeamView === "players"
      ? "Browse roster coverage, live presence, and current platform seats across the field."
      : signedInTeamView === "table"
        ? "Track the current ladder and open any team page directly from the table."
        : signedInTeamView === "discover"
          ? "Scout open rooms, compare platform coverage, and jump straight into the right team page."
          : "Manage your roster, invites, and current room from one team page.";

    return (
      <TeamPage
        title={currentUser ? signedInTeamTitle : "Teams"}
        description={
          currentUser
            ? signedInTeamDescription
            : "Build team rooms, scout the field, check platform coverage, and keep the table in sight while the roster takes shape."
        }
        activeTeam={activeMemberTeam}
        activeTeamMembers={activeTeamMembers}
        pendingInviteCount={pendingInviteCount}
        metricsNode={!currentUser ? renderMetricStrip(teamMetrics) : null}
        showcaseNode={!currentUser ? (
          <div className="workspaceShowcase workspaceShowcaseTeams">
            <div className="workspaceShowcaseLead">
              <span className="workspaceShowcaseLabel">Club Floor</span>
              <h3>Build the room you want to run.</h3>
              <p>
                Owners set the platforms, shape the roster, and decide how wide the team
                opens its pool. Players scout the field, compare ladders, and find the side
                that actually has a seat for them.
              </p>
            </div>
            <div className="workspaceShowcaseGrid">
              <div className="workspaceShowcaseTile">
                <span>Current seat</span>
                <strong>{activeMemberTeam?.name || "Open board"}</strong>
                <p>{activeMemberTeam ? `${activeMemberTeam.member_count} players | site ${activeMemberTeam.competitive_rating || 1500}` : "You are free to build or join a roster."}</p>
              </div>
              <div className="workspaceShowcaseTile">
                <span>Invite pulse</span>
                <strong>{incomingInvites.filter((invite) => invite.status === "pending").length}</strong>
                <p>Invites currently waiting in player inboxes.</p>
              </div>
              <div className="workspaceShowcaseTile">
                <span>Pool shape</span>
                <strong>{standingTeams.length}</strong>
                <p>Ranked teams already separated by site rating.</p>
              </div>
            </div>
          </div>
        ) : null}
        tabsNode={(
          <div className="workspaceTabs">
            {teamRoomPrimaryViews.map((view) => (
              <button
                key={view.id}
                type="button"
                className={`workspaceTabButton ${(currentUser ? signedInTeamView : resolvedTeamView) === view.id ? "isActive" : ""}`}
                onClick={() => navigateTeamView(view.id)}
              >
                {view.label}
              </button>
            ))}
            {currentUser && pendingInviteCount > 0 && (
              <button className={`workspaceTabButton ${signedInTeamView === "invites" ? "isActive" : ""}`} type="button" onClick={() => navigateTeamView("invites")}>
                Invites
              </button>
            )}
            <button className="workspaceTabButton" type="button" onClick={handleLoadTeamsCenter}>
              Refresh
            </button>
          </div>
        )}
        gateNode={!currentUser ? (
          <div className="surfaceCard teamsGateCard">
            <h3>Sign in to create or join a team.</h3>
            <p className="compactNote">
              Visitors can browse. Players and owners can actually sit down and move pieces.
              Sign in once you are ready to join a roster or start one.
            </p>
            <button className="primaryButton" type="button" onClick={() => handleNavigate("account")}>
              Sign In Or Create Account
            </button>
          </div>
        ) : null}
      >
      {currentUser && signedInTeamView === "my-team" && renderSignedInTeamOverview()}
      {resolvedTeamView === "team-page" && renderTeamProfilePage()}
      {(showVisitorTeamHub || showSignedInDiscoverHub) && (
      <div className="sectionGrid threeColumn teamHubGrid">
        <div className="surfaceCard">
          <div className="cardIntro">
            <h3>{currentUser ? "Start A Team" : "Club Directory"}</h3>
            <p className="compactNote">
              {currentUser
                ? "Name the side, choose the platforms it can actually field, and give it a room on the site."
                : "Browse the active clubs, the sites they can field on, and the roster strength they are carrying."}
            </p>
          </div>
          {currentUser && canManageMembership ? (
            <form className="signupForm" onSubmit={handleCreateTeam}>
              <input type="text" name="name" placeholder="Team Name" value={teamForm.name} onChange={handleTeamChange} required />
              <div className="teamIdentityGrid">
                <input type="text" name="country" list="country-options" placeholder="Country" value={teamForm.country} onChange={handleTeamChange} />
                <div className="badgePickerCard">
                  <span className="badgePickerLabel">Team Badge</span>
                  {renderBadgePicker(teamForm.badge, (badge) => setTeamForm((prev) => ({ ...prev, badge })))}
                </div>
              </div>
              <textarea
                name="bio"
                placeholder="A few lines about the team, its style, or the club behind it"
                value={teamForm.bio}
                onChange={handleTeamChange}
                rows={4}
              />
              <div className="fieldBlock">
                <div className="fieldBlockHeader">
                  <strong>Matchmaking pool</strong>
                  <span>Choose whether this team should answer on one platform or let the desk split pairings board by board.</span>
                </div>
                {renderMatchmakingPreferenceControl(
                  teamForm.matchmaking_preference,
                  linkedAccounts.map((account) => String(account.id)),
                  (nextPreference) => setTeamForm((prev) => ({ ...prev, matchmaking_preference: nextPreference })),
                  "full"
                )}
              </div>
              <p className="teamEditHint">
                Lichess and Chess.com keep the whole match on one site. Allow all lets the site read each player's linked platform and build mixed pairings automatically.
              </p>
              <div className="leagueFormGrid teamModeGrid">
                <label>
                  <span>Team Mode</span>
                  <select
                    name="competition_mode"
                    value={teamForm.competition_mode}
                    onChange={(event) =>
                      setTeamForm((prev) => ({
                        ...prev,
                        competition_mode: event.target.value,
                        grade_band: event.target.value === "scholastic" ? prev.grade_band : "",
                      }))
                    }
                  >
                    {LEAGUE_MODE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                {teamForm.competition_mode === "scholastic" && (
                  <label>
                    <span>Grade Band</span>
                    <select name="grade_band" value={teamForm.grade_band} onChange={handleTeamChange}>
                      <option value="">Select grade band</option>
                      {GRADE_BAND_OPTIONS.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              <label className="checkboxRow">
                <input type="checkbox" name="league_registered" checked={teamForm.league_registered} onChange={handleTeamChange} />
                <span>Registered to compete in leagues</span>
              </label>
              <button className="primaryButton signupButton" type="submit" disabled={teamLoading || linkedAccounts.length === 0}>
                {teamLoading ? "Creating Team..." : "Create Team"}
              </button>
            </form>
          ) : !currentUser ? (
            <div className="infoList">
              <div>{teams.length} teams are already on the floor.</div>
              <div>{standingTeams.length} teams are sitting on the live table.</div>
              <div>Owners choose the platforms their roster can actually answer on.</div>
            </div>
          ) : (
            <div className="teamListCard emptyTeamCard">
              <h3>Already on a team</h3>
              <p className="compactNote">One roster at a time. Leave your current team before starting another.</p>
              <button className="secondaryButton inlineActionButton" type="button" onClick={() => navigateTeamView("my-team")}>
                Open Current Team
              </button>
            </div>
          )}
          <div className="actions teamActions">
            <button className="secondaryButton" type="button" onClick={handleLoadTeamsCenter}>Refresh Teams</button>
          </div>
          {teamMessage && <p className="successMessage">{teamMessage}</p>}
          {teamError && <p className="errorMessage">{teamError}</p>}
        </div>
        <div className="surfaceCard">
          <div className="cardIntro">
            <h3>{currentUser ? "Discover Teams" : "Explore Team Rooms"}</h3>
            <p className="compactNote">
              {currentUser
                ? "The list starts with teams that fit your country and connected platforms, then opens into the wider field when you want to browse."
                : "Search by team name or platform, then open the clubs that look like the right fit."}
            </p>
          </div>
          <input
            className="teamSearchInput"
            type="text"
            placeholder="Search teams by name or platform"
            value={teamSearch}
            onChange={(e) => setTeamSearch(e.target.value)}
          />
          {!normalizedTeamSearch && recommendedTeams.length > 0 && (
            <div className="teamDiscoverySection">
              <div className="teamDiscoveryHeader">
                <div>
                  <h4>{recommendedHeading}</h4>
                  <p className="compactNote">
                    Teams are ranked by your country, your connected platforms, and the shape of the current ladder.
                  </p>
                </div>
                {recommendedTeams.length > 1 && (
                  <div className="carouselControls">
                    <button
                      className="secondaryButton inlineActionButton"
                      type="button"
                      onClick={() => setRecommendedTeamIndex((current) => (current - 1 + recommendedTeams.length) % recommendedTeams.length)}
                    >
                      Previous
                    </button>
                    <span className="compactNote">
                      {recommendedTeamIndex + 1} / {recommendedTeams.length}
                    </span>
                    <button
                      className="secondaryButton inlineActionButton"
                      type="button"
                      onClick={() => setRecommendedTeamIndex((current) => (current + 1) % recommendedTeams.length)}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
              <div className="teamCarousel featuredTeamList">
                {activeRecommendedTeam && renderDiscoverTeamCard(activeRecommendedTeam, { featured: true })}
              </div>
            </div>
          )}
          <div className="teamList">
            {(normalizedTeamSearch ? rankedDiscoverTeams : visibleFallbackTeams).map((team) => renderDiscoverTeamCard(team))}
            {normalizedTeamSearch && rankedDiscoverTeams.length === 0 && (
              <div className="teamListCard emptyTeamCard">
                <h3>No teams match that search</h3>
                <p className="compactNote">Try another team name or platform.</p>
              </div>
            )}
            {!normalizedTeamSearch && recommendedTeams.length === 0 && visibleFallbackTeams.length === 0 && (
              <div className="teamListCard emptyTeamCard">
                <h3>No teams are available yet</h3>
                <p className="compactNote">Create the first team room and start the table.</p>
              </div>
            )}
          </div>
          {!normalizedTeamSearch && discoverTeamsFallback.length > visibleFallbackTeams.length && (
            <div className="actions teamActions">
              <button className="secondaryButton" type="button" onClick={() => setShowAllDiscoverTeams(true)}>
                Show More Teams
              </button>
            </div>
          )}
        </div>
        <div className="surfaceCard leaderboardCard">
          <div className="cardIntro">
            <h3>Club Table</h3>
            <p className="compactNote">
              Site rating moves with match results. Average Elo still shows the raw shape
              of the roster underneath it.
            </p>
          </div>
          <div className="leaderboardList">
            {standingTeams.map((team, index) => (
              <div
                key={`leaderboard-${team.id}`}
                className={`leaderboardRow isTeamPageTrigger ${index === 0 ? "isPodiumGold" : index === 1 ? "isPodiumSilver" : index === 2 ? "isPodiumBronze" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => handleOpenTeamProfile(team)}
                onKeyDown={(event) => handleTeamCardKeyDown(event, team)}
              >
                <div className="leaderboardIdentity">
                  <span className="leaderboardRank">#{index + 1}</span>
                  <div>
                    {renderTeamNameButton(team, "leaderboardNameLink")}
                    <p>
                      {team.member_count} players
                      {team.team_rating ? ` | avg ${team.team_rating}` : " | avg pending"}
                    </p>
                  </div>
                </div>
                <div className="leaderboardRating">
                  <strong>{team.competitive_rating || 1500}</strong>
                  <span>site rating</span>
                </div>
              </div>
            ))}
            {standingTeams.length === 0 && (
              <div className="teamListCard emptyTeamCard">
                <h3>No standings yet</h3>
                <p className="compactNote">Once teams finish matches, the rating table will start to separate.</p>
              </div>
            )}
          </div>
        </div>
        </div>
      )}
      {currentUser && signedInTeamView === "table" && (
        <div className="surfaceCard largeLeaderboardCard">
          <div className="cardIntro">
            <h3>Club Table</h3>
            <p className="compactNote">
              Site rating shifts with completed matches. Average Elo still shows the shape of the roster underneath it.
            </p>
          </div>
          <div className="leaderboardList">
            {standingTeams.map((team, index) => (
              <div
                key={`table-${team.id}`}
                className={`leaderboardRow isTeamPageTrigger ${index === 0 ? "isPodiumGold" : index === 1 ? "isPodiumSilver" : index === 2 ? "isPodiumBronze" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => handleOpenTeamProfile(team)}
                onKeyDown={(event) => handleTeamCardKeyDown(event, team)}
              >
                <div className="leaderboardIdentity">
                  <span className="leaderboardRank">#{index + 1}</span>
                  <div>
                    <strong>{team.name}</strong>
                    <p>
                      {team.member_count} players
                      {team.team_rating ? ` | avg ${team.team_rating}` : " | avg pending"}
                    </p>
                  </div>
                </div>
                <div className="leaderboardRating">
                  <strong>{team.competitive_rating || 1500}</strong>
                  <span>site rating</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {resolvedTeamView === "players" && (
        <div className="sectionGrid twoColumn teamHubGrid">
          <div className="surfaceCard">
            <div className="cardIntro">
              <h3>Player Directory</h3>
              <p className="compactNote">
                Browse active players, see their current team, and check whether
                their roster is set up for Lichess, Chess.com, or both.
              </p>
            </div>
            <input
              type="text"
              placeholder="Search players, teams, or platforms"
              value={teamSearch}
              onChange={(e) => setTeamSearch(e.target.value)}
            />
            <div className="teamList">
              {visiblePlayers.map((player) => (
                <div key={`${player.user_id}-${player.team_id}-${player.platform_account_id || player.platform}`} className="teamListCard directoryCard">
                  <div className="teamCardHeader">
                    <div>
                      <h3>{player.username}</h3>
                      <button className="textActionButton teamMetaLink" type="button" onClick={() => handleOpenTeamProfile({ id: player.team_id })}>
                        {player.team_name}
                      </button>
                    </div>
                    <div className="directoryStatusStack">
                      {renderPresencePill(player.is_online)}
                      <span className="ownerBadge">{player.platform || "unassigned"}</span>
                    </div>
                  </div>
                  <p className="compactNote">
                    {player.platform_username ? `${player.platform_username}` : "No linked platform username"}{player.rating_value ? ` | ${player.rating_label || "rating"} ${player.rating_value}` : ""}
                  </p>
                  <div className="teamPlatformList">
                    {(player.supported_platforms || []).map((platformLink) => (
                      <span key={`${player.user_id}-${player.team_id}-${platformLink.platform}`} className="teamPlatformChip">
                        {platformLink.platform}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
              {visiblePlayers.length === 0 && (
                <div className="teamListCard emptyTeamCard">
                  <h3>No players found</h3>
                  <p className="compactNote">Try another name, team, or platform search.</p>
                </div>
              )}
            </div>
          </div>
          <div className="surfaceCard largeLeaderboardCard">
            <div className="cardIntro">
              <h3>Team Coverage</h3>
              <p className="compactNote">
                Team cards show supported match platforms. Player rows show the
                account each member is currently using for team play.
              </p>
            </div>
            <div className="teamList">
              {teams.slice(0, 12).map((team) => (
                <div
                  key={`coverage-${team.id}`}
                  className="teamListCard isTeamPageTrigger"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpenTeamProfile(team)}
                  onKeyDown={(event) => handleTeamCardKeyDown(event, team)}
                >
                  <div className="teamCardHeader">
                    <div>
                      <h3>{renderTeamNameButton(team)}</h3>
                      <p className="teamMetaLine">{team.member_count} players | site {team.competitive_rating || 1500}</p>
                    </div>
                    <span className="statusPill is-in-progress">{team.team_rating ? `avg ${team.team_rating}` : "avg pending"}</span>
                  </div>
                  <p className="compactNote">Matchmaking: {formatMatchmakingPreference(team.matchmaking_preference)}</p>
                  <div className="teamPlatformList">
                    {(team.supported_platforms || []).map((platformLink) => (
                      <span key={`coverage-${team.id}-${platformLink.platform}`} className="teamPlatformChip">
                        {platformLink.platform}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {currentUser && (resolvedTeamView === "my-team" || resolvedTeamView === "invites") && (
        <div className="sectionGrid twoColumn teamsSecondaryGrid">
          <div className="surfaceCard">
            <div className="cardIntro">
              <h3>{resolvedTeamView === "my-team" ? "Current Team" : "Owner Controls"}</h3>
              <p className="compactNote">
                {resolvedTeamView === "my-team"
                  ? "This is the roster you are currently on. Joining and creating stay locked until you leave it."
                  : "Owners can edit team details, supported matchmaking platforms, and invitations from this page."}
              </p>
            </div>
            <div className="teamList">
              {resolvedTeamView === "my-team" && activeMemberTeam && (
                <div className="teamListCard ownerTeamCard">
                  <div className="teamCardHeader">
                    <div>
                      <h3>{renderTeamNameButton(activeMemberTeam)}</h3>
                      <p className="teamMetaLine">
                        {activeMemberTeam.team_rating ? `avg ${activeMemberTeam.team_rating}` : "avg pending"} | site {activeMemberTeam.competitive_rating || 1500} | {activeMemberTeam.member_count} players
                      </p>
                    </div>
                    {activeTeamIsOwnerControlled ? <span className="ownerBadge">Owner</span> : <span className="statusPill is-in-progress">Member</span>}
                  </div>
                  <div className="teamIdentityRow">
                    <span className="teamBadgeMark">{getTeamBadgeMark(activeMemberTeam)}</span>
                    {activeMemberTeam.country && <span className="teamIdentityChip">{activeMemberTeam.country}</span>}
                    <span className="teamIdentityChip">
                      {getProjectedLeagueBand(activeMemberTeam.team_rating).name}
                    </span>
                    <span className={`teamIdentityChip ${activeMemberTeam.league_registered ? "isLeagueRegistered" : ""}`}>
                      {activeMemberTeam.league_registered ? "League registered" : "League optional"}
                    </span>
                  </div>
                  {activeMemberTeam.bio && <p className="teamBioText">{activeMemberTeam.bio}</p>}
                  <p className="compactNote">Matchmaking: {formatMatchmakingPreference(activeMemberTeam.matchmaking_preference)}</p>
                  <div className="teamPlatformList">
                    {(activeMemberTeam.supported_platforms || []).map((platformLink) => (
                      <span key={`member-${activeMemberTeam.id}-${platformLink.platform}`} className="teamPlatformChip">
                        {platformLink.platform} - {platformLink.platform_username}
                      </span>
                    ))}
                  </div>
                  {!activeTeamIsOwnerControlled && (
                    <div className="teamCardActions">
                      <button className="secondaryButton inlineActionButton dangerButton" type="button" onClick={() => handleLeaveTeam(activeMemberTeam)} disabled={teamLoading}>
                        {teamLoading ? "Leaving..." : "Leave Team"}
                      </button>
                    </div>
                  )}
                  {activeTeamIsOwnerControlled && editingTeamId === activeMemberTeam.id && (
                    renderTeamEditPanel(activeMemberTeam.id)
                  )}
                  {activeTeamIsOwnerControlled && editingTeamId !== activeMemberTeam.id && (
                    <div className="teamCardActions">
                      <button className="secondaryButton inlineActionButton" type="button" onClick={() => startEditingTeam(activeMemberTeam)}>
                        Edit Team
                      </button>
                      <button className="secondaryButton inlineActionButton" type="button" onClick={() => navigateTeamView("invites")}>
                        Open Invite Desk
                      </button>
                      <button className="secondaryButton inlineActionButton dangerButton" type="button" onClick={() => handleLeaveTeam(activeMemberTeam)} disabled={teamLoading}>
                        {teamLoading ? "Working..." : "Leave Team"}
                      </button>
                    </div>
                  )}
                  {activeTeamIsOwnerControlled && (
                    <p className="compactNote">
                      If you step away from this roster, pass captaincy to another member first. Solo-owner teams will be disbanded when you leave.
                    </p>
                  )}
                </div>
              )}
              {myTeams
                .filter((team) => resolvedTeamView === "invites" || team.id !== activeMemberTeam?.id)
                .map((team) => {
                const isEditing = editingTeamId === team.id;
                const leagueBand = getProjectedLeagueBand(team.team_rating);

                return (
                  <div key={team.id} className="teamListCard ownerTeamCard">
                    <div className="teamCardHeader">
                      <div>
                        <h3>{renderTeamNameButton(team)}</h3>
                        <p className="teamMetaLine">
                          {team.team_rating ? `avg ${team.team_rating}` : "avg pending"} | site {team.competitive_rating || 1500} | {team.member_count} players
                        </p>
                      </div>
                      <span className="ownerBadge">Owner</span>
                    </div>
                    <div className="teamIdentityRow">
                      <span className="teamBadgeMark">{getTeamBadgeMark(team)}</span>
                      {team.country && <span className="teamIdentityChip">{team.country}</span>}
                      <span className="teamIdentityChip">{leagueBand.name}</span>
                      <span className={`teamIdentityChip ${team.league_registered ? "isLeagueRegistered" : ""}`}>
                        {team.league_registered ? "League registered" : "League optional"}
                      </span>
                    </div>
                    {team.bio && <p className="teamBioText">{team.bio}</p>}
                    <p className="compactNote">Matchmaking: {formatMatchmakingPreference(team.matchmaking_preference)}</p>
                    <div className="teamPlatformList">
                      {(team.supported_platforms || []).map((platformLink) => (
                        <span key={`${team.id}-${platformLink.platform}`} className="teamPlatformChip">
                          {platformLink.platform} - {platformLink.platform_username}
                        </span>
                      ))}
                    </div>
                    {isEditing ? (
                      renderTeamEditPanel(team.id)
                    ) : (
                      <div className="teamCardActions">
                        <button className="secondaryButton inlineActionButton" type="button" onClick={() => startEditingTeam(team)}>Edit Team</button>
                      </div>
                    )}
                    <div className="inviteComposer">
                      <input
                        type="text"
                        placeholder="Invite by username or email"
                        value={inviteDrafts[team.id] || ""}
                        onChange={(e) => handleInviteDraftChange(team.id, e.target.value)}
                      />
                      <button className="secondaryButton inlineActionButton" type="button" onClick={() => handleCreateInvite(team)} disabled={teamLoading}>
                        Send Invite
                      </button>
                    </div>
                    <p className="teamEditHint">
                      Send a direct invite and the player will have 24 hours to confirm or decline it. If they are outside the site, you can still share the generated link from the success message.
                    </p>
                  </div>
                );
              })}
              {resolvedTeamView === "invites" && myTeams.length === 0 && (
                <div className="teamListCard emptyTeamCard">
                  <h3>No owned teams yet</h3>
                  <p className="compactNote">Create a team above to open owner controls and invites.</p>
                </div>
              )}
              {resolvedTeamView === "my-team" && !activeMemberTeam && (
                <div className="teamListCard emptyTeamCard">
                  <h3>No current team</h3>
                  <p className="compactNote">Open Discover to create or join a roster.</p>
                </div>
              )}
            </div>
          </div>
          <div className="sectionGrid inviteColumn">
            {resolvedTeamView === "my-team" && (
            <div className="surfaceCard ratingSnapshotCard">
              <div className="cardIntro">
                <h3>Your Ratings</h3>
                <p className="compactNote">
                  These are the ratings currently attached to your linked
                  accounts. Team averages are built from the joined accounts of
                  all current members.
                </p>
              </div>
              <div className="linkedAccountList">
                {linkedAccounts.map((account) => (
                  <div key={`rating-${account.id}`} className="linkedAccountChip ratingChip">
                    <span>{account.platform}</span>
                    <strong>{account.platform_username}</strong>
                    <small>{formatAccountRating(account)}</small>
                  </div>
                ))}
              </div>
            </div>
            )}
            {resolvedTeamView === "invites" && (
            <div className="surfaceCard">
              <div className="cardIntro">
                <h3>Invites For You</h3>
                <p className="compactNote">
                  Each invite stays live for 24 hours. Confirm it with a supported linked
                  account or decline it so the owner can send a fresh one later.
                </p>
              </div>
              {invitePreview && (
                <div className="invitePreviewCard">
                  <div className="teamCardHeader">
                    <div>
                      <h3>{invitePreview.team_name}</h3>
                      <p className="teamMetaLine">Invited by {invitePreview.owner_username}</p>
                    </div>
                    <span className={`statusPill is-${invitePreview.status}`}>{invitePreview.status}</span>
                  </div>
                  <div className="teamPlatformList">
                    {(invitePreview.supported_platforms || []).map((platformLink) => (
                      <span key={`preview-${invitePreview.id}-${platformLink.platform}`} className="teamPlatformChip">
                        {platformLink.platform} - {platformLink.platform_username}
                      </span>
                    ))}
                  </div>
                  {!currentUser ? (
                    <div className="invitePreviewActions">
                      <p className="mutedText">Sign in or create an account with the invited email address to accept this invite.</p>
                      <button className="primaryButton inlineActionButton" type="button" onClick={() => handleNavigate("account")}>
                        Sign In To Accept
                      </button>
                    </div>
                  ) : previewInviteMatchingAccounts.length > 0 ? (
                    <div className="invitePreviewActions">
                      {renderAccountChoiceGroup(
                        previewInviteMatchingAccounts,
                        invitePlatformSelections[invitePreview.id] || String(previewInviteMatchingAccounts[0]?.id || ""),
                        (nextId) => setInvitePlatformSelections((prev) => ({ ...prev, [invitePreview.id]: nextId }))
                      )}
                      <div className="teamCardActions">
                        <button className="primaryButton inlineActionButton" type="button" onClick={() => handleAcceptInvite(invitePreview)} disabled={acceptingInviteId === invitePreview.id || invitePreview.status !== "pending"}>
                          {acceptingInviteId === invitePreview.id ? "Accepting..." : "Accept Invite"}
                        </button>
                        <button className="secondaryButton inlineActionButton dangerButton" type="button" onClick={() => handleDeclineInvite(invitePreview)} disabled={acceptingInviteId === invitePreview.id || invitePreview.status !== "pending"}>
                          {acceptingInviteId === invitePreview.id ? "Working..." : "Decline Invite"}
                        </button>
                        <button className="secondaryButton inlineActionButton" type="button" onClick={clearInviteTokenFromUrl}>
                          Clear Invite
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="mutedText">Connect one of this team's supported platforms before accepting the invite.</p>
                  )}
                  {invitePreview.expires_at && invitePreview.status === "pending" && (
                    <p className="compactNote">Expires: {new Date(invitePreview.expires_at).toLocaleString()}</p>
                  )}
                </div>
              )}
              <div className="inviteList">
                {incomingInvites.map((invite) => {
                  const matchingAccounts = getInviteMatchingAccounts(invite);
                  const selectedAccountId = invitePlatformSelections[invite.id] || String(matchingAccounts[0]?.id || "");

                  return (
                    <div key={invite.id} className="inviteRowCard">
                      <div className="teamCardHeader">
                        <div>
                          <h3>{invite.team_name}</h3>
                          <p className="teamMetaLine">Owner: {invite.owner_username}</p>
                        </div>
                        <span className={`statusPill is-${invite.status}`}>{invite.status}</span>
                      </div>
                      <div className="teamPlatformList">
                        {(invite.supported_platforms || []).map((platformLink) => (
                          <span key={`invite-${invite.id}-${platformLink.platform}`} className="teamPlatformChip">
                            {platformLink.platform} - {platformLink.platform_username}
                          </span>
                        ))}
                      </div>
                      {invite.status === "pending" && matchingAccounts.length > 0 ? (
                        <div className="inviteAcceptRow">
                          {renderAccountChoiceGroup(
                            matchingAccounts,
                            selectedAccountId,
                            (nextId) => setInvitePlatformSelections((prev) => ({ ...prev, [invite.id]: nextId }))
                          )}
                          <div className="teamCardActions">
                            <button className="secondaryButton inlineActionButton" type="button" onClick={() => handleAcceptInvite(invite)} disabled={acceptingInviteId === invite.id}>
                              {acceptingInviteId === invite.id ? "Accepting..." : "Accept"}
                            </button>
                            <button className="secondaryButton inlineActionButton dangerButton" type="button" onClick={() => handleDeclineInvite(invite)} disabled={acceptingInviteId === invite.id}>
                              {acceptingInviteId === invite.id ? "Working..." : "Decline"}
                            </button>
                          </div>
                        </div>
                      ) : invite.status === "pending" ? (
                        <p className="mutedText">Connect a supported platform account before accepting this invite.</p>
                      ) : null}
                      {invite.expires_at && invite.status === "pending" && (
                        <p className="compactNote">Expires: {new Date(invite.expires_at).toLocaleString()}</p>
                      )}
                    </div>
                  );
                })}
                {incomingInvites.length === 0 && !invitePreview && (
                  <div className="teamListCard emptyTeamCard">
                    <h3>No pending invites</h3>
                    <p className="compactNote">Direct invites to your username or email will appear here.</p>
                  </div>
                )}
              </div>
            </div>
            )}
          </div>
        </div>
      )}
      </TeamPage>
  );
  };

  const renderTeamProfilePage = () => {
    if (!selectedTeamProfile?.team) return null;

    const { team, members = [] } = selectedTeamProfile;
    const projectedLeagueBand = getProjectedLeagueBand(team.team_rating);
    const returnView = activeMemberTeam?.id === team.id ? "my-team" : "discover";

    return (
      <div className="surfaceCard teamProfilePageCard">
        <div className="teamProfilePageTop">
          <div className="cardIntro">
            <span className="editorBadge">Team Page</span>
            <h3>{team.name}</h3>
            <p className="compactNote">
              {team.country || "Country pending"} | {team.member_count} players | site {team.competitive_rating || 1500}
              {team.team_rating ? ` | avg ${team.team_rating}` : " | avg pending"}
            </p>
          </div>
          <div className="teamCardActions">
            <button
              className="secondaryButton inlineActionButton"
              type="button"
              onClick={() => {
                setSelectedTeamProfile(null);
                navigateTeamView(returnView);
              }}
            >
              Back To Team Rooms
            </button>
          </div>
        </div>

        <div className="teamProfileHero">
          <span className="teamProfileBadge">{getTeamBadgeMark(team)}</span>
          <div className="teamProfileHeroText">
            <span className="editorBadge">Roster Identity</span>
            <h3>{team.name}</h3>
            <p className="compactNote">
              {team.bio || "This roster is live on Global Chess Arena and ready for the match pool."}
            </p>
          </div>
        </div>

        <div className="teamIdentityRow">
          <span className="teamIdentityChip">{projectedLeagueBand.name}</span>
          <span className="teamIdentityChip">{projectedLeagueBand.range}</span>
          <span className={`teamIdentityChip ${team.league_registered ? "isLeagueRegistered" : ""}`}>
            {team.league_registered ? "Registered for leagues" : "Not registered for leagues"}
          </span>
          <span className="teamIdentityChip">{formatMatchmakingPreference(team.matchmaking_preference)}</span>
        </div>

        <div className="teamProfileLayout">
          <div className="teamProfileSummaryCard">
            <div className="cardIntro">
              <h3>Team Details</h3>
              <p className="compactNote">
                Country, ladder band, supported platforms, and registration status stay attached to the room here.
              </p>
            </div>
            <p className="teamProfileBio">
              {team.bio || "This team has not added a bio yet."}
            </p>
            <div className="teamPlatformList">
              {(team.supported_platforms || []).map((platformLink) => (
                <span key={`profile-${team.id}-${platformLink.platform}`} className="teamPlatformChip">
                  {platformLink.platform} - {platformLink.platform_username}
                </span>
              ))}
            </div>
          </div>

          <div className="teamProfileRoster">
            <div className="cardIntro">
              <h3>Lineup</h3>
              <p className="compactNote">
                Every roster seat shows the platform handle, current rating, and presence state attached to the room.
              </p>
            </div>
            <div className="teamList">
              {members.map((member) => (
                <div key={`profile-member-${member.user_id}-${member.platform_account_id || member.username}`} className="teamProfileMember">
                  <div>
                    <div className="memberIdentityRow">
                      <strong>{member.username}</strong>
                      {renderPresencePill(member.is_online)}
                    </div>
                    <p className="compactNote">
                      {member.platform_username ? `${member.platform} - ${member.platform_username}` : "Platform seat pending"}
                    </p>
                  </div>
                  <div className="teamProfileMemberMeta">
                    <span>{member.rating_value ? `${member.rating_label || "rating"} ${member.rating_value}` : `team rating ${member.rating || "pending"}`}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderLeagueCard = (league) => {
    const isSelected = Number(selectedLeague?.id || 0) === Number(league.id);
    const isAdminLeague = Boolean(currentUser && (selectedLeague?.id === league.id
      ? canManageSelectedLeague
      : (league.admins || []).some((admin) => admin.user_id === currentUser.id)));

    return (
      <button
        key={`league-${league.id}`}
        type="button"
        className={`leagueListCard ${isSelected ? "isSelected" : ""}`}
        onClick={() => openLeague(league)}
      >
        <div className="leagueListCardTop">
          <div>
            <strong>{league.name}</strong>
            <p className="compactNote">
              {formatCompetitionMode(league.mode)} | {formatLeagueFormat(league.format)}
            </p>
          </div>
          <StatusBadge status={league.status} label={formatLeagueStatus(league.status)} />
        </div>
        <div className="leagueIdentityRow">
          <span className="teamIdentityChip">{formatLeagueEntryType(league.entry_type)}</span>
          {league.grade_band && <span className="teamIdentityChip">{league.grade_band}</span>}
          {isAdminLeague && <span className="teamIdentityChip isLeagueRegistered">Admin</span>}
        </div>
        <p className="compactNote">{league.description || "League details are live in the desk."}</p>
        <div className="leagueListMeta">
          <span>{league.current_team_count || 0}/{league.max_teams} registered</span>
          <span>{formatLeagueDateRange(league)}</span>
        </div>
      </button>
    );
  };

  const renderLeagueShowcaseCard = (league) => {
    const isAdminLeague = Boolean(currentUser && (league.admins || []).some((admin) => admin.user_id === currentUser.id));
    const availableSeats = Math.max(Number(league.max_teams || 0) - Number(league.current_team_count || 0), 0);

    return (
      <div key={`league-showcase-${league.id}`} className="surfaceCard leagueDetailCard leagueShowcaseCard">
        <div className="leagueHeroHeader">
          <div className="cardIntro">
            <span className="editorBadge">{league.status === "active" ? "Live League" : "Registration Open"}</span>
            <h3>{league.name}</h3>
            <p className="compactNote">
              {formatCompetitionMode(league.mode)} | {formatLeagueFormat(league.format)} | {formatLeagueEntryType(league.entry_type)}
            </p>
          </div>
          <div className="teamCardActions">
            {isAdminLeague && (
              <button
                className="secondaryButton inlineActionButton"
                type="button"
                onClick={() => startEditingLeague(league)}
              >
                Edit League
              </button>
            )}
            <button
              className="secondaryButton inlineActionButton"
              type="button"
              onClick={() => openLeague(league, "rules")}
            >
              Open Rules
            </button>
            <button
              className="primaryButton inlineActionButton"
              type="button"
              onClick={() => openLeague(league, "overview")}
            >
              Open Competition
            </button>
          </div>
        </div>
        <p className="leagueDescriptionText">
          {league.description || "This competition room is live on Global Chess Arena and ready for teams to register or follow the bracket."}
        </p>
        <div className="teamIdentityRow">
          <span className={`teamIdentityChip ${league.status === "active" ? "isLeagueRegistered" : ""}`}>
            {formatLeagueStatus(league.status)}
          </span>
          <span className="teamIdentityChip">{league.current_team_count || 0}/{league.max_teams} teams</span>
          {league.grade_band && <span className="teamIdentityChip">{league.grade_band}</span>}
          {isAdminLeague && <span className="teamIdentityChip isLeagueRegistered">Admin</span>}
        </div>
        <div className="leagueDetailStats">
          <div className="leagueDetailStat">
            <span>Registered Teams</span>
            <strong>{league.current_team_count || 0}</strong>
            <small>Teams already locked into the field</small>
          </div>
          <div className="leagueDetailStat">
            <span>Open Seats</span>
            <strong>{availableSeats}</strong>
            <small>Slots remaining before the field closes</small>
          </div>
          <div className="leagueDetailStat">
            <span>{league.status === "active" ? "Competition" : "Window"}</span>
            <strong>{league.status === "active" ? "In Progress" : "Registration"}</strong>
            <small>{formatLeagueDateRange(league)}</small>
          </div>
          <div className="leagueDetailStat">
            <span>Clock / Policy</span>
            <strong>{league.default_time_control || "Clock pending"}</strong>
            <small>{formatMatchmakingPreference(league.default_match_platform)}</small>
          </div>
        </div>
      </div>
    );
  };

  const renderLeagueBracket = () => (
    <Bracket
      rounds={selectedLeagueRounds}
      canManage={canManageSelectedLeague && !leagueLoading}
      onStartRound={(round) => handleSetLeagueRoundStatus(round.id, "active")}
      onCloseRound={(round) => handleSetLeagueRoundStatus(round.id, "completed")}
      onResolveMatch={(match, winnerTeamId) => handleResolveLeagueMatch(match.id, winnerTeamId)}
      onOpenMatch={(match) => {
        fetchMatchDetail(match.id);
        handleNavigate("play");
      }}
      emptyTitle="No bracket generated yet"
      emptyNote={
        selectedLeague?.format === "round_robin"
          ? "Round-robin scheduling is the next league format coming online. Use single elimination for bracket play right now."
          : "Register teams, assign seeds if needed, then generate the single-elimination bracket."
      }
    />
  );

  const renderLeagueEditorPanel = () => {
    if (!currentUser) return null;

    return (
      <div className="surfaceCard compactCard">
        <div className="cardIntro">
          <h3>{editingLeagueId ? "Edit League" : "Create League"}</h3>
          <p className="compactNote">Keep the competition setup tight here, then jump back to overview once the league shell is ready for teams.</p>
        </div>
        <form className="leagueForm" onSubmit={editingLeagueId ? handleUpdateLeague : handleCreateLeague}>
          <div className="leagueIdentityGrid">
            <label className="leagueField">
              <span>League Name</span>
              <input type="text" name="name" placeholder="Global Chess Arena Open" value={leagueForm.name} onChange={handleLeagueFormChange} required />
            </label>
            <label className="leagueField">
              <span>Season Label</span>
              <input type="text" name="season_label" placeholder="Spring 2026" value={leagueForm.season_label} onChange={handleLeagueFormChange} />
            </label>
          </div>
          <label className="leagueField">
            <span>Description</span>
            <textarea name="description" placeholder="Short public summary for teams and players." value={leagueForm.description} onChange={handleLeagueFormChange} rows={3} />
          </label>
          <div className="leagueFormGrid">
            <label className="leagueField">
              <span>Mode</span>
              <select name="mode" value={leagueForm.mode} onChange={handleLeagueFormChange}>
                {LEAGUE_MODE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="leagueField">
              <span>Format</span>
              <select name="format" value={leagueForm.format} onChange={handleLeagueFormChange}>
                {LEAGUE_FORMAT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="leagueField">
              <span>Entry Type</span>
              <select name="entry_type" value={leagueForm.entry_type} onChange={handleLeagueFormChange}>
                {LEAGUE_ENTRY_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="leagueField">
              <span>Status</span>
              <select name="status" value={leagueForm.status} onChange={handleLeagueFormChange}>
                {["draft", "registration_open", "registration_closed", "active", "completed", "archived"].map((status) => (
                  <option key={status} value={status}>{formatLeagueStatus(status)}</option>
                ))}
              </select>
            </label>
            <label className="leagueField">
              <span>Max Teams</span>
              <select name="max_teams" value={leagueForm.max_teams} onChange={handleLeagueFormChange}>
                {[4, 8, 16, 32].map((size) => (
                  <option key={size} value={size}>{size} teams</option>
                ))}
              </select>
            </label>
            <label className="leagueField">
              <span>Platform Policy</span>
              <select name="default_match_platform" value={leagueForm.default_match_platform} onChange={handleLeagueFormChange}>
                <option value="allow_all">Allow all</option>
                <option value="lichess">Lichess only</option>
                <option value="chess.com">Chess.com only</option>
              </select>
            </label>
            <label className="leagueField">
              <span>Default Clock</span>
              <select name="default_time_control" value={leagueForm.default_time_control} onChange={handleLeagueFormChange}>
                {MATCH_TIME_CONTROL_OPTIONS.map((timeControl) => (
                  <option key={timeControl} value={timeControl}>{timeControl}</option>
                ))}
              </select>
            </label>
            <label className="leagueField">
              <span>Match Window</span>
              <select name="default_match_duration_days" value={leagueForm.default_match_duration_days} onChange={handleLeagueFormChange}>
                {MATCH_DURATION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="leagueField">
              <span>Registration Opens</span>
              <input type="datetime-local" name="registration_open_at" value={leagueForm.registration_open_at} onChange={handleLeagueFormChange} />
            </label>
            <label className="leagueField">
              <span>Registration Closes</span>
              <input type="datetime-local" name="registration_close_at" value={leagueForm.registration_close_at} onChange={handleLeagueFormChange} />
            </label>
            {leagueForm.mode === "scholastic" && (
              <label className="leagueField">
                <span>Grade Band</span>
                <select name="grade_band" value={leagueForm.grade_band} onChange={handleLeagueFormChange}>
                  <option value="">Select grade band</option>
                  {GRADE_BAND_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <label className="leagueField">
            <span>Published Rules</span>
            <textarea
              name="rules_text"
              placeholder="Published rules"
              value={leagueForm.rules_text}
              onChange={handleLeagueFormChange}
              rows={3}
            />
          </label>
          <label className="leagueField">
            <span>Published Tiebreak Rules</span>
            <textarea
              name="tiebreak_rules_text"
              placeholder="Published tiebreak rules"
              value={leagueForm.tiebreak_rules_text}
              onChange={handleLeagueFormChange}
              rows={3}
            />
          </label>
          <div className="leagueCheckboxGrid">
            <label className="checkboxRow">
              <input type="checkbox" name="is_public" checked={leagueForm.is_public} onChange={handleLeagueFormChange} />
              <span>Public league page</span>
            </label>
            <label className="checkboxRow">
              <input type="checkbox" name="default_rated" checked={leagueForm.default_rated} onChange={handleLeagueFormChange} />
              <span>Default rated matches</span>
            </label>
            <label className="checkboxRow">
              <input type="checkbox" name="requires_verification" checked={leagueForm.requires_verification} onChange={handleLeagueFormChange} />
              <span>Require verification</span>
            </label>
            <label className="checkboxRow">
              <input type="checkbox" name="allow_cross_mode_registration" checked={leagueForm.allow_cross_mode_registration} onChange={handleLeagueFormChange} />
              <span>Allow cross-mode teams</span>
            </label>
          </div>
          <div className="teamCardActions">
            <button className="primaryButton inlineActionButton" type="submit" disabled={leagueLoading}>
              {leagueLoading ? "Saving..." : editingLeagueId ? "Save League" : "Create League"}
            </button>
            <button
              className="secondaryButton inlineActionButton"
              type="button"
              onClick={() => {
                resetLeagueForm();
                navigateLeagueSubpage("overview");
              }}
              disabled={leagueLoading}
            >
              {editingLeagueId ? "Cancel" : "Back To Overview"}
            </button>
          </div>
        </form>
      </div>
    );
  };

  const renderLeagueOverviewPanel = () => {
    if (!selectedLeague) {
      return (
        <div className="leagueDetailCard">
          <div className="surfaceCard compactCard">
            <div className="matchEmptyState">
              <strong>No league selected</strong>
              <span>Open a competition from the left rail or create a new one to start registration.</span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="surfaceCard leagueDetailCard">
        <div className="leagueHeroHeader">
          <div className="cardIntro">
            <span className="editorBadge">League</span>
            <h3>{selectedLeague.name}</h3>
            <p className="compactNote">
              {formatCompetitionMode(selectedLeague.mode)} | {formatLeagueFormat(selectedLeague.format)} | {formatLeagueEntryType(selectedLeague.entry_type)}
            </p>
          </div>
          <div className="teamCardActions">
            {canManageSelectedLeague && (
              <button
                className="secondaryButton inlineActionButton"
                type="button"
                onClick={() => startEditingLeague(selectedLeague)}
              >
                Edit League
              </button>
            )}
            <button
              className="secondaryButton inlineActionButton"
              type="button"
              onClick={() => navigateLeagueSubpage("rules")}
            >
              Open Rules
            </button>
            <button className="secondaryButton inlineActionButton" type="button" onClick={handleLoadLeaguesCenter}>
              Refresh
            </button>
          </div>
        </div>
        <p className="leagueDescriptionText">
          {selectedLeague.description || "Rules, bracket progression, and official team match flow all live inside this competition room."}
        </p>
        <div className="teamIdentityRow">
          <span className="teamIdentityChip">{formatLeagueStatus(selectedLeague.status)}</span>
          <span className="teamIdentityChip">{registeredLeagueRegistrations.length}/{selectedLeague.max_teams} teams</span>
          {selectedLeague.grade_band && <span className="teamIdentityChip">{selectedLeague.grade_band}</span>}
          {selectedLeague.requires_verification && <span className="teamIdentityChip">Verification required</span>}
          {selectedLeague.champion_team_name && <span className="teamIdentityChip isLeagueRegistered">Champion: {selectedLeague.champion_team_name}</span>}
        </div>
        <div className="leagueDetailStats">
          <div className="leagueDetailStat">
            <span>Registered Teams</span>
            <strong>{registeredLeagueRegistrations.length}</strong>
            <small>Locked into this competition room</small>
          </div>
          <div className="leagueDetailStat">
            <span>Open Seats</span>
            <strong>{Math.max(Number(selectedLeague.max_teams || 0) - registeredLeagueRegistrations.length, 0)}</strong>
            <small>Remaining team slots</small>
          </div>
          <div className="leagueDetailStat">
            <span>{selectedLeague.format === "single_elimination" ? "Rounds" : "Format"}</span>
            <strong>{selectedLeague.format === "single_elimination" ? selectedLeagueRounds.length : "Round Robin"}</strong>
            <small>
              {selectedLeague.format === "single_elimination"
                ? `${selectedLeagueMatches.length} bracket matches`
                : "Pairing engine comes next for season play"}
            </small>
          </div>
          <div className="leagueDetailStat">
            <span>Window</span>
            <strong>{formatLeagueDateRange(selectedLeague)}</strong>
            <small>{selectedLeague.default_time_control} default clock</small>
          </div>
        </div>
        <div className="sectionGrid twoColumn leaguePrimaryGrid">
          <div className="surfaceCard compactCard">
            <div className="cardIntro">
              <h3>Your Team Status</h3>
              <p className="compactNote">Captains, coaches, and creators register eligible teams instantly when they meet the published rules.</p>
            </div>
            {selectedLeagueParticipationEntry ? (
              <div className="infoList">
                <div>Current team entry: Registered</div>
                <div>{selectedLeagueParticipationEntry.seed_number ? `Seed #${selectedLeagueParticipationEntry.seed_number}` : "Seed not assigned yet"}</div>
              </div>
            ) : availableOwnedTeamsForSelectedLeague.length > 0 ? (
              <div className="leagueRegistrationList">
                {availableOwnedTeamsForSelectedLeague.map((team) => (
                  <div key={`league-register-${team.id}`} className="leagueRegistrationCard">
                    <div>
                      <strong>{team.name}</strong>
                      <p className="compactNote">
                        {formatCompetitionMode(team.competition_mode)} | site {team.competitive_rating || 1500} | {formatMatchmakingPreference(team.matchmaking_preference)}
                      </p>
                    </div>
                    <button
                      className="primaryButton inlineActionButton"
                      type="button"
                      onClick={() => handleRegisterOwnedTeamForLeague(team.id)}
                      disabled={leagueLoading}
                    >
                      Register Team
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="matchEmptyState">
                <strong>No eligible owner-controlled teams</strong>
                <span>Create or edit a team so its mode and platform rules fit this league.</span>
              </div>
            )}
          </div>
          <div className="surfaceCard compactCard">
            <div className="cardIntro">
              <h3>Competition Notes</h3>
              <p className="compactNote">Quick scan of how this room is configured before teams start checking in.</p>
            </div>
            <div className="infoList">
              <div>Platform policy: {formatMatchmakingPreference(selectedLeague.default_match_platform)}</div>
              <div>Default clock: {selectedLeague.default_time_control}</div>
              <div>Match window: {selectedLeague.default_match_duration_days} day{Number(selectedLeague.default_match_duration_days) === 1 ? "" : "s"}</div>
              <div>{selectedLeague.is_public !== false ? "Public league page enabled" : "Private league page"}</div>
            </div>
          </div>
        </div>
        <div className="surfaceCard compactCard">
          <div className="cardIntro">
            <h3>{selectedLeague.format === "single_elimination" ? "Bracket" : "Season Structure"}</h3>
            <p className="compactNote">
              {selectedLeague.format === "single_elimination"
                ? "Rounds unlock in order. Completed winners advance automatically as official team matches resolve."
                : "Round-robin leagues keep the same league shell, rules page, and registration flow while scheduling support is added next."}
            </p>
          </div>
          {selectedLeague.format === "single_elimination" ? renderLeagueBracket() : (
            <div className="matchEmptyState">
              <strong>Round-robin scheduling is next</strong>
              <span>This league can be configured today, but bracket generation only applies to single-elimination rooms right now.</span>
            </div>
          )}
        </div>
        <div className="surfaceCard compactCard">
          <div className="cardIntro">
            <h3>Registrations</h3>
            <p className="compactNote">Teams that join appear here instantly. Seeds are only needed for single-elimination brackets.</p>
          </div>
          <div className="leagueRegistrationList">
            {registeredLeagueRegistrations.map((registration) => (
              <div key={`league-entry-${registration.id}`} className="leagueRegistrationCard">
                <div>
                  <button className="textActionButton teamMetaLink" type="button" onClick={() => handleOpenTeamProfile({ id: registration.team_id })}>
                    {registration.team_name}
                  </button>
                  <p className="compactNote">
                    {formatCompetitionMode(registration.competition_mode)} | {registration.member_count} players | site {registration.competitive_rating || 1500}
                  </p>
                </div>
                <div className="leagueRegistrationActions">
                  <span className="statusPill is-approved">Registered</span>
                  {selectedLeague.format === "single_elimination" && (
                    <input
                      type="number"
                      min="1"
                      className="leagueSeedInput"
                      value={leagueSeedDrafts[registration.id] || ""}
                      onChange={(event) =>
                        setLeagueSeedDrafts((prev) => ({ ...prev, [registration.id]: event.target.value }))
                      }
                      placeholder="Seed"
                    />
                  )}
                </div>
              </div>
            ))}
            {registeredLeagueRegistrations.length === 0 && (
              <div className="matchEmptyState">
                <strong>No teams registered yet</strong>
                <span>Open registration and captains will be able to lock a team into the field immediately.</span>
              </div>
            )}
          </div>
          {canManageSelectedLeague && selectedLeague.format === "single_elimination" && approvedLeagueRegistrations.length > 0 && (
            <div className="teamCardActions leagueAdminFooter">
              <button className="secondaryButton inlineActionButton" type="button" onClick={handleSaveLeagueSeeds} disabled={leagueLoading}>
                Save Seeds
              </button>
              <button className="primaryButton inlineActionButton" type="button" onClick={handleGenerateLeagueBracket} disabled={leagueLoading}>
                Generate Bracket
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderLeagueRulesPanel = () => {
    if (!selectedLeague) {
      return (
        <div className="surfaceCard leagueDetailCard">
          <div className="matchEmptyState">
            <strong>No league selected</strong>
            <span>Select a competition to read its published rules, tiebreak order, and team eligibility notes.</span>
          </div>
        </div>
      );
    }

    return (
      <div className="surfaceCard leagueDetailCard">
        <div className="leagueHeroHeader">
          <div className="cardIntro">
            <span className="editorBadge">Rules</span>
            <h3>{selectedLeague.name}</h3>
            <p className="compactNote">
              Published rules, eligibility, tiebreaks, and match settings for this competition room.
            </p>
          </div>
          <div className="teamCardActions">
            {canManageSelectedLeague && (
              <button
                className="secondaryButton inlineActionButton"
                type="button"
                onClick={() => startEditingLeague(selectedLeague)}
              >
                Edit Rules
              </button>
            )}
            <button className="secondaryButton inlineActionButton" type="button" onClick={() => navigateLeagueSubpage("overview")}>
              Back To Overview
            </button>
          </div>
        </div>
        <div className="leagueRulesGrid">
          <div className="surfaceCard compactCard">
            <div className="cardIntro">
              <h3>Published Rules</h3>
              <p className="compactNote">Show this page to teams before they register.</p>
            </div>
            <div className="leagueRulesBody">
              {selectedLeague.rules_text || "League rules have not been published yet. Add the format, lineup policy, scheduling standards, and dispute timing here."}
            </div>
          </div>
          <div className="surfaceCard compactCard">
            <div className="cardIntro">
              <h3>Tiebreak Policy</h3>
              <p className="compactNote">Simple and public beats surprising. Publish it before the field locks.</p>
            </div>
            <div className="leagueRulesBody">
              {selectedLeague.tiebreak_rules_text || "Default order: total board points, then top-board result, then second-board result, then admin ruling if still unresolved."}
            </div>
          </div>
          <div className="surfaceCard compactCard">
            <div className="cardIntro">
              <h3>Eligibility</h3>
              <p className="compactNote">Teams can only join when their competition mode and published restrictions match this room.</p>
            </div>
            <div className="infoList">
              <div>Mode: {formatCompetitionMode(selectedLeague.mode)}</div>
              <div>Format: {formatLeagueFormat(selectedLeague.format)}</div>
              <div>Entry type: {formatLeagueEntryType(selectedLeague.entry_type)}</div>
              <div>{selectedLeague.grade_band ? `Grade band: ${selectedLeague.grade_band}` : "No grade-band restriction"}</div>
              <div>{selectedLeague.requires_verification ? "Verification required before registration" : "No extra verification gate"}</div>
            </div>
          </div>
          <div className="surfaceCard compactCard">
            <div className="cardIntro">
              <h3>Match Policy</h3>
              <p className="compactNote">Every league match still runs through the external chess sites and the platform match desk.</p>
            </div>
            <div className="infoList">
              <div>Platform policy: {formatMatchmakingPreference(selectedLeague.default_match_platform)}</div>
              <div>Default clock: {selectedLeague.default_time_control}</div>
              <div>Match window: {selectedLeague.default_match_duration_days} day{Number(selectedLeague.default_match_duration_days) === 1 ? "" : "s"}</div>
              <div>{selectedLeague.default_rated ? "Rated by default" : "Casual by default"}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderLeagueOverviewLanding = (featuredLeagues) => {
    if (featuredLeagues.length === 0) {
      return (
        <div className="surfaceCard leagueDetailCard">
          <div className="matchEmptyState">
            <strong>No open competitions right now</strong>
            <span>The league home page highlights rooms that are active or currently open for registration.</span>
          </div>
        </div>
      );
    }

    return renderLeagueShowcaseCard(featuredLeagues[0]);
  };

  const renderLeagueMainPanel = (featuredLeagues) => {
    if (leagueLoading && !selectedLeague && leagueSubpage !== "edit") {
      return (
        <div className="surfaceCard leagueDetailCard">
          <div className="matchEmptyState">
            <strong>Loading League Desk</strong>
            <span>Pulling registration, rules, and round data now.</span>
          </div>
        </div>
      );
    }

    if (leagueSubpage === "edit") {
      return (
        <div className="leagueDetailCard">
          {canManageSelectedLeague || !selectedLeague ? renderLeagueEditorPanel() : (
            <div className="surfaceCard compactCard">
              <div className="matchEmptyState">
                <strong>League editing stays with league admins</strong>
                <span>Open the overview or rules page if you only need to read the competition room.</span>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (leagueSubpage === "overview") {
      return selectedLeague ? renderLeagueOverviewPanel() : renderLeagueOverviewLanding(featuredLeagues);
    }

    if (leagueSubpage === "rules") {
      return renderLeagueRulesPanel();
    }

    return renderLeagueOverviewPanel();
  };

  const renderLeagueTabs = (leagueTabs) => (
    <div className="workspaceTabs leagueWorkspaceTabs" role="tablist" aria-label="League desk views">
      {leagueTabs.map((tab) => (
        <button
          key={tab.id}
          className={`workspaceTabButton ${leagueSubpage === tab.id ? "isActive" : ""}`}
          type="button"
          disabled={tab.disabled}
          onClick={() => {
            if (tab.id === "edit") {
              if (selectedLeague && canManageSelectedLeague) {
                startEditingLeague(selectedLeague);
              } else {
                resetLeagueForm();
                navigateLeagueSubpage("edit");
              }
              return;
            }
            navigateLeagueSubpage(tab.id);
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );

  const renderLeagueSidebar = (sidebarLeagues, subpage) => (
    <div className="surfaceCard leagueSidebarCard">
      <div className="cardIntro">
        <h3>{subpage === "overview" ? "Open / Active Leagues" : "Competitions"}</h3>
        <p className="compactNote">
          {subpage === "overview"
            ? "This home view stays focused on competition rooms that are live now or currently taking teams."
            : "Open a league to inspect published rules and the live bracket path behind each round."}
        </p>
      </div>
      {currentUser && (
        <div className="leagueSidebarActions">
          <button
            className="primaryButton inlineActionButton"
            type="button"
            onClick={() => {
              resetLeagueForm();
              navigateLeagueSubpage("edit");
            }}
          >
            Create League
          </button>
          <button className="secondaryButton inlineActionButton" type="button" onClick={handleLoadLeaguesCenter}>
            Refresh Desk
          </button>
        </div>
      )}
      <div className="leagueList">
        {sidebarLeagues.map((league) => renderLeagueCard(league))}
        {sidebarLeagues.length === 0 && (
          <div className="matchEmptyState">
            <strong>{subpage === "overview" ? "No open competitions yet" : "No leagues yet"}</strong>
            <span>
              {subpage === "overview"
                ? "Once a league is active or opens registration, it will show up here."
                : "Create the first competition room to open registration and start building the field."}
            </span>
          </div>
        )}
      </div>
      {currentUser && activeMemberTeam && (
        <div className="surfaceCard compactCard leagueParticipationCard">
          <div className="cardIntro">
            <h3>{activeMemberTeam.name}</h3>
            <p className="compactNote">Current team participation across the league system.</p>
          </div>
          {leagueTeamParticipation.length > 0 ? (
            <div className="infoList">
              {leagueTeamParticipation.map((entry) => (
                <div key={`team-league-${entry.id}`}>
                  {entry.name} | Registered
                  {entry.seed_number ? ` | Seed #${entry.seed_number}` : ""}
                </div>
              ))}
            </div>
          ) : (
            <div className="matchEmptyState">
              <strong>No league entries yet</strong>
              <span>Register this team for a competition to track it here.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderLeagueGate = () => (
    !currentUser ? (
      <div className="surfaceCard teamsGateCard">
        <h3>Sign in to run leagues.</h3>
        <p className="compactNote">
          Visitors can browse public competitions and rule pages. League creation, team registration, seeding, and round control stay behind the signed-in desk.
        </p>
        <button className="primaryButton" type="button" onClick={() => handleNavigate("account")}>
          Go To Account
        </button>
      </div>
    ) : null
  );

  const renderLeaguesSection = () => {
    const visibleLeagues = currentUser ? leagues : leagues.filter((league) => league.is_public !== false);
    const featuredLeagues = visibleLeagues
      .filter((league) => isLeagueOpenOrActive(league))
      .sort(compareLeagueDeskPriority);
    const sidebarLeagues = (leagueSubpage === "overview" ? featuredLeagues : visibleLeagues)
      .slice()
      .sort(compareLeagueDeskPriority);
    const leagueTabs = [
      { id: "overview", label: "Overview", disabled: false },
      { id: "rules", label: "Rules", disabled: !selectedLeague },
      { id: "edit", label: editingLeagueId ? "Edit League" : "Create League", disabled: !currentUser },
    ];

    const sharedProps = {
      metricsNode: null,
      gateNode: renderLeagueGate(),
      message: leagueMessage,
      error: leagueError,
      tabsNode: renderLeagueTabs(leagueTabs),
      railNode: renderLeagueSidebar(sidebarLeagues, leagueSubpage),
      mainNode: renderLeagueMainPanel(featuredLeagues),
      selectedLeague,
      featuredLeagues,
    };

    if (leagueSubpage === "edit") {
      return <LeagueEditPage {...sharedProps} />;
    }

    if (leagueSubpage === "rules") {
      return <LeagueRulesPage {...sharedProps} />;
    }

    return <LeaguePage {...sharedProps} />;
  };

  const renderPlaySection = () => {
    const matchSidebarNode = (
      <div className="surfaceCard matchDeskSidebar">
          <div className="cardIntro">
            <h3>Matches</h3>
            <p className="compactNote">
              Open a match from the list, keep the score visible, and move between rounds without losing the thread.
            </p>
          </div>
          {currentUser && ownerManagedTeams.length === 0 && (
            <div className="matchEmptyState">
              <strong>Captain controls are locked.</strong>
              <span>Only team owners can send match requests. Open Team Rooms to create a team or take ownership of one.</span>
            </div>
          )}
          {currentUser && showMatchComposer && (
            <form className="signupForm matchComposerForm" onSubmit={handleCreateMatch}>
              <div className="matchComposerIntro">
                <div>
                  <strong>Challenge setup</strong>
                  <span>Pick your room, then either search for a target or let live matchmaking pull the closest team from the active pool.</span>
                </div>
                {selectedTeamB && (
                  <span className="statusPill is-in-progress">
                    Match ready: {selectedTeamA?.name} vs {selectedTeamB.name}
                  </span>
                )}
              </div>
              <div className="fieldBlock compactFieldBlock">
                <div className="fieldBlockHeader">
                  <strong>From Team</strong>
                  <span>Only owner-controlled teams can send match requests.</span>
                </div>
                <div className="matchTeamChoiceRail">
                  {ownerManagedTeams.map((team) => {
                    const isSelected = selectedTeamA?.id === team.id;
                    return (
                      <button
                        key={`challenge-team-${team.id}`}
                        type="button"
                        className={`matchTeamChoiceCard ${isSelected ? "isSelected" : ""}`}
                        onClick={() => {
                          setMatchForm((prev) => ({
                            ...prev,
                            team_a_id: String(team.id),
                            team_b_id: "",
                            platform: "",
                          }));
                          setMatchOpponentQuery("");
                        }}
                      >
                        <div>
                          <strong>{team.name}</strong>
                          <small>{team.member_count} players | site {team.competitive_rating || 1500}</small>
                        </div>
                        <span className="teamIdentityChip">{formatMatchmakingPreference(team.matchmaking_preference)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="matchModeSwitch">
                <button
                  type="button"
                  className={`matchModeCard ${matchComposerMode === "search" ? "isSelected" : ""}`}
                  onClick={() => {
                    setMatchComposerMode("search");
                    setMatchForm((prev) => ({ ...prev, platform: "" }));
                  }}
                >
                  <span className="matchModeLabel">Search for team</span>
                  <strong>Direct challenge</strong>
                  <small>Type the opponent room and send a request to that captain.</small>
                </button>
                <button
                  type="button"
                  className={`matchModeCard ${matchComposerMode === "live" ? "isSelected" : ""}`}
                  onClick={() => {
                    setMatchComposerMode("live");
                    setMatchForm((prev) => ({ ...prev, platform: "" }));
                  }}
                >
                  <span className="matchModeLabel">Live Matchmaking</span>
                  <strong>Rated pool pairing</strong>
                  <small>Pull the closest compatible team by site rating, platform overlap, and live presence.</small>
                </button>
              </div>
              {matchComposerMode === "search" ? (
                <div className="matchOpponentPicker">
                  <div className="fieldBlock compactFieldBlock">
                    <div className="fieldBlockHeader">
                      <strong>Search For Team</strong>
                      <span>Enter the room name and choose the captain you want to challenge.</span>
                    </div>
                    <input
                      type="text"
                      placeholder="Search for team"
                      value={matchOpponentQuery}
                      onChange={(event) => setMatchOpponentQuery(event.target.value)}
                    />
                  </div>
                  <div className="matchSearchResults">
                    {searchedOpponentTeams.slice(0, 5).map((team) => {
                      const isSelected = selectedTeamB?.id === team.id;
                      return (
                        <button
                          key={`match-search-${team.id}`}
                          type="button"
                          className={`matchOpponentCard ${isSelected ? "isSelected" : ""}`}
                          onClick={() =>
                            setMatchForm((prev) => ({
                              ...prev,
                              team_b_id: String(team.id),
                              platform: "",
                            }))
                          }
                        >
                          <div className="matchOpponentHeader">
                            <strong>{team.name}</strong>
                            <span className="statusPill is-waiting">{teamPresenceCounts[team.id] || 0} online</span>
                          </div>
                          <p className="compactNote">
                            {team.country || "Country pending"} | site {team.competitive_rating || 1500} | {team.member_count} players
                          </p>
                          <div className="teamPlatformList">
                            {getMatchPoolPlatforms(team).map((platformId) => (
                              <span key={`search-platform-${team.id}-${platformId}`} className="teamPlatformChip">
                                {platformId === "allow_all" ? "mixed" : platformId}
                              </span>
                            ))}
                          </div>
                        </button>
                      );
                    })}
                    {searchedOpponentTeams.length === 0 && (
                      <div className="matchEmptyState">
                        <strong>No matching teams</strong>
                        <span>Try another name or switch to live matchmaking.</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="liveMatchmakingCard">
                  <div className="fieldBlockHeader">
                    <strong>Live Matchmaking</strong>
                    <span>The desk is ranking active teams by site rating, platform fit, and who is online right now.</span>
                  </div>
                  {suggestedLiveOpponent ? (
                    <div className="matchOpponentCard isSelected">
                      <div className="matchOpponentHeader">
                        <strong>{suggestedLiveOpponent.name}</strong>
                        <span className="statusPill is-in-progress">
                          {teamPresenceCounts[suggestedLiveOpponent.id] || 0} online
                        </span>
                      </div>
                      <p className="compactNote">
                        Rating gap {Math.abs((suggestedLiveOpponent.competitive_rating || 1500) - (selectedTeamA?.competitive_rating || 1500))} |
                        {" "}site {suggestedLiveOpponent.competitive_rating || 1500} | {suggestedLiveOpponent.member_count} players
                      </p>
                      <div className="teamPlatformList">
                        {getMatchPoolPlatforms(suggestedLiveOpponent).map((platformId) => (
                          <span key={`live-platform-${suggestedLiveOpponent.id}-${platformId}`} className="teamPlatformChip">
                            {platformId === "allow_all" ? "mixed" : platformId}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="matchEmptyState">
                      <strong>No live team is ready yet.</strong>
                      <span>Stay on this mode and refresh the desk when another compatible roster comes online.</span>
                    </div>
                  )}
                </div>
              )}
            <select name="platform" value={pendingMatchPlatform} onChange={handleMatchChange} required disabled={!currentUser || matchPlatformOptions.length === 0}>
              <option value="">Choose Match Platform</option>
              {matchPlatformOptions.map((platformId) => (
                <option key={platformId} value={platformId}>
                  {platformId === "allow_all" ? "Allow all (mixed boards)" : platformId}
                </option>
              ))}
            </select>
            <div className="matchSettingsGrid">
              {renderMatchSettingSelector({
                id: "match-time-control",
                label: "Time Control",
                valueLabel: getMatchTimeControlLabel(matchForm.time_control),
                active: activeMatchSettingPanel === "time-control",
                disabled: !currentUser,
                onToggle: () => setActiveMatchSettingPanel((current) => (current === "time-control" ? null : "time-control")),
                children: (
                  <div className="matchSettingGroups">
                    {MATCH_TIME_CONTROL_GROUPS.map((group) => (
                      <div key={group.label} className="matchSettingGroup">
                        <span className="matchSettingGroupLabel">{group.label}</span>
                        <div className="matchSettingOptions">
                          {group.options.map((timeControl) => (
                            <button
                              key={timeControl}
                              type="button"
                              className={`matchSettingOption ${matchForm.time_control === timeControl ? "isSelected" : ""}`}
                              disabled={!currentUser}
                              onClick={() => {
                                setMatchForm((prev) => ({ ...prev, time_control: timeControl }));
                                setActiveMatchSettingPanel(null);
                              }}
                            >
                              {timeControl}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ),
              })}
              {renderMatchSettingSelector({
                id: "match-duration",
                label: "Match Duration",
                valueLabel: getMatchDurationLabel(matchForm.match_duration_days),
                active: activeMatchSettingPanel === "match-duration",
                disabled: !currentUser,
                onToggle: () => setActiveMatchSettingPanel((current) => (current === "match-duration" ? null : "match-duration")),
                children: (
                  <div className="matchSettingGroups">
                    <div className="matchSettingGroup">
                      <span className="matchSettingGroupLabel">Challenge Window</span>
                      <div className="matchSettingOptions">
                        {MATCH_DURATION_OPTIONS.map((durationOption) => (
                          <button
                            key={durationOption.value}
                            type="button"
                            className={`matchSettingOption ${Number(matchForm.match_duration_days) === Number(durationOption.value) ? "isSelected" : ""}`}
                            disabled={!currentUser}
                            onClick={() => {
                              setMatchForm((prev) => ({ ...prev, match_duration_days: durationOption.value }));
                              setActiveMatchSettingPanel(null);
                            }}
                          >
                            {durationOption.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ),
              })}
            </div>
            <label className="checkboxRow">
              <input type="checkbox" name="rated" checked={matchForm.rated} onChange={handleMatchChange} disabled={!currentUser} />
              <span>Rated match</span>
            </label>
            {selectedTeamA && selectedTeamB && matchPlatformOptions.length === 0 && (
              <p className="compactNote">
                These teams do not currently share an allowed matchmaking pool.
              </p>
            )}
            {selectedTeamA && selectedTeamB && !canCurrentUserRequestMatch && currentUser && (
              <p className="compactNote">
                Only the owner of Team A or Team B can send this match request.
              </p>
            )}
            {allowMixedMatch && (
              <p className="compactNote">
                Both teams are open to the full pool, so `Allow all` will assign each board to the best available platform as the pairing is built.
              </p>
            )}
            {chessComRatedDraft && (
              <p className="compactNote">
                Any match that includes Chess.com boards is currently casual-only, so the assigned White and Black seats stay correct.
              </p>
            )}
            <p className="compactNote">
              {matchForm.match_duration_days
                ? `Match window: ${getMatchDurationLabel(matchForm.match_duration_days)} from acceptance. After the deadline, board launch actions close automatically.`
                : "Select how long the match stays open after both owners accept. Once that window closes, launch actions shut off automatically."}
            </p>
            <button className="primaryButton signupButton" type="submit" disabled={matchLoading || !currentUser || !pendingMatchPlatform || !canCurrentUserRequestMatch || !matchForm.time_control || !matchForm.match_duration_days}>
              {matchLoading ? "Sending Match Request..." : "Send Match Request"}
            </button>
            </form>
          )}
          {matchMessage && <p className="successMessage">{matchMessage}</p>}
          {matchError && <p className="errorMessage">{matchError}</p>}
          {matches.length > 0 ? (
            <div className="matchList">
              {matches.map((match) => (
                <button key={match.id} type="button" className="matchSummaryCard" onClick={() => fetchMatchDetail(match.id)}>
                  <div className="matchSummaryTop">
                    <strong>{match.team_a_name} vs {match.team_b_name}</strong>
                    <span className={`statusPill is-${match.status}`}>{match.status.replace(/_/g, " ")}</span>
                  </div>
                  <span>{formatMatchMeta(match)}</span>
                  <span>Score: {match.team_a_score} - {match.team_b_score}</span>
                  <span>{formatMatchDeadline(match)}</span>
                  <span>{match.platform === "mixed" ? "Mixed platform pairing" : `${match.platform} match`}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="matchEmptyState">
              <strong>No matches yet</strong>
              <span>Created matches and live requests will show up here first.</span>
            </div>
          )}
      </div>
    );

    const matchMainNode = selectedMatch ? (
      <div className="selectedMatchPanel matchDeskStage">
        <div className="selectedMatchHeader">
          <div>
            <h3>{selectedMatch.team_a_name} vs {selectedMatch.team_b_name}</h3>
            <p>{formatMatchMeta(selectedMatch)} | {selectedMatch.status.replace(/_/g, " ")}</p>
            <p className="compactNote">{formatMatchDeadline(selectedMatch)} | {selectedMatch.match_duration_days} day match window</p>
          </div>
          <button className="secondaryButton inlineActionButton dangerButton" type="button" onClick={handleDeleteSelectedMatch} disabled={matchLoading}>Delete Match</button>
        </div>
        <div className="matchDeskScoreboard">
          <div className="matchDeskScoreStat">
            <span className="matchDeskMetaLabel">Time Control</span>
            <strong>{selectedMatch.time_control}</strong>
          </div>
          <div className="matchDeskScoreStat">
            <span className="matchDeskMetaLabel">Total Boards</span>
            <strong>{selectedMatch.boards?.length || 0}</strong>
          </div>
          <div className="matchDeskScoreStat isScore">
            <span className="matchDeskMetaLabel">Score</span>
            <strong>{selectedMatch.team_a_score} - {selectedMatch.team_b_score}</strong>
          </div>
        </div>
        <div className="cardIntro boardSectionIntro">
          <h3>Board Results</h3>
          <p className="compactNote">
            Launch boards, update results, and keep the running score tied to the same match record.
          </p>
        </div>
        {selectedMatch.status === "expired" && (
          <div className="surfaceCard matchApprovalBanner">
            <div>
              <h4>Match window closed</h4>
              <p className="compactNote">
                The deadline has passed for this match. Launch and ready actions are closed, and the current score stands.
              </p>
            </div>
          </div>
        )}
        {selectedMatch.status === "pending_acceptance" && (
          <div className="surfaceCard matchApprovalBanner">
            <div>
              <h4>Awaiting captain approval</h4>
              <p className="compactNote">
                The request is on the table. Both team owners need to accept before boards can mark ready or launch.
              </p>
              <p className="compactNote">
                {selectedMatch.team_a_name}: {selectedMatch.team_a_accepted ? "accepted" : "waiting"} | {selectedMatch.team_b_name}: {selectedMatch.team_b_accepted ? "accepted" : "waiting"}
              </p>
            </div>
            {currentUser && (
              <div className="teamCardActions">
                {selectedMatch.team_a_owner_id === currentUser.id && !selectedMatch.team_a_accepted && (
                  <>
                    <button className="primaryButton inlineActionButton" type="button" onClick={() => handleRespondToMatchRequest(true)} disabled={matchLoading}>
                      Accept Match
                    </button>
                    <button className="secondaryButton inlineActionButton dangerButton" type="button" onClick={() => handleRespondToMatchRequest(false)} disabled={matchLoading}>
                      Decline
                    </button>
                  </>
                )}
                {selectedMatch.team_b_owner_id === currentUser.id && !selectedMatch.team_b_accepted && (
                  <>
                    <button className="primaryButton inlineActionButton" type="button" onClick={() => handleRespondToMatchRequest(true)} disabled={matchLoading}>
                      Accept Match
                    </button>
                    <button className="secondaryButton inlineActionButton dangerButton" type="button" onClick={() => handleRespondToMatchRequest(false)} disabled={matchLoading}>
                      Decline
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
        <div className="boardGrid">
          {selectedMatch.boards.map((board) => renderBoardCard(board))}
        </div>
      </div>
    ) : (
      <div className="selectedMatchPanel matchDeskStage isEmpty">
        <div className="cardIntro">
          <h3>Match Stage</h3>
          <p className="compactNote">
            Select a match from the left to load the scoreline, official links, and board-level results.
          </p>
        </div>
      </div>
    );

    return (
      <MatchPage
        title="Matches"
        description="Track live boards, official links, and resolved results from one match center."
        selectedMatch={selectedMatch}
        createActionNode={currentUser && ownerManagedTeams.length > 0 ? (
          <button
            className="primaryButton matchDeskCreateButton"
            type="button"
            onClick={() => navigatePlayView(showMatchComposer ? "matches" : "create")}
          >
            {showMatchComposer ? "Close Match Builder" : "Create Match"}
          </button>
        ) : null}
        metricsNode={!currentUser ? renderMetricStrip([
          { label: "Live Matches", value: liveMatches, note: `${scheduledMatches} waiting to start` },
          { label: "Completed", value: completedMatches, note: `${matches.length} total match records` },
          { label: "Selected Match", value: selectedMatch ? `#${selectedMatch.id}` : "None", note: selectedMatch ? `${selectedMatch.team_a_name} vs ${selectedMatch.team_b_name}` : "Choose a match to manage boards" },
        ]) : null}
        showcaseNode={!currentUser ? (
          <div className="workspaceShowcase workspaceShowcasePlay">
            <div className="workspaceShowcaseLead">
              <span className="workspaceShowcaseLabel">Match Center</span>
              <h3>Run every official board from one clean match page.</h3>
              <p>
                Pair the round, launch the right board on the right site, and keep every result tied to one official match record.
              </p>
            </div>
            <div className="workspaceShowcaseGrid">
              <div className="workspaceShowcaseTile">
                <span>Ready boards</span>
                <strong>{selectedMatch?.boards?.filter((board) => board.white_ready || board.black_ready).length || 0}</strong>
                <p>Boards already checked in on the currently selected match.</p>
              </div>
              <div className="workspaceShowcaseTile">
                <span>Platform mix</span>
                <strong>{selectedMatch?.platform === "mixed" ? "Mixed" : selectedMatch?.platform || "Open"}</strong>
                <p>Choose one site or let mixed boards split the round.</p>
              </div>
              <div className="workspaceShowcaseTile">
                <span>Score line</span>
                <strong>{selectedMatch ? `${selectedMatch.team_a_score} - ${selectedMatch.team_b_score}` : "0 - 0"}</strong>
                <p>Board wins score 1, draws 0.5, losses 0.</p>
              </div>
            </div>
          </div>
        ) : null}
        gateNode={!currentUser ? (
          <div className="surfaceCard teamsGateCard">
            <h3>Sign in to create and manage matches.</h3>
            <p className="compactNote">
              Matches need real owners, real players, and real linked handles. Sign in first so the desk knows who is running the round.
            </p>
            <button className="primaryButton" type="button" onClick={() => handleNavigate("account")}>
              Go To Account
            </button>
          </div>
        ) : null}
        sidebarNode={matchSidebarNode}
        mainNode={matchMainNode}
      />
    );
  };

  const renderForumsSection = () => (
    <section id="forums" className="productSection">
      <div className="sectionHeader">
        <div>
          <div className="sectionLabel">ROUND ROOM</div>
          <h2>Keep the week between rounds organized.</h2>
        </div>
        <p>
          Announcements, substitutions, board scheduling, and captain notes should live in one place, not vanish into side chats.
        </p>
      </div>
      <div className="sectionGrid twoColumn">
        {FORUM_COLUMNS.map(([title, items]) => (
          <div key={title} className="surfaceCard compactCard">
            <h3>{title}</h3>
            {renderInfoBlocks(items)}
          </div>
        ))}
      </div>
    </section>
  );

  const renderDisputesSection = () => (
    <section id="disputes" className="productSection">
      <div className="sectionHeader">
        <div>
          <div className="sectionLabel">REVIEW DESK</div>
          <h2>When a board gets messy, keep the record sharp.</h2>
        </div>
        <p>
          Bring the board, the evidence, and a clean summary so corrections can be made against the match record, not against memory.
        </p>
      </div>
      <div className="sectionGrid twoColumn">
        {DISPUTE_COLUMNS.map(([title, items]) => (
          <div key={title} className="surfaceCard compactCard">
            <h3>{title}</h3>
            {renderInfoBlocks(items)}
          </div>
        ))}
      </div>
    </section>
  );

  const renderContactSection = () => (
    <section id="contact" className="productSection">
      <div className="sectionHeader">
        <div>
          <div className="sectionLabel">SUPPORT DESK</div>
          <h2>Reach the right desk fast.</h2>
        </div>
        <p>
          Account issues, league operations, and arbiter review do not belong in the same inbox. Route it once and route it right.
        </p>
      </div>
      <div className="sectionGrid threeColumn">
        {CONTACT_COLUMNS.map(([title, text]) => (
          <div key={title} className="surfaceCard compactCard">
            <h3>{title}</h3>
            <p className="compactNote">{text}</p>
          </div>
        ))}
      </div>
    </section>
  );

  const signedIn = Boolean(currentUser);
  const activeRouteKey = resolveAppRouteKey({
    currentPage,
    currentSubpage,
    signedIn,
  });
  const RoutedPageComponent = getAppRouteComponent(activeRouteKey);
  const primaryNavItems = getPrimaryNavItems({ signedIn });
  const secondaryNavItem = getSecondaryNavItem({ signedIn });
  const pageMapItems = getPageMapItems({ signedIn });
  const pageShell = currentPage === "home"
    ? null
    : getPageShell(currentPage, {
        signedIn,
        subpage: currentSubpage,
      });
  const pageRendererById = {
    home: renderHomeSection,
    dashboard: renderHomeSection,
    profile: renderAccountSection,
    team: renderTeamsSection,
    league: renderLeaguesSection,
    "league-edit": renderLeaguesSection,
    "league-rules": renderLeaguesSection,
    match: renderPlaySection,
    forums: renderForumsSection,
    disputes: renderDisputesSection,
    contact: renderContactSection,
  };
  const activePageRenderer = pageRendererById[activeRouteKey] || pageRendererById[currentPage] || renderHomeSection;

  return (
    <RootLayout
      currentPage={currentPage}
      currentSubpage={currentSubpage}
      pageShell={pageShell}
      pageMapItems={pageMapItems}
      primaryNavItems={primaryNavItems}
      secondaryNavItem={secondaryNavItem}
      onNavigate={handleNavigate}
      signedIn={signedIn}
    >
      <datalist id="country-options">
        {COUNTRY_OPTIONS.map((country) => (
          <option key={country} value={country} />
        ))}
      </datalist>
      {RoutedPageComponent ? activePageRenderer() : activePageRenderer()}
      {teamExitPrompt && (
        <div className="dialogScrim" onClick={() => setTeamExitPrompt(null)}>
          <div className="dialogCard" onClick={(event) => event.stopPropagation()}>
            <span className="editorBadge">Ownership Transfer</span>
            <h3>{teamExitPrompt.mode === "invite" ? "Pass the team before accepting the invite" : "Pass the team before leaving"}</h3>
            <p className="compactNote">
              {teamExitPrompt.mode === "invite"
                ? `Choose who should take over ${teamExitPrompt.team.name}. Once confirmed, you will leave that roster and join ${teamExitPrompt.invite.team_name}.`
                : `Choose who should take over ${teamExitPrompt.team.name}. Once confirmed, you will leave the roster and the new owner will keep the team running.`}
            </p>
            <div className="dialogChoiceList">
              {teamExitPrompt.members.map((member) => {
                const isSelected = String(teamExitPrompt.transferTeamOwnerId) === String(member.user_id);
                return (
                  <button
                    key={`transfer-${member.user_id}`}
                    type="button"
                    className={`accountChoiceCard ${isSelected ? "isSelected" : ""}`}
                    onClick={() =>
                      setTeamExitPrompt((prev) => ({
                        ...prev,
                        transferTeamOwnerId: String(member.user_id),
                      }))
                    }
                  >
                    <span className="accountChoicePlatform">Captain Candidate</span>
                    <strong>{member.username}</strong>
                    <small>Will become the new team owner</small>
                  </button>
                );
              })}
            </div>
            <div className="teamCardActions">
              <button className="primaryButton inlineActionButton" type="button" onClick={handleConfirmTeamExitPrompt} disabled={teamLoading || acceptingInviteId === teamExitPrompt.invite?.id}>
                {teamExitPrompt.mode === "invite"
                  ? acceptingInviteId === teamExitPrompt.invite?.id
                    ? "Accepting..."
                    : "Transfer And Accept Invite"
                  : teamLoading
                    ? "Leaving..."
                    : "Transfer And Leave"}
              </button>
              <button className="secondaryButton inlineActionButton" type="button" onClick={() => setTeamExitPrompt(null)} disabled={teamLoading || acceptingInviteId === teamExitPrompt.invite?.id}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </RootLayout>
  );
}

export default App;
